<template>
  <div v-if="props.isOpen" class="fixed inset-0 z-[9999] md:z-[10000] flex items-center justify-center bg-black bg-opacity-50" @click="closeModal">
    <div class="modal-panel relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-[96vw] h-[94vh] md:w-[94vw] md:h-[92vh] lg:w-[80vw] lg:h-[90vh] mx-2 md:mx-3 max-h-[94vh] md:max-h-[92vh] lg:max-h-[90vh] overflow-hidden" @click.stop>
      <!-- Header -->
      <div class="modal-header flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-3 pr-12 sm:pr-3 border-b border-gray-200 dark:border-gray-700 bg-[var(--bg-secondary)]">
        <div class="flex min-w-0 items-center space-x-3">
          <div class="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
            <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M6 16h.01"></path>
            </svg>
          </div>
          <div class="flex min-w-0 items-center space-x-3">
            <div class="w-3 h-3 rounded-full" :style="{ backgroundColor: formData.cor || '#3B82F6' }"></div>
            <div class="min-w-0">
              <h2 class="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">{{ t('kanban.taskDetail.header.title') }}</h2>
              <p class="text-[11px] sm:text-xs text-gray-600 dark:text-gray-400">
                {{ t('kanban.taskDetail.header.subtitle') }}
              </p>
            </div>
          </div>
        </div>
        <div class="flex w-full items-center justify-between sm:w-auto sm:justify-end sm:space-x-3">
          <!-- Indicador de Status de Salvamento -->
          <div class="flex items-center space-x-2">
            <!-- Salvando (informações, tags ou checklist) -->
            <Transition name="status-fade" mode="out-in">
              <div v-if="salvando || salvandoTags || salvandoChecklist" key="salvando" class="flex items-center space-x-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 rounded-lg transition-all duration-300">
                <svg class="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                <span class="hidden sm:inline text-xs font-medium text-blue-600 dark:text-blue-400">
                  {{ salvando ? t('kanban.taskDetail.saveStatus.saving') : salvandoTags ? t('kanban.taskDetail.saveStatus.savingTags') : t('kanban.taskDetail.saveStatus.savingChecklist') }}
                </span>
              </div>
              
              <!-- Salvo (informações, tags ou checklist) -->
              <div v-else-if="salvo || salvoTags || salvoChecklist" key="salvo" class="flex items-center space-x-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded-lg transition-all duration-300">
                <svg class="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="hidden sm:inline text-xs font-medium text-green-600 dark:text-green-400">
                  {{ salvo ? t('kanban.taskDetail.saveStatus.saved') : salvoTags ? t('kanban.taskDetail.saveStatus.savedTags') : t('kanban.taskDetail.saveStatus.savedChecklist') }}
                </span>
              </div>
              
              <!-- Pronto para editar (estado padrão) -->
              <div v-else key="pronto" class="flex items-center space-x-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg transition-all duration-300">
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
                <span class="hidden sm:inline text-xs font-medium text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.saveStatus.ready') }}</span>
              </div>
            </Transition>
          </div>

          <button
            v-if="(isMobileViewport || isTabletViewport) && resolvedHistoricoChatEnabled"
            @click="showMobileChat = true"
            class="p-2 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200 lg:hidden"
            :title="t('kanban.taskDetail.header.openChat')"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h7m8 0c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
            </svg>
          </button>
          
          <!-- Botão Discord Daily (desativado até uso futuro) -->
          <button
            v-if="showDiscordDailyButton"
            @click="openDiscordDailyModal"
            class="p-2 text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-all duration-200"
            :title="t('kanban.taskDetail.header.discordDaily')"
          >
            <img src="/discord-icon.svg" alt="Discord" class="w-5 h-5" />
          </button>

          <!-- Botão Fechar -->
          <button @click="closeModal" class="absolute top-3 right-2 z-20 sm:static p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>

      <div class="flex flex-col md:flex-row h-[calc(94vh-128px)] md:h-[calc(90vh-60px)]">
        <!-- Main Content -->
        <div
          ref="layoutCol1El"
          class="modal-panel-column w-full md:w-[60%] md:flex-none flex-1 min-h-0 p-3 overflow-y-auto"
          :class="resolvedHistoricoChatEnabled ? 'lg:w-[40%]' : 'lg:w-[60%]'"
        >
          <!-- Informações da Task -->
          <TaskDetailPlanSection
            :visible="sectionEnabled.informacoesTask"
            section-key="informacoesTask"
            :blocked="planBlocked('informacoesTask')"
            :body-expanded="!minimizedCards.informacoesTask"
            :gate-title="planGateTitle('informacoesTask')"
          >
            <template #header>
<div class="flex items-center justify-between mb-3" :class="{ 'border-b border-dashed border-gray-300 dark:border-gray-500 pb-3': minimizedCards.informacoesTask }">
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-md">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M6 16h.01"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ t('kanban.taskDetail.sections.taskInfo.title') }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.taskInfo.subtitle') }}</p>
                </div>
              </div>
              <button @click="toggleCard('informacoesTask')" class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200" :class="{ 'rotate-180': minimizedCards.informacoesTask }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
            </div>
            </template>
            <template #body>
            <div v-show="!minimizedCards.informacoesTask" class="modal-surface bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 relative">
              <div class="space-y-3">
                <div>
                  <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{{ t('kanban.taskDetail.sections.taskInfo.titleLabel') }}</label>
                  <textarea 
                    ref="tituloTextareaRef"
                    v-model="formData.titulo" 
                    @blur="salvarInformacoesTask"
                    @input="(e) => { salvarInformacoesTask(); autoResizeTitulo(e.target) }"
                    @keydown.enter.exact.prevent
                    rows="1"
                    class="w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-600 dark:text-white transition-all duration-200 resize-none titulo-textarea" 
                    required
                  ></textarea>
                </div>
                <div>
                  <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{{ t('kanban.taskDetail.sections.taskInfo.descriptionLabel') }}</label>
                <div class="border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
                  <div class="modal-surface-gradient flex items-center space-x-2 p-2 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-md">
                    <select class="text-sm border-none bg-transparent focus:outline-none dark:text-white">
                      <option>Normal</option>
                      <option>H1</option>
                      <option>H2</option>
                      <option>H3</option>
                    </select>
                    <div class="flex items-center space-x-1">
                      <button class="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 6h12M6 12h12M6 18h12"></path>
                        </svg>
                      </button>
                      <button class="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                        </svg>
                      </button>
                      <button class="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                        </svg>
                      </button>
                      <button class="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                      </button>
                    </div>
                    <div class="flex items-center space-x-1 ml-auto">
                      <button class="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
                        </svg>
                      </button>
                      <button class="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <textarea 
                    v-model="formData.descricao" 
                    @blur="salvarInformacoesTask"
                    @input="salvarInformacoesTask"
                    rows="6" 
                    class="w-full p-3 border-none resize-none focus:outline-none dark:bg-gray-800 dark:text-white"
                    :placeholder="t('kanban.taskDetail.sections.taskInfo.descriptionPlaceholder')"
                  ></textarea>
                </div>
              </div>
              </div>
            </div>
          
            </template>
          </TaskDetailPlanSection>

          <!-- Checklist -->
          <TaskDetailPlanSection
            :visible="sectionEnabled.checklist"
            section-key="checklist"
            :blocked="planBlocked('checklist')"
            :body-expanded="!minimizedCards.checklist"
            :gate-title="planGateTitle('checklist')"
          >
            <template #header>
<div class="flex items-center justify-between mb-3" :class="{ 'border-b border-dashed border-gray-300 dark:border-gray-500 pb-3': minimizedCards.checklist }">
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ t('kanban.taskDetail.sections.checklist.title') }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.checklist.subtitle') }}</p>
                </div>
              </div>
              <div class="flex items-center space-x-2">
                <button
                  @click="toggleChecklistVisibility"
                  class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  :title="showAllChecklistItems ? t('kanban.taskDetail.sections.checklist.hideCompleted') : t('kanban.taskDetail.sections.checklist.showAll')"
                >
                  <svg v-if="showAllChecklistItems" class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                  <svg v-else class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.958 9.958 0 012.143-3.592m2.49-2.19A9.953 9.953 0 0112 5c4.477 0 8.268 2.943 9.542 7a9.957 9.957 0 01-4.132 5.411M15 12a3 3 0 00-3-3m0 0a3 3 0 00-3 3m3-3l3 3"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3l18 18"></path>
                  </svg>
                </button>
                <button @click="toggleCard('checklist')" class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200" :class="{ 'rotate-180': minimizedCards.checklist }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              </div>
            </div>
            </template>
            <template #body>
            <div v-show="!minimizedCards.checklist" class="modal-surface-gradient bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-3 sm:p-6 shadow-xl border border-gray-200 dark:border-gray-700 relative overflow-visible">
              <!-- Decorative background pattern -->
              <div class="absolute top-0 left-60 w-32 h-32 bg-gradient-to-br from-emerald-100/30 to-green-100/30 dark:from-emerald-900/20 dark:to-green-900/20 rounded-full -translate-y-16 translate-x-16"></div>
              <div class="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-blue-100/30 to-indigo-100/30 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-full translate-y-12 -translate-x-12"></div>
              <div class="relative z-10 space-y-6">
              <!-- Categorias Existentes -->
              <div v-if="formData.checklist.length === 0" class="text-center py-12">
                <div class="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-emerald-100 to-green-100 dark:from-emerald-900/30 dark:to-green-900/30 rounded-2xl flex items-center justify-center">
                  <svg class="w-10 h-10 text-emerald-500 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path>
                  </svg>
                </div>
                <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">{{ t('kanban.taskDetail.sections.checklist.emptyTitle') }}</h3>
                <p class="text-gray-600 dark:text-gray-400">{{ t('kanban.taskDetail.sections.checklist.emptyHint') }}</p>
              </div>

              <draggable 
                v-else
                v-model="formData.checklist" 
                item-key="id" 
                handle=".category-drag-handle"
                :animation="200"
                ghost-class="category-ghost"
                drag-class="category-drag"
                @end="onCategoryReorder"
                class="space-y-6"
              >
                <template #item="{ element: category }">
              <div class="modal-surface bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-visible hover:shadow-xl transition-all duration-300">
                <div class="relative overflow-hidden rounded-t-xl border-b border-gray-200/90 dark:border-gray-700/90 bg-gradient-to-br from-slate-50 via-white to-indigo-50/70 dark:from-gray-900/50 dark:via-gray-800 dark:to-indigo-950/30 px-2.5 py-2 sm:px-3 sm:py-2">
                  <div class="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent dark:via-white/10"></div>
                  <div class="relative z-10 flex flex-col gap-1.5">
                    <!-- Linha 1: identidade + stats + ações -->
                    <div class="flex items-center gap-2 sm:gap-2.5">
                      <div class="category-drag-handle shrink-0 cursor-grab active:cursor-grabbing rounded-md border border-gray-200/80 bg-white/90 p-1 shadow-sm dark:border-gray-600 dark:bg-gray-800/70 hover:border-blue-300 hover:bg-blue-50/70 dark:hover:border-blue-500/40 dark:hover:bg-blue-950/30 transition-colors duration-150" :title="t('kanban.taskDetail.sections.checklist.dragReorder')">
                        <svg class="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                          <circle cx="9" cy="5" r="1.5"/><circle cx="15" cy="5" r="1.5"/>
                          <circle cx="9" cy="10" r="1.5"/><circle cx="15" cy="10" r="1.5"/>
                          <circle cx="9" cy="15" r="1.5"/><circle cx="15" cy="15" r="1.5"/>
                          <circle cx="9" cy="20" r="1.5"/><circle cx="15" cy="20" r="1.5"/>
                        </svg>
                      </div>
                      <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 shadow-sm ring-1 ring-white/40 dark:ring-gray-700/60">
                        <svg class="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path>
                        </svg>
                      </div>
                      <h4 class="min-w-0 flex-1 truncate text-sm font-semibold leading-tight tracking-tight text-gray-900 dark:text-white sm:text-base">
                        {{ category.nome }}
                      </h4>
                      <div class="flex shrink-0 flex-wrap items-center justify-end gap-1.5 sm:gap-2">
                        <span class="inline-flex items-center gap-1 rounded-md border border-gray-200/80 bg-white/80 px-2 py-0.5 text-[11px] font-medium tabular-nums text-gray-600 shadow-sm dark:border-gray-600 dark:bg-gray-800/80 dark:text-gray-300" :title="t('kanban.taskDetail.sections.checklist.itemsCompleted', { done: category.completed || 0, total: category.items?.length || 0 })">
                          <span class="h-1 w-1 shrink-0 rounded-full bg-emerald-500"></span>
                          {{ category.completed || 0 }}/{{ category.items?.length || 0 }}
                        </span>
                        <span class="inline-flex min-w-[2.25rem] items-center justify-center rounded-md bg-gradient-to-r from-indigo-600 to-blue-600 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-white shadow-sm" :title="t('kanban.taskDetail.sections.checklist.categoryProgress')">
                          {{ Math.round((category.items?.length || 0) > 0 ? ((category.completed || 0) / (category.items?.length || 1) * 100) : 0) }}%
                        </span>
                        <div class="inline-flex items-center gap-px rounded-lg border border-gray-200/70 bg-white/90 p-px shadow-sm backdrop-blur-sm dark:border-gray-600/80 dark:bg-gray-800/90">
                          <button
                            type="button"
                            @click="toggleCategoryCompletion(category.id)"
                            class="flex h-7 w-7 items-center justify-center rounded-md text-emerald-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700 dark:hover:bg-emerald-900/35 dark:hover:text-emerald-300"
                            :title="(category.items || []).length > 0 && (category.items || []).every((item) => item.completed) ? t('kanban.taskDetail.sections.checklist.reopenCategory') : t('kanban.taskDetail.sections.checklist.completeCategory')"
                          >
                            <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                          </button>
                          <span class="mx-px h-4 w-px shrink-0 self-center rounded-full bg-gray-200 dark:bg-gray-600" aria-hidden="true"></span>
                          <button type="button" @click="toggleCategory(category.id)" class="flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-gray-700 dark:hover:text-gray-100" :title="t('kanban.taskDetail.sections.checklist.expandCollapse')">
                            <svg class="h-3.5 w-3.5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" :class="{ 'rotate-180': minimizedCategories[String(category.id)] }">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                          </button>
                          <span class="mx-px h-4 w-px shrink-0 self-center rounded-full bg-gray-200 dark:bg-gray-600" aria-hidden="true"></span>
                          <button type="button" @click="deleteCategory(category.id)" class="flex h-7 w-7 items-center justify-center rounded-md text-red-500 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 dark:hover:text-red-300" :title="t('kanban.taskDetail.sections.checklist.deleteCategory')">
                            <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                    <!-- Barra de progresso em largura total do cabeçalho -->
                    <div class="w-full">
                      <div class="modal-progress-track h-1.5 w-full overflow-hidden rounded-full bg-gray-200/90 dark:bg-gray-700/90">
                        <div
                          class="h-full rounded-full bg-gradient-to-r from-emerald-400 via-teal-400 to-green-500 transition-all duration-500 ease-out"
                          :style="{ width: `${(category.items?.length || 0) > 0 ? ((category.completed || 0) / (category.items?.length || 1) * 100) : 0}%` }"
                        ></div>
                      </div>
                    </div>
                    <!-- Equipe e datas -->
                    <div class="flex flex-wrap items-center gap-1.5 border-t border-gray-200/60 pt-1.5 dark:border-gray-600/40 sm:gap-2">
                        <div v-if="category.assignedUsers && category.assignedUsers.length > 0" class="flex items-center -space-x-1 pr-0.5">
                          <div
                            v-for="user in category.assignedUsers.slice(0, 3)"
                            :key="user.id"
                            class="group relative cursor-pointer ring-2 ring-white dark:ring-gray-800 rounded-full"
                          >
                            <div class="relative h-7 w-7">
                              <img
                                v-if="user.fotoPerfil && user.fotoPerfil.trim()"
                                :src="user.fotoPerfil"
                                :alt="user.nomeCompleto"
                                class="h-7 w-7 rounded-full border border-white object-cover shadow-sm transition-transform duration-200 group-hover:scale-105 dark:border-gray-700"
                              />
                              <div
                                v-else-if="fotosCarregando"
                                class="flex h-7 w-7 items-center justify-center rounded-full border border-white bg-gradient-to-br from-blue-500 to-indigo-600 shadow-sm dark:border-gray-700"
                              >
                                <div class="loading-spinner"></div>
                              </div>
                              <div
                                v-else
                                class="flex h-7 w-7 items-center justify-center rounded-full border border-white bg-gradient-to-br from-blue-500 to-indigo-600 text-[10px] font-semibold text-white shadow-sm dark:border-gray-700"
                              >
                                {{ getUserInitials(user) }}
                              </div>
                            </div>
                            <div class="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-0.5 text-[11px] text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100 dark:bg-gray-700">
                              {{ user.nomeCompleto }}
                              <div class="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                            </div>
                          </div>
                          <div v-if="category.assignedUsers.length > 3" class="z-[1] flex h-7 w-7 items-center justify-center rounded-full border border-white bg-gray-500 text-[10px] font-semibold text-white shadow-sm dark:border-gray-800">
                            +{{ category.assignedUsers.length - 3 }}
                          </div>
                        </div>
                        <button
                          @click="openUserSelectorForCategory(category.id)"
                          :class="[
                            'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-all duration-200 checklist-info-button',
                            category.assignedUsers && category.assignedUsers.length > 0
                              ? 'border-blue-200/90 bg-blue-50 text-blue-700 shadow-sm dark:border-blue-800/80 dark:bg-blue-950/50 dark:text-blue-300 has-data'
                              : 'border-gray-200/90 bg-white/90 text-gray-600 hover:border-blue-200 hover:bg-blue-50/60 dark:border-gray-600 dark:bg-gray-800/80 dark:text-gray-300 dark:hover:border-blue-800 dark:hover:bg-blue-950/30'
                          ]"
                          :title="category.assignedUsers && category.assignedUsers.length > 0 ? t('kanban.taskDetail.sections.checklist.usersAssigned', { count: category.assignedUsers.length }) : t('kanban.taskDetail.sections.checklist.assignUsers')"
                        >
                          <svg class="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
                          </svg>
                          <span v-if="category.assignedUsers && category.assignedUsers.length > 0" class="font-semibold tabular-nums">{{ category.assignedUsers.length }}</span>
                          <span v-else class="hidden sm:inline">{{ t('kanban.taskDetail.sections.checklist.team') }}</span>
                        </button>
                        <button
                          @click="openDatePickerForCategory(category.id)"
                          :class="[
                            'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-all duration-200 checklist-info-button',
                            category.dataPrevista
                              ? getDateStatus(category.dataPrevista) === 'overdue'
                                ? 'border-red-200/90 bg-red-50 text-red-700 shadow-sm dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300 overdue'
                                : getDateStatus(category.dataPrevista) === 'today'
                                ? 'border-amber-200/90 bg-amber-50 text-amber-800 shadow-sm dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200 today'
                                : 'border-emerald-200/90 bg-emerald-50 text-emerald-800 shadow-sm dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-200 future'
                              : 'border-gray-200/90 bg-white/90 text-gray-600 hover:border-emerald-200 hover:bg-emerald-50/50 dark:border-gray-600 dark:bg-gray-800/80 dark:text-gray-300 dark:hover:border-emerald-900 dark:hover:bg-emerald-950/20'
                          ]"
                          :title="category.dataPrevista ? t('kanban.taskDetail.sections.checklist.dateTitle', { date: formatDate(category.dataPrevista), suffix: getDateStatus(category.dataPrevista) === 'overdue' ? t('kanban.taskDetail.sections.checklist.dateOverdue') : getDateStatus(category.dataPrevista) === 'today' ? t('kanban.taskDetail.sections.checklist.dateToday') : '' }) : t('kanban.taskDetail.sections.checklist.setDueDate')"
                        >
                          <svg class="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                          </svg>
                          <span v-if="category.dataPrevista" class="flex items-center gap-0.5 font-semibold">
                            <span>{{ formatDate(category.dataPrevista) }}</span>
                            <span v-if="getDateStatus(category.dataPrevista) === 'overdue'" :title="t('kanban.taskDetail.sections.checklist.statusOverdue')">⚠️</span>
                            <span v-else-if="getDateStatus(category.dataPrevista) === 'today'" :title="t('kanban.taskDetail.sections.checklist.statusToday')">⚡</span>
                            <span v-else :title="t('kanban.taskDetail.sections.checklist.statusFuture')">✅</span>
                          </span>
                          <span v-else class="hidden sm:inline">{{ t('kanban.taskDetail.sections.checklist.deadline') }}</span>
                        </button>
                    </div>
                  </div>
                </div>
                
                <div v-show="!minimizedCategories[String(category.id)]" class="p-4 space-y-3">
                  <div 
                    v-for="item in getVisibleChecklistItems(category)" 
                    :key="item.id" 
                    @contextmenu.prevent="openContextMenu($event, item, category)"
                    class="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
                    :class="getItemBackgroundClass(item)"
                  >
                    <input 
                      :id="`item-${item.id}`"
                      v-model="item.completed" 
                      type="checkbox" 
                      class="w-5 h-5 text-emerald-600 bg-gray-100 border-gray-300 rounded focus:ring-emerald-500 dark:focus:ring-emerald-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 transition-all duration-200"
                    >
                    
                    <!-- Modo de visualização -->
                    <div v-if="!editingItems[item.id]" class="flex-1">
                      <div class="flex flex-col space-y-1">
                        <label 
                          :for="`item-${item.id}`" 
                          @click="startEditingItem(category.id, item.id, item.text)"
                          class="block text-sm font-medium transition-all duration-200 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 px-2 py-1 rounded" 
                          :class="item.completed ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'"
                          v-html="formatItemText(item.text)"
                        >
                        </label>
                        <!-- Tags do item -->
                        <div v-if="item.tags && item.tags.length > 0" class="flex flex-wrap gap-1 px-2">
                          <span 
                            v-for="tag in item.tags" 
                            :key="tag.id"
                            class="inline-flex items-center px-2 py-1 rounded text-xs font-medium space-x-1.5 border shadow-md border-transparent"
                            :style="{ 
                              backgroundColor: tag.cor,
                              color: getContrastColor(tag.cor)
                            }"
                          >
                            <!-- Emoji da tag -->
                            <span class="text-sm">{{ getTagIcon(tag.icone) }}</span>
                            <!-- Nome da tag -->
                            <span>{{ tag.nome }}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Modo de edição -->
                    <div v-else class="flex-1">
                      <textarea 
                        v-model="editingText[item.id]"
                        @input="onEditingItemInput"
                        @keydown.enter.exact.stop.prevent="saveItemEdit(category.id, item.id)"
                        @keydown.escape="cancelItemEdit(item.id)"
                        @blur="saveItemEdit(category.id, item.id)"
                        rows="2"
                        class="w-full px-2 py-1 text-sm border border-emerald-300 dark:border-emerald-700 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-800 dark:text-white transition-all duration-200 resize-none"
                        :data-editing-id="item.id"
                      ></textarea>
                    </div>
                    
                    <!-- Usuários atribuídos -->
                    <div v-if="item.assignedUsers && item.assignedUsers.length > 0" class="flex items-center space-x-1">
                      <div class="flex -space-x-1">
                        <div 
                          v-for="user in item.assignedUsers.slice(0, 3)" 
                          :key="user.id"
                          class="relative group cursor-pointer"
                        >
                          <!-- Avatar com loading suave -->
                          <div class="relative w-6 h-6">
                            <!-- Foto do usuário -->
                            <img 
                              v-if="user.fotoPerfil && user.fotoPerfil.trim()" 
                              :src="user.fotoPerfil" 
                              :alt="user.nomeCompleto"
                              class="w-6 h-6 rounded-full border-2 border-white dark:border-gray-700 object-cover hover:scale-110 transition-all duration-300"
                            />
                            <!-- Spinner temporário enquanto carrega dados -->
                            <div 
                              v-else-if="fotosCarregando" 
                              class="w-6 h-6 rounded-full border-2 border-white dark:border-gray-700 bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center hover:scale-110 transition-all duration-300 shadow-sm"
                            >
                              <!-- Spinner animado -->
                              <div class="loading-spinner"></div>
                            </div>
                            <!-- Iniciais definitivas quando não há foto -->
                            <div 
                              v-else 
                              class="w-6 h-6 rounded-full border-2 border-white dark:border-gray-700 bg-gradient-to-br from-blue-500 to-blue-600 text-white text-xs flex items-center justify-center font-medium hover:scale-110 transition-all duration-300 shadow-sm"
                            >
                              {{ getUserInitials(user) }}
                            </div>
                          </div>
                          
                          <!-- Tooltip -->
                          <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black dark:bg-gray-700 text-white dark:text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                            {{ user.nomeCompleto }}
                            <div class="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-black dark:border-t-gray-700"></div>
                          </div>
                        </div>
                        <div 
                          v-if="item.assignedUsers.length > 3"
                          class="relative group w-6 h-6 rounded-full border-2 border-white dark:border-gray-700 bg-gray-400 text-white text-xs flex items-center justify-center font-medium cursor-pointer hover:scale-110 transition-transform duration-200"
                        >
                          +{{ item.assignedUsers.length - 3 }}
                          
                          <!-- Tooltip com lista de usuários extras -->
                          <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-black dark:bg-gray-700 text-white dark:text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                            <div class="font-medium mb-1">{{ t('kanban.taskDetail.sections.activity.moreUsers') }}</div>
                            <div v-for="user in item.assignedUsers.slice(3)" :key="user.id" class="text-xs">
                              {{ user.nomeCompleto }}
                            </div>
                            <div class="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-black dark:border-t-gray-700"></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Botão para atribuir responsável -->
                    <button 
                      @click="openUserSelectorForItem(category.id, item.id)"
                      :class="[
                        'p-1.5 rounded-lg transition-all duration-200 flex items-center space-x-1 checklist-info-button',
                        item.assignedUsers && item.assignedUsers.length > 0
                          ? 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 has-data' 
                          : 'text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                      ]"
                      :title="item.assignedUsers && item.assignedUsers.length > 0 ? t('kanban.taskDetail.sections.checklist.usersAssigned', { count: item.assignedUsers.length }) : t('kanban.taskDetail.sections.checklist.assignResponsible')"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
                      </svg>
                      <span v-if="item.assignedUsers && item.assignedUsers.length > 0" class="text-xs font-medium">
                        {{ item.assignedUsers.length }}
                      </span>
                    </button>
                    
                    <!-- Botão para definir data prevista -->
                    <button 
                      @click="openDatePicker(category.id, item.id)"
                      :class="[
                        'p-1.5 rounded-lg transition-all duration-200 flex items-center space-x-1 checklist-info-button',
                        item.dueDate 
                          ? getDateStatus(item.dueDate) === 'overdue'
                            ? 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400 overdue'
                            : getDateStatus(item.dueDate) === 'today'
                            ? 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400 today'
                            : 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400 future'
                          : 'text-green-500 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20'
                      ]"
                      :title="item.dueDate ? t('kanban.taskDetail.sections.checklist.dateTitle', { date: formatDate(item.dueDate), suffix: getDateStatus(item.dueDate) === 'overdue' ? t('kanban.taskDetail.sections.checklist.dateOverdue') : getDateStatus(item.dueDate) === 'today' ? t('kanban.taskDetail.sections.checklist.dateToday') : '' }) : t('kanban.taskDetail.sections.checklist.setDueDate')"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      <span v-if="item.dueDate" class="text-xs font-medium flex items-center space-x-1">
                        <span>{{ formatDate(item.dueDate) }}</span>
                        <span v-if="getDateStatus(item.dueDate) === 'overdue'" class="text-red-600" :title="t('kanban.taskDetail.sections.checklist.statusOverdue')">⚠️</span>
                        <span v-else-if="getDateStatus(item.dueDate) === 'today'" class="text-yellow-600" :title="t('kanban.taskDetail.sections.checklist.statusToday')">⚡</span>
                        <span v-else class="text-green-600" :title="t('kanban.taskDetail.sections.checklist.statusFuture')">✅</span>
                      </span>
                    </button>
                    
                    <button @click="deleteItem(category.id, item.id)" class="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </button>
                  </div>
                  
                  
                  <div class="flex items-start space-x-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                    <textarea 
                      :ref="(el) => { if (el) itemInputRefs[category.id] = el }"
                      v-model="newItemText[category.id]" 
                      @keydown.enter.exact.prevent="addItem(category.id)"
                      @input="autoResizeNewItemTextarea($event)"
                      :placeholder="t('kanban.taskDetail.sections.checklist.addItemPlaceholder')" 
                      rows="1"
                      class="flex-1 px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-700 dark:text-white transition-all duration-200 resize-none"
                    ></textarea>
                    <button @click="addItem(category.id)" class="px-4 py-3 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-lg hover:from-emerald-600 hover:to-green-600 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-105">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
                </template>
              </draggable>

              <!-- Criar Nova Categoria (sempre por último) -->
              <div class="bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
                <div class="flex items-center space-x-3">
                  <div class="w-8 h-8 bg-gradient-to-br from-emerald-400 to-green-500 rounded-lg flex items-center justify-center">
                    <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                  </div>
                  <input 
                    v-model="newCategoryName" 
                    @keypress.enter="createCategory"
                    type="text" 
                    :placeholder="t('kanban.taskDetail.sections.checklist.newCategoryPlaceholder')" 
                    class="flex-1 px-4 py-3 border border-emerald-300 dark:border-emerald-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                  >
                  <button @click="createCategory" class="px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-lg hover:from-emerald-600 hover:to-green-600 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-105">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                  </button>
                </div>
              </div>
              </div>
            </div>
          
            </template>
          </TaskDetailPlanSection>

          <!-- Anexos e Imagens -->
          <TaskDetailPlanSection
            :visible="sectionEnabled.anexos"
            section-key="anexos"
            :blocked="planBlocked('anexos')"
            :body-expanded="!minimizedCards.anexos"
            :gate-title="planGateTitle('anexos')"
          >
            <template #header>
<div class="flex items-center justify-between mb-3" :class="{ 'border-b border-dashed border-gray-300 dark:border-gray-500 pb-3': minimizedCards.anexos }">
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center shadow-md">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ t('kanban.taskDetail.sections.attachments.title') }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.attachments.subtitle') }}</p>
                </div>
              </div>
              <button @click="toggleCard('anexos')" class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200" :class="{ 'rotate-180': minimizedCards.anexos }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
            </div>
            </template>
            <template #body>
            <div v-show="!minimizedCards.anexos" class="modal-surface bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 relative">
              <AnexosSection 
                :taskId="task?.id" 
                :reloadSignal="anexosReloadSignal"
                @anexosUpdated="handleAnexosUpdated"
              />
            </div>
          
            </template>
          </TaskDetailPlanSection>

          <TaskDetailPlanSection
            v-for="group in getCustomGroupsByColumn('col1')"
            :key="`custom-col1-${group.id}`"
            :visible="sectionEnabled[customGroupKey(group.id)] !== false"
            :section-key="customGroupKey(group.id)"
            :blocked="!planCan('customForms')"
            :body-expanded="!minimizedCards[customGroupKey(group.id)]"
            :gate-title="`${t('kanban.taskDetail.sections.customFields.gateTitle')} — ${t('kanban.taskDetail.planGateSuffix')}`"
          >
            <template #header>
<div class="flex items-center justify-between mb-3" :class="{ 'border-b border-dashed border-gray-300 dark:border-gray-500 pb-3': minimizedCards[customGroupKey(group.id)] }">
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl flex items-center justify-center shadow-md">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14-6H5m14 12H5m14 6H5"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ group.nome }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.customFields.subtitle') }}</p>
                </div>
              </div>
              <button @click="toggleCard(customGroupKey(group.id))" class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200" :class="{ 'rotate-180': minimizedCards[customGroupKey(group.id)] }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
            </div>
            </template>
            <template #body>
            <div v-show="!minimizedCards[customGroupKey(group.id)]" class="modal-surface bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 relative">
              <div class="space-y-3">
                <div v-for="field in group.campos" :key="field.id">
                  <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{{ field.label || t('kanban.taskDetail.sections.customFields.noLabel') }}</label>
                  <input
                    v-if="field.tipo === 'input'"
                    :value="getCustomFieldValue(group.id, field.id, field.tipo)"
                    @input="setCustomFieldValue(group.id, field.id, $event.target.value)"
                    type="text"
                    class="w-full px-2.5 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                  <textarea
                    v-else-if="field.tipo === 'textarea'"
                    :value="getCustomFieldValue(group.id, field.id, field.tipo)"
                    @input="setCustomFieldValue(group.id, field.id, $event.target.value)"
                    rows="3"
                    class="w-full px-2.5 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  ></textarea>
                  <input
                    v-else-if="field.tipo === 'date'"
                    :value="getCustomFieldValue(group.id, field.id, field.tipo)"
                    @input="setCustomFieldValue(group.id, field.id, $event.target.value)"
                    type="date"
                    class="w-full px-2.5 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                  <label v-else-if="field.tipo === 'checkbox'" class="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      :checked="!!getCustomFieldValue(group.id, field.id, field.tipo)"
                      @change="setCustomFieldValue(group.id, field.id, $event.target.checked)"
                      type="checkbox"
                    />
                    <span>{{ t('kanban.taskDetail.sections.customFields.check') }}</span>
                  </label>
                  <label v-else-if="field.tipo === 'toggle'" class="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      :checked="!!getCustomFieldValue(group.id, field.id, field.tipo)"
                      @change="setCustomFieldValue(group.id, field.id, $event.target.checked)"
                      type="checkbox"
                    />
                    <span>{{ t('kanban.taskDetail.sections.customFields.toggle') }}</span>
                  </label>
                  <select
                    v-else-if="field.tipo === 'select'"
                    :value="getCustomFieldValue(group.id, field.id, field.tipo)"
                    @change="setCustomFieldValue(group.id, field.id, $event.target.value)"
                    class="w-full px-2.5 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{{ t('kanban.taskDetail.sections.customFields.select') }}</option>
                    <option v-for="option in parseSelectOptions(field)" :key="option" :value="option">{{ option }}</option>
                  </select>
                </div>
              </div>
            </div>
          
            </template>
          </TaskDetailPlanSection>

        </div>

        <!-- Right Panel - Sidebar -->
        <div
          ref="layoutCol2El"
          class="modal-panel-column hidden md:block md:w-[40%] md:flex-none md:min-h-0 md:border-l border-gray-200 dark:border-gray-700 p-3 space-y-4 overflow-y-auto"
          :class="resolvedHistoricoChatEnabled ? 'lg:w-[30%]' : 'lg:w-[40%]'"
        >
          <!-- Cor da Task -->
          <TaskDetailPlanSection
            :visible="sectionEnabled.cor"
            section-key="cor"
            :blocked="planBlocked('cor')"
            :body-expanded="sectionsVisible.cor"
            :gate-title="planGateTitle('cor')"
            root-class=""
          >
            <template #header>
<div
              class="flex items-center justify-between mb-3"
              :class="{ 'border-b border-dashed border-gray-300 dark:border-gray-500 pb-3': !sectionsVisible.cor }"
            >
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center shadow-md">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ t('kanban.taskDetail.sections.color.title') }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.color.subtitle') }}</p>
                </div>
              </div>
              <button @click="toggleSection('cor')" class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200" :class="{ 'rotate-180': !sectionsVisible.cor }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
            </div>
            </template>
            <template #body>
            <div v-if="sectionsVisible.cor" class="modal-surface bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 relative">
              <div class="space-y-3">
                <ColorSelector v-model="formData.cor" @update:modelValue="saveColor" />
                
                <!-- Status de salvamento da cor -->
                <div class="flex items-center justify-between mt-3">
                  <div class="flex items-center space-x-2">
                    <Transition name="status-fade" mode="out-in">
                      <div v-if="salvandoCor" key="salvando" class="flex items-center space-x-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 rounded-lg transition-all duration-300">
                        <svg class="w-3 h-3 text-blue-600 dark:text-blue-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        <span class="text-xs font-medium text-blue-600 dark:text-blue-400">{{ t('kanban.taskDetail.sections.color.saving') }}</span>
                      </div>
                      <div v-else-if="salvoCor" key="salvo" class="flex items-center space-x-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded-lg transition-all duration-300">
                        <svg class="w-3 h-3 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <span class="text-xs font-medium text-green-600 dark:text-green-400">{{ t('kanban.taskDetail.sections.color.saved') }}</span>
                      </div>
                      <div v-else key="pronto" class="flex items-center space-x-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg transition-all duration-300">
                        <div class="w-3 h-3 rounded-full shadow-sm" :style="{ backgroundColor: formData.cor }"></div>
                        <span class="text-xs font-medium text-gray-600 dark:text-gray-400">{{ t('kanban.taskDetail.sections.color.readyToEdit') }}</span>
                      </div>
                    </Transition>
                  </div>
                </div>
              </div>
              <!-- Separador discreto -->
              <div class="absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-600 to-transparent"></div>
            </div>
          
            </template>
          </TaskDetailPlanSection>

          <!-- Financeiro (Recebíveis) -->
          <TaskDetailPlanSection
            :visible="sectionEnabled.financeiro"
            section-key="financeiro"
            :blocked="planBlocked('financeiro')"
            :body-expanded="sectionsVisible.financeiro"
            :gate-title="planGateTitle('financeiro')"
            root-class=""
          >
            <template #header>
<div
              class="flex items-center justify-between mb-3"
              :class="{ 'border-b border-dashed border-gray-300 dark:border-gray-500 pb-3': !sectionsVisible.financeiro }"
            >
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-md">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ t('kanban.taskDetail.sections.finance.title') }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.finance.subtitle') }}</p>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <button
                  v-if="!planBlocked('financeiro')"
                  @click="openRecebivelModal()"
                  class="px-2 py-1 text-xs font-semibold rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white transition-colors"
                >
                  {{ t('kanban.taskDetail.sections.finance.addButton') }}
                </button>
                <button @click="toggleSection('financeiro')" class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                  <svg class="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200" :class="{ 'rotate-180': !sectionsVisible.financeiro }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
              </div>
            </div>
            </template>
            <template #body>
            <div v-if="sectionsVisible.financeiro" class="modal-surface bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 relative">
              <div v-if="recebiveisOrdenados.length === 0" class="text-sm text-gray-500 dark:text-gray-400">
                {{ t('kanban.taskDetail.sections.finance.noReceivables') }}
              </div>
              <div v-else class="space-y-2">
                <div
                  v-for="item in recebiveisOrdenados"
                  :key="item.id"
                  class="modal-surface bg-white/90 dark:bg-gray-800 rounded-xl p-3 shadow-sm border relative overflow-hidden"
                  :class="recebivelStatusClass(item)"
                >
                  <div class="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-emerald-500/70 via-blue-500/60 to-violet-500/70"></div>
                  <div class="flex items-center justify-between gap-2">
                    <div class="min-w-0 flex items-center gap-2">
                      <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center shadow p-1">
                        <img src="@/assets/money-icon.png" alt="" class="w-5 h-5 object-contain" aria-hidden="true" />
                      </div>
                      <div class="min-w-0">
                        <p class="text-sm font-semibold truncate">{{ formatMoneyAmount(item.valor || 0, locale) }}</p>
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                          {{ t('kanban.taskDetail.sections.finance.dueLabel', { date: formatDateOnly(item.dataVencimento) }) }}
                        </p>
                      </div>
                    </div>
                    <span class="text-[11px] font-semibold px-2 py-1 rounded-full border" :class="recebivelBadgeClass(item)">
                      {{ recebivelStatusLabel(item) }}
                    </span>
                  </div>
                  <div v-if="item.pago && item.dataPagamento" class="text-[11px] text-emerald-600 dark:text-emerald-400 mt-2">
                    {{ t('kanban.taskDetail.sections.finance.paidOn', { date: formatDateOnly(item.dataPagamento) }) }}
                  </div>
                  <div class="flex items-center justify-end gap-2 mt-2">
                    <button @click="openRecebivelModal(item)" class="px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white/80 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                      {{ t('kanban.taskDetail.sections.finance.edit') }}
                    </button>
                    <button
                      @click="toggleRecebivelPago(item)"
                      class="px-2.5 py-1.5 text-xs rounded-lg text-white shadow-sm transition-colors"
                      :class="item.pago ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-500 hover:bg-emerald-600'"
                    >
                      {{ item.pago ? t('kanban.taskDetail.sections.finance.undoPayment') : t('kanban.taskDetail.sections.finance.markPaid') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          
            </template>
          </TaskDetailPlanSection>

          <!-- Progresso da tarefa -->
          <TaskDetailPlanSection
            :visible="sectionEnabled.progressoTarefa"
            section-key="progressoTarefa"
            :blocked="planBlocked('progressoTarefa')"
            :body-expanded="sectionsVisible.progressoTarefa"
            :gate-title="planGateTitle('progressoTarefa')"
            root-class=""
          >
            <template #header>
<div
              class="flex items-center justify-between mb-3"
              :class="{ 'border-b border-dashed border-gray-300 dark:border-gray-500 pb-3': !sectionsVisible.progressoTarefa }"
            >
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-6m4 6V7m4 10v-3M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ t('kanban.taskDetail.sections.progress.title') }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.progress.subtitle') }}</p>
                </div>
              </div>
              <button @click="toggleSection('progressoTarefa')" class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200" :class="{ 'rotate-180': !sectionsVisible.progressoTarefa }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
            </div>
            </template>
            <template #body>
            <div
              v-if="sectionsVisible.progressoTarefa"
              class="task-progress-card rounded-xl border border-gray-200/90 bg-gradient-to-br from-white via-slate-50/40 to-indigo-50/30 p-3.5 shadow-sm dark:border-gray-700 dark:from-gray-800 dark:via-gray-800/90 dark:to-indigo-950/25 sm:p-4"
            >
              <div class="flex flex-col gap-4">
                <!-- Percentual -->
                <div
                  class="flex flex-col rounded-xl border border-gray-200/70 bg-white/70 p-3.5 shadow-sm dark:border-gray-600/60 dark:bg-gray-900/40"
                >
                  <div class="mb-3 flex items-center justify-between gap-2">
                    <div class="flex items-center gap-2">
                      <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-300">
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6m4 6V8m4 11V5M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                        </svg>
                      </span>
                      <div>
                        <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.progress.completed') }}</p>
                        <p class="text-[11px] text-gray-500 dark:text-gray-500">{{ t('kanban.taskDetail.sections.progress.dragHint') }}</p>
                      </div>
                    </div>
                    <span class="tabular-nums text-2xl font-bold tracking-tight text-indigo-600 dark:text-indigo-300">
                      {{ formData.progressoTarefaPercentual ?? 0 }}<span class="text-base font-semibold text-indigo-500/90 dark:text-indigo-400/90">%</span>
                    </span>
                  </div>
                  <div class="flex flex-col gap-3">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      :value="formData.progressoTarefaPercentual ?? 0"
                      @input="formData.progressoTarefaPercentual = Number($event.target.value)"
                      @change="saveFinanceiro"
                      class="financeiro-range financeiro-range--task w-full cursor-pointer appearance-none rounded-lg"
                      :aria-valuenow="formData.progressoTarefaPercentual ?? 0"
                      aria-valuemin="0"
                      aria-valuemax="100"
                      :aria-label="t('kanban.taskDetail.sections.progress.completionAria')"
                    />
                    <div class="financeiro-progress financeiro-progress--task h-2.5 w-full overflow-hidden rounded-full">
                      <div
                        class="financeiro-progress-bar h-full rounded-full transition-[width] duration-300 ease-out"
                        :style="getProgressPreviewStyle(formData.progressoTarefaPercentual)"
                      ></div>
                    </div>
                  </div>
                </div>
                <!-- Prazo -->
                <div
                  class="flex flex-col rounded-xl border border-gray-200/70 bg-white/70 p-3.5 shadow-sm dark:border-gray-600/60 dark:bg-gray-900/40"
                >
                  <div class="mb-2 flex items-center gap-2">
                    <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                    </span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.progress.deadline') }}</p>
                      <p class="text-[11px] text-gray-500 dark:text-gray-500">{{ t('kanban.taskDetail.sections.progress.deadlineHint') }}</p>
                    </div>
                  </div>
                  <div class="task-prazo-datepicker relative mt-1 min-h-[2.75rem]">
                    <VueDatePicker
                      v-model="prazoTarefaDate"
                      :enable-time-picker="false"
                      format="dd/MM/yyyy"
                      preview-format="dd/MM/yyyy"
                      :locale="datePickerLocale"
                      :placeholder="t('kanban.taskDetail.sections.finance.selectDate')"
                      :clearable="true"
                      :auto-apply="true"
                      :teleport="true"
                      class="custom-datepicker custom-datepicker--task-prazo w-full"
                      @update:model-value="saveFinanceiro"
                    />
                  </div>
                  <p
                    v-if="formData.prazoTarefa"
                    class="mt-2 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400"
                  >
                    Prazo definido:
                    <span class="font-medium text-gray-700 dark:text-gray-300">{{ formatDateOnly(formData.prazoTarefa) }}</span>
                  </p>
                </div>
              </div>
            </div>
          
            </template>
          </TaskDetailPlanSection>

          <div v-if="showRecebivelModal" class="fixed inset-0 z-[12000] flex items-center justify-center bg-black/60 px-3">
            <div class="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-2xl p-5 space-y-4 relative overflow-hidden">
              <div class="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-blue-500 to-violet-500"></div>
              <div class="flex items-center justify-between pt-1">
                <h4 class="text-base font-semibold text-gray-900 dark:text-white tracking-tight">
                  {{ recebivelEmEdicaoId ? t('kanban.taskDetail.sections.finance.editReceivable') : t('kanban.taskDetail.sections.finance.newReceivable') }}
                </h4>
                <button @click="closeRecebivelModal" class="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                  <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
              <div>
                <label class="block text-sm mb-1 text-gray-700 dark:text-gray-300 font-medium">{{ t('kanban.taskDetail.sections.finance.valueLabel') }}</label>
                <div class="money-input-with-prefix">
                  <MoneyInputPrefix />
                  <input
                    :value="recebivelValorDisplay"
                    @input="handleRecebivelValorInput"
                    inputmode="numeric"
                    autocomplete="off"
                    :placeholder="t('kanban.taskDetail.sections.finance.amountPlaceholder')"
                    class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
              <div>
                <label class="block text-sm mb-1 text-gray-700 dark:text-gray-300 font-medium">{{ t('kanban.taskDetail.sections.finance.dueDate') }}</label>
                <div class="task-prazo-datepicker relative mt-1 min-h-[2.75rem]">
                  <VueDatePicker
                    v-model="recebivelDataVencimentoDate"
                    :enable-time-picker="false"
                    format="dd/MM/yyyy"
                    preview-format="dd/MM/yyyy"
                    :locale="datePickerLocale"
                    :placeholder="t('kanban.taskDetail.sections.finance.selectDate')"
                    :clearable="true"
                    :auto-apply="true"
                    :teleport="true"
                    class="custom-datepicker custom-datepicker--task-prazo w-full"
                  />
                </div>
                <p class="text-[11px] mt-1 text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.finance.selectedDate', { date: formatDateOnly(recebivelForm.dataVencimento) }) }}</p>
              </div>
              <div class="flex justify-end gap-2">
                <button @click="closeRecebivelModal" class="px-3.5 py-2 text-sm rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors">{{ t('kanban.taskDetail.footer.cancel') }}</button>
                <button @click="saveRecebivelItem" class="px-3.5 py-2 text-sm rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm transition-colors">{{ t('kanban.taskDetail.assignModal.save') }}</button>
              </div>
            </div>
          </div>

          <!-- Tags -->
          <TaskDetailPlanSection
            :visible="sectionEnabled.tags"
            section-key="tags"
            :blocked="planBlocked('tags')"
            :body-expanded="sectionsVisible.tags"
            :gate-title="planGateTitle('tags')"
            root-class=""
          >
            <template #header>
<div
              class="flex items-center justify-between mb-3"
              :class="{ 'border-b border-dashed border-gray-300 dark:border-gray-500 pb-3': !sectionsVisible.tags }"
            >
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ t('kanban.taskDetail.sections.tags.title') }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.tags.subtitle') }}</p>
                </div>
              </div>
              <button @click="toggleSection('tags')" class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200" :class="{ 'rotate-180': !sectionsVisible.tags }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
            </div>
            </template>
            <template #body>
            <div v-if="sectionsVisible.tags" class="modal-surface bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 relative">
              <div class="space-y-3">
                <div v-for="category in workspaceTags" :key="category.id" class="space-y-2">
                  <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center space-x-2">
                    <span>{{ category.nome }}</span>
                    <span class="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 text-xs rounded-full">{{ category.tags.length }}</span>
                  </h4>
                  <div class="flex flex-wrap gap-1.5">
                    <button 
                      v-for="tag in category.tags" 
                      :key="tag.id"
                      @click="toggleTag(tag.id)"
                      :class="[
                        'px-2 py-1 rounded text-xs font-medium transition-all duration-200 hover:scale-105 flex items-center space-x-1.5 border',
                        selectedTags.includes(tag.id) 
                          ? 'shadow-md border-transparent' 
                          : 'bg-white dark:bg-gray-600 hover:bg-gray-100 dark:hover:bg-gray-500 border-gray-200 dark:border-gray-500'
                      ]"
                      :style="selectedTags.includes(tag.id) ? { 
                        backgroundColor: tag.cor,
                        color: getContrastColor(tag.cor)
                      } : {}"
                    >
                      <!-- Emoji da tag -->
                      <span class="text-sm">{{ getTagIcon(tag.icone) }}</span>
                      <!-- Nome da tag -->
                      <span>{{ tag.nome }}</span>
                    </button>
                  </div>
                </div>
              
              <!-- Empty State -->
              <div v-if="workspaceTags.length === 0" class="text-center py-8">
                <svg class="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                </svg>
                <p class="text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.tags.empty') }}</p>
              </div>
              </div>
            </div>
          
            </template>
          </TaskDetailPlanSection>

          <!-- Usuários Atribuídos -->
          <TaskDetailPlanSection
            :visible="sectionEnabled.usuariosAtribuidos"
            section-key="usuariosAtribuidos"
            :blocked="planBlocked('usuariosAtribuidos')"
            :body-expanded="sectionsVisible.usuariosAtribuidos"
            :gate-title="planGateTitle('usuariosAtribuidos')"
            root-class=""
          >
            <template #header>
<div
              class="flex items-center justify-between mb-3"
              :class="{ 'border-b border-dashed border-gray-300 dark:border-gray-500 pb-3': !sectionsVisible.usuariosAtribuidos }"
            >
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center shadow-md">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ t('kanban.taskDetail.sections.assignedUsers.title') }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.assignedUsers.subtitle') }}</p>
                </div>
              </div>
              <button @click="toggleSection('usuariosAtribuidos')" class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200" :class="{ 'rotate-180': !sectionsVisible.usuariosAtribuidos }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
            </div>
            </template>
            <template #body>
            <div v-if="sectionsVisible.usuariosAtribuidos" class="modal-surface bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 relative">
              <div class="space-y-2">
              <div v-for="user in formData.usuariosAtribuidos" :key="user.id" class="modal-item-surface flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div class="flex items-center space-x-2">
                  <!-- Avatar com foto de perfil -->
                  <div class="relative w-8 h-8">
                    <!-- Foto do usuário -->
                    <img 
                      v-if="user.fotoPerfil && user.fotoPerfil.trim()" 
                      :src="user.fotoPerfil" 
                      :alt="user.nomeCompleto"
                      class="w-8 h-8 rounded-full border-2 border-white dark:border-gray-700 object-cover hover:scale-110 transition-all duration-300"
                    />
                    <!-- Spinner temporário enquanto carrega dados -->
                    <div 
                      v-else-if="fotosCarregando" 
                      class="w-8 h-8 rounded-full border-2 border-white dark:border-gray-700 bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center hover:scale-110 transition-all duration-300 shadow-sm"
                    >
                      <!-- Spinner animado -->
                      <div class="loading-spinner"></div>
                    </div>
                    <!-- Iniciais definitivas quando não há foto -->
                    <div 
                      v-else 
                      class="w-8 h-8 rounded-full border-2 border-white dark:border-gray-700 bg-gradient-to-br from-blue-500 to-blue-600 text-white text-xs flex items-center justify-center font-medium hover:scale-110 transition-all duration-300 shadow-sm"
                    >
                      {{ getUserInitials(user) }}
                    </div>
                  </div>
                  <span class="text-sm text-gray-900 dark:text-white">{{ user.nomeCompleto }}</span>
                </div>
                <button @click="removeUser(user.id)" class="text-red-500 hover:text-red-700 p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
              <button @click="openUserSelector" class="w-full p-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 hover:border-blue-500 hover:text-blue-500 transition-colors text-sm">
                {{ t('kanban.taskDetail.sections.assignedUsers.assignButton') }}
              </button>
              </div>
            </div>
          
            </template>
          </TaskDetailPlanSection>

          <!-- Controles de Atividade -->
          <TaskDetailPlanSection
            :visible="sectionEnabled.controlesAtividade"
            section-key="controlesAtividade"
            :blocked="planBlocked('controlesAtividade')"
            :body-expanded="sectionsVisible.controlesAtividade"
            :gate-title="planGateTitle('controlesAtividade')"
            root-class=""
          >
            <template #header>
<div
              class="flex items-center justify-between mb-3"
              :class="{ 'border-b border-dashed border-gray-300 dark:border-gray-500 pb-3': !sectionsVisible.controlesAtividade }"
            >
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ t('kanban.taskDetail.sections.activity.title') }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.activity.subtitle') }}</p>
                </div>
              </div>
              <button @click="toggleSection('controlesAtividade')" class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200" :class="{ 'rotate-180': !sectionsVisible.controlesAtividade }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
            </div>
            </template>
            <template #body>
            <div v-if="sectionsVisible.controlesAtividade" class="modal-surface bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 shadow-lg border border-gray-200 dark:border-gray-700">
              <!-- Botões de Controle -->
              <div class="mb-5 flex flex-col gap-2 sm:flex-row sm:justify-center sm:gap-3">
                <button 
                  v-if="!usuarioAtivo"
                  type="button"
                  @click="iniciarAtividade"
                  :disabled="loadingAtividade || processandoAtividade"
                  class="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-700/10 bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-emerald-700 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-emerald-600 disabled:hover:shadow-sm dark:border-emerald-500/20 dark:focus-visible:ring-offset-gray-900 sm:w-auto sm:min-w-[200px]"
                  :title="t('kanban.taskDetail.sections.activity.startTitle')"
                >
                  <span class="flex h-8 w-8 items-center justify-center rounded-md bg-white/15">
                    <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </span>
                  <span>{{ processandoAtividade ? t('kanban.taskDetail.sections.activity.pleaseWait') : t('kanban.taskDetail.sections.activity.startWork') }}</span>
                </button>
                
                <button 
                  v-else
                  type="button"
                  @click="pararAtividade"
                  :disabled="loadingAtividade || processandoAtividade"
                  class="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-red-700/10 bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-red-700 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-red-600 disabled:hover:shadow-sm dark:border-red-500/20 dark:focus-visible:ring-offset-gray-900 sm:w-auto sm:min-w-[200px]"
                  :title="t('kanban.taskDetail.sections.activity.stopTitle')"
                >
                  <span class="flex h-8 w-8 items-center justify-center rounded-md bg-white/15">
                    <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M6 6h12v12H6z"/>
                    </svg>
                  </span>
                  <span>{{ processandoAtividade ? t('kanban.taskDetail.sections.activity.pleaseWait') : t('kanban.taskDetail.sections.activity.stopWork') }}</span>
                </button>
              </div>

              <!-- Loading -->
              <div v-if="loadingAtividade" class="mb-6">
                <div class="flex items-center justify-center space-x-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-700">
                  <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                  <span class="text-blue-700 dark:text-blue-300 font-medium">{{ t('kanban.taskDetail.sections.activity.processing') }}</span>
                </div>
              </div>

              <!-- Usuários Trabalhando -->
              <div v-if="atividadesAtivas.length > 0" class="mb-6">
                <div class="flex items-center space-x-2 mb-4">
                  <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <h4 class="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    Usuários trabalhando ({{ atividadesAtivas.length }})
                  </h4>
                </div>
                <div class="grid grid-cols-1 gap-4">
                  <div 
                    v-for="atividade in atividadesAtivas" 
                    :key="atividade.id"
                    class="flex items-center space-x-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 rounded-xl border border-green-200 dark:border-green-700 shadow-sm"
                  >
                    <div class="relative">
                      <img 
                        :src="atividade.usuarioFotoPerfil || '/default-avatar.png'" 
                        :alt="atividade.usuarioNome"
                        class="w-12 h-12 rounded-full object-cover border-2 border-green-300 dark:border-green-600"
                      />
                      <div class="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-base font-semibold text-gray-900 dark:text-white">
                        {{ atividade.usuarioNome }}
                      </p>
                      <div class="flex items-center space-x-2 mt-1">
                        <div class="flex items-center space-x-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded-md">
                          <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <svg class="w-3 h-3 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                          </svg>
                        </div>
                        <span class="text-xs text-gray-500 dark:text-gray-400">
                          {{ calcularTempoDecorrido(atividade.dataInicio) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Log de Atividades -->
              <div>
                <div class="flex items-center space-x-2 mb-3">
                  <svg class="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                  </svg>
                  <h4 class="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    Log de Atividades ({{ atividadesLog.length }})
                  </h4>
                </div>
                
                <div v-if="atividadesLog.length > 0" class="space-y-2.5">
                  <div 
                    v-for="atividade in atividadesLog" 
                    :key="atividade.id"
                    class="overflow-hidden rounded-xl border border-gray-200/90 bg-gradient-to-br from-white to-gray-50/90 shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-md dark:border-gray-700 dark:from-gray-800/90 dark:to-gray-900/60 dark:hover:border-gray-600"
                    :class="atividade.dataFim ? 'border-l-[3px] border-l-blue-500 dark:border-l-blue-400' : 'border-l-[3px] border-l-emerald-500 dark:border-l-emerald-400'"
                  >
                    <div class="p-3">
                      <div class="flex items-start justify-between gap-3">
                        <div class="flex min-w-0 flex-1 items-start gap-2.5">
                          <img 
                            :src="atividade.usuarioFotoPerfil || '/default-avatar.png'" 
                            :alt="atividade.usuarioNome"
                            class="h-9 w-9 shrink-0 rounded-full object-cover ring-2 ring-gray-100 dark:ring-gray-700"
                          />
                          <div class="min-w-0 flex-1">
                            <p class="truncate text-sm font-semibold leading-tight text-gray-900 dark:text-white">
                              {{ atividade.usuarioNome }}
                            </p>
                            <p class="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                              {{ atividade.colunaNome || 'Sem coluna' }}
                            </p>
                          </div>
                        </div>
                        <div class="flex shrink-0 flex-col items-end gap-1.5 text-right">
                          <span
                            class="inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
                            :class="atividade.dataFim
                              ? 'border-blue-200/80 bg-blue-50 text-blue-800 dark:border-blue-800/60 dark:bg-blue-950/50 dark:text-blue-200'
                              : 'border-emerald-200/80 bg-emerald-50 text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200'"
                          >
                            {{ atividade.dataFim ? t('kanban.taskDetail.sections.activity.statusFinished') : t('kanban.taskDetail.sections.activity.statusInProgress') }}
                          </span>
                          <span class="text-xs font-bold tabular-nums text-gray-900 dark:text-white">
                            {{ calcularDuracao(atividade.dataInicio, atividade.dataFim) }}
                          </span>
                        </div>
                      </div>
                      <div
                        class="mt-3 grid gap-2 border-t border-gray-100 pt-3 dark:border-gray-700/80"
                        :class="atividade.dataFim ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'"
                      >
                        <div class="flex gap-2 rounded-lg bg-gray-50/90 px-2.5 py-2 dark:bg-gray-900/50">
                          <div class="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300">
                            <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                          </div>
                          <div class="min-w-0">
                            <p class="text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.activity.sessionStart') }}</p>
                            <p class="mt-0.5 font-mono text-xs leading-snug text-gray-900 dark:text-gray-100">{{ formatarData(atividade.dataInicio) }}</p>
                          </div>
                        </div>
                        <div v-if="atividade.dataFim" class="flex gap-2 rounded-lg bg-gray-50/90 px-2.5 py-2 dark:bg-gray-900/50">
                          <div class="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                            <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                          </div>
                          <div class="min-w-0">
                            <p class="text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.activity.end') }}</p>
                            <p class="mt-0.5 font-mono text-xs leading-snug text-gray-900 dark:text-gray-100">{{ formatarData(atividade.dataFim) }}</p>
                          </div>
                        </div>
                        <div v-else class="flex items-center gap-2 rounded-lg border border-emerald-200/70 bg-emerald-50/80 px-2.5 py-2 dark:border-emerald-800/50 dark:bg-emerald-950/30">
                          <span class="relative flex h-2 w-2 shrink-0">
                            <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
                            <span class="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
                          </span>
                          <span class="text-xs font-medium text-emerald-800 dark:text-emerald-200">{{ t('kanban.taskDetail.sections.activity.activeSession') }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div v-else class="text-center py-12">
                  <div class="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                    <svg class="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                    </svg>
                  </div>
                  <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">{{ t('kanban.taskDetail.sections.activity.emptyTitle') }}</h3>
                  <p class="text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.activity.emptyHint', { button: t('kanban.taskDetail.sections.activity.startButton') }) }}</p>
                </div>
              </div>
            </div>
          
            </template>
          </TaskDetailPlanSection>

          <!-- Agendamento -->
          <TaskDetailPlanSection
            :visible="sectionEnabled.agendamento"
            section-key="agendamento"
            :blocked="planBlocked('agendamento')"
            :body-expanded="sectionsVisible.agendamento"
            :gate-title="planGateTitle('agendamento')"
            root-class="space-y-4"
          >
            <template #header>
<div
              class="flex items-center justify-between mb-3"
              :class="{ 'border-b border-dashed border-gray-300 dark:border-gray-500 pb-3': !sectionsVisible.agendamento }"
            >
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ t('kanban.taskDetail.sections.schedule.title') }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.schedule.subtitle') }}</p>
                </div>
              </div>
              <button @click="toggleSection('agendamento')" class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200" :class="{ 'rotate-180': !sectionsVisible.agendamento }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
            </div>
            </template>
            <template #body>
            <div v-if="sectionsVisible.agendamento" class="modal-surface bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
              <!-- Botão de ação -->
              <div class="modal-surface-gradient p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600">
                <button @click="abrirModalAgendamento" class="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-500 via-blue-500 to-indigo-600 text-white rounded-lg hover:from-purple-600 hover:via-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02] font-semibold">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  <span>{{ formData.agendamento ? t('kanban.taskDetail.sections.schedule.rescheduleButton') : t('kanban.taskDetail.sections.schedule.scheduleButton') }}</span>
                </button>
              </div>

              <!-- Conteúdo do agendamento -->
              <div class="p-4">
                <!-- Agendamento existente -->
                <div v-if="formData.agendamento" class="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-700 relative overflow-hidden">
                  <!-- Decoração de fundo -->
                  <div class="absolute top-0 right-0 w-12 h-12 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full -translate-y-6 translate-x-6"></div>
                  <div class="absolute bottom-0 left-0 w-8 h-8 bg-gradient-to-tr from-indigo-400/20 to-blue-400/20 rounded-full translate-y-4 -translate-x-4"></div>
                  
                  <div class="relative z-10">
                    <div class="flex items-center justify-between mb-2">
                      <div class="flex items-center space-x-2">
                        <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
                          <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                          </svg>
                        </div>
                        <div>
                          <div class="text-xs font-semibold text-blue-900 dark:text-blue-100 uppercase tracking-wide">
                            {{ t('kanban.taskDetail.sections.schedule.scheduledFor') }}
                          </div>
                          <div class="text-lg font-bold text-blue-800 dark:text-blue-200">
                            {{ formatDataBrasileira(formData.agendamento) }}
                          </div>
                          <div class="text-sm font-medium text-blue-600 dark:text-blue-300">
                            {{ formatAgendamentoHora(formData.agendamento) }}
                          </div>
                        </div>
                      </div>
                      
                      
                      <button @click="removerAgendamento" class="w-7 h-7 bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-md flex items-center justify-center hover:bg-red-100 dark:hover:bg-red-900/30 transition-all duration-200 shadow-sm hover:shadow-md" :title="t('kanban.taskDetail.sections.schedule.remove')">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    </div>
                    
                    <!-- Status badge e Motivo -->
                    <div class="flex items-center space-x-3">
                      <div class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                        <div class="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5"></div>
                        {{ t('kanban.taskDetail.sections.schedule.scheduled') }}
                      </div>
                      
                      <!-- Motivo do Agendamento -->
                      <div v-if="formData.agendamento?.motivo" class="reason-card">
                        <div class="reason-icon" :style="{ backgroundColor: getMotivoCor(formData.agendamento.motivo) }">
                          <span>{{ getMotivoIcon(formData.agendamento.motivo) }}</span>
                        </div>
                        <div class="reason-content">
                          <h3>{{ formData.agendamento.motivo }}</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              
                <!-- Nenhum agendamento -->
                <div v-else class="text-center py-8">
                  <div class="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md">
                    <svg class="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                  <h4 class="text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">{{ t('kanban.taskDetail.sections.schedule.emptyTitle') }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto leading-relaxed">
                    {{ t('kanban.taskDetail.sections.schedule.emptyHint') }}
                  </p>
                </div>
              </div>
            </div>
          
            </template>
          </TaskDetailPlanSection>

          <TaskDetailPlanSection
            v-for="group in getCustomGroupsByColumn('col2')"
            :key="`custom-col2-${group.id}`"
            :visible="sectionEnabled[customGroupKey(group.id)] !== false"
            :section-key="customGroupKey(group.id)"
            :blocked="!planCan('customForms')"
            :body-expanded="!minimizedCards[customGroupKey(group.id)]"
            :gate-title="`${t('kanban.taskDetail.sections.customFields.gateTitle')} — ${t('kanban.taskDetail.planGateSuffix')}`"
          >
            <template #header>
<div class="flex items-center justify-between mb-3" :class="{ 'border-b border-dashed border-gray-300 dark:border-gray-500 pb-3': minimizedCards[customGroupKey(group.id)] }">
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl flex items-center justify-center shadow-md">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14-6H5m14 12H5m14 6H5"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ group.nome }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.sections.customFields.subtitle') }}</p>
                </div>
              </div>
              <button @click="toggleCard(customGroupKey(group.id))" class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200" :class="{ 'rotate-180': minimizedCards[customGroupKey(group.id)] }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
            </div>
            </template>
            <template #body>
            <div v-show="!minimizedCards[customGroupKey(group.id)]" class="modal-surface bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 relative">
              <div class="space-y-3">
                <div v-for="field in group.campos" :key="field.id">
                  <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{{ field.label || t('kanban.taskDetail.sections.customFields.noLabel') }}</label>
                  <input
                    v-if="field.tipo === 'input'"
                    :value="getCustomFieldValue(group.id, field.id, field.tipo)"
                    @input="setCustomFieldValue(group.id, field.id, $event.target.value)"
                    type="text"
                    class="w-full px-2.5 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                  <textarea
                    v-else-if="field.tipo === 'textarea'"
                    :value="getCustomFieldValue(group.id, field.id, field.tipo)"
                    @input="setCustomFieldValue(group.id, field.id, $event.target.value)"
                    rows="3"
                    class="w-full px-2.5 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  ></textarea>
                  <input
                    v-else-if="field.tipo === 'date'"
                    :value="getCustomFieldValue(group.id, field.id, field.tipo)"
                    @input="setCustomFieldValue(group.id, field.id, $event.target.value)"
                    type="date"
                    class="w-full px-2.5 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                  <label v-else-if="field.tipo === 'checkbox'" class="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      :checked="!!getCustomFieldValue(group.id, field.id, field.tipo)"
                      @change="setCustomFieldValue(group.id, field.id, $event.target.checked)"
                      type="checkbox"
                    />
                    <span>{{ t('kanban.taskDetail.sections.customFields.check') }}</span>
                  </label>
                  <label v-else-if="field.tipo === 'toggle'" class="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      :checked="!!getCustomFieldValue(group.id, field.id, field.tipo)"
                      @change="setCustomFieldValue(group.id, field.id, $event.target.checked)"
                      type="checkbox"
                    />
                    <span>{{ t('kanban.taskDetail.sections.customFields.toggle') }}</span>
                  </label>
                  <select
                    v-else-if="field.tipo === 'select'"
                    :value="getCustomFieldValue(group.id, field.id, field.tipo)"
                    @change="setCustomFieldValue(group.id, field.id, $event.target.value)"
                    class="w-full px-2.5 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{{ t('kanban.taskDetail.sections.customFields.select') }}</option>
                    <option v-for="option in parseSelectOptions(field)" :key="option" :value="option">{{ option }}</option>
                  </select>
                </div>
              </div>
            </div>
          
            </template>
          </TaskDetailPlanSection>

        </div>

        <!-- Chat Panel -->
        <div v-if="resolvedHistoricoChatEnabled && !isMobileViewport && !isTabletViewport" class="modal-panel-column hidden lg:flex lg:flex-col lg:min-h-0 lg:w-[30%] lg:flex-none lg:border-l border-gray-200 dark:border-gray-700 p-0 overflow-hidden">
          <TaskChatPanel
            :task-id="task?.id || null"
            :plan-blocked="planBlocked('historicoChat')"
            incoming-bubble-light-text
            @chatAnexosUpdated="handleChatAnexosUpdated"
          />
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-surface-gradient flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
        <button @click="closeModal" class="w-full sm:w-auto px-6 py-2.5 text-gray-700 dark:text-gray-300 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-white dark:hover:bg-gray-700 hover:shadow-sm transition-all duration-200 font-medium">
          {{ t('kanban.taskDetail.footer.cancel') }}
        </button>
        <button @click="saveTask" class="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md">
          {{ t('kanban.taskDetail.footer.save') }}
        </button>
      </div>

      <div
        v-if="showMobileChat && (isMobileViewport || isTabletViewport) && resolvedHistoricoChatEnabled"
        class="absolute inset-0 z-30 bg-white dark:bg-gray-800 flex flex-col"
        @click.stop
      >
        <div class="flex items-center justify-between px-3 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-[var(--bg-secondary)]">
          <button
            type="button"
            @click="showMobileChat = false"
            class="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
            {{ t('kanban.taskDetail.mobileChat.back') }}
          </button>
          <h3 class="text-sm font-semibold text-gray-900 dark:text-white">{{ t('kanban.taskDetail.mobileChat.title') }}</h3>
          <div class="w-14"></div>
        </div>
        <div class="flex-1 min-h-0 overflow-y-auto">
          <TaskChatPanel
            :task-id="task?.id || null"
            :plan-blocked="planBlocked('historicoChat')"
            incoming-bubble-light-text
            @chatAnexosUpdated="handleChatAnexosUpdated"
          />
        </div>
      </div>
    </div>

    <!-- Modal de Seleção de Usuários para Item do Checklist -->
    <Teleport to="body">
    <div v-if="showUserSelectorForItem" class="fixed inset-0 z-[12050] flex items-center justify-center bg-black bg-opacity-50" @click.stop @keydown.escape.window="showUserSelectorForItem = false">
      <div class="modal-surface bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-96 mx-4 max-h-[80vh] overflow-hidden" @click.stop>
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ t('kanban.taskDetail.assignModal.title') }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.assignModal.subtitle') }}</p>
          </div>
          <button @click="showUserSelectorForItem = false" class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Lista de Usuários -->
        <div class="p-4 max-h-96 overflow-y-auto">
          <div v-if="workspaceUsers.length === 0" class="text-center py-8">
            <div class="text-gray-500 dark:text-gray-400">
              <svg class="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
              </svg>
              <p class="text-sm font-medium mb-1">{{ t('kanban.taskDetail.assignModal.noUsers') }}</p>
              <p class="text-xs">{{ t('kanban.taskDetail.assignModal.onlyYou') }}</p>
            </div>
          </div>
          
          <div v-else class="space-y-2">
            <!-- Usuários do workspace -->
            <div 
              v-for="user in workspaceUsers" 
              :key="user.id"
              @click="toggleUserSelection(user.id)"
              class="modal-item-surface flex items-center space-x-4 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors"
              :class="{ 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700': selectedUsers.includes(user.id) }"
            >
              <!-- Avatar do usuário -->
              <div class="relative">
                <img 
                  v-if="user.fotoPerfil && user.fotoPerfil.trim()" 
                  :src="user.fotoPerfil" 
                  :alt="user.nomeCompleto"
                  class="w-12 h-12 rounded-full object-cover border-2 border-white dark:border-gray-600 transition-all duration-300"
                />
                <!-- Spinner temporário enquanto carrega dados -->
                <div 
                  v-else-if="fotosCarregando" 
                  class="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-600 shadow-sm transition-all duration-300"
                >
                  <!-- Spinner animado maior -->
                  <div class="loading-spinner-large"></div>
                </div>
                <!-- Iniciais definitivas quando não há foto -->
                <div 
                  v-else 
                  class="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-lg font-medium border-2 border-white dark:border-gray-600 shadow-sm transition-all duration-300"
                >
                  {{ getUserInitials(user) }}
                </div>
                
                <!-- Indicador de seleção -->
                <div 
                  v-if="selectedUsers.includes(user.id)"
                  class="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center"
                >
                  <svg class="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                  </svg>
                </div>
              </div>
              <div class="flex-1">
                <p class="text-sm font-medium text-gray-900 dark:text-white">{{ user.nomeCompleto }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">{{ user.email }}</p>
                <div v-if="user.roleWorkspace" class="mt-1">
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200">
                    {{ user.roleWorkspace }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer com contador e botões -->
        <div class="flex justify-between items-center p-4 border-t border-gray-200 dark:border-gray-700">
          <div class="text-sm text-gray-600 dark:text-gray-400">
            <span v-if="selectedUsers.length === 0" class="text-gray-500">{{ t('kanban.taskDetail.assignModal.noneSelected') }}</span>
            <span v-else-if="selectedUsers.length === 1" class="text-blue-600 dark:text-blue-400">{{ t('kanban.taskDetail.assignModal.oneSelected') }}</span>
            <span v-else class="text-blue-600 dark:text-blue-400">{{ t('kanban.taskDetail.assignModal.manySelected', { count: selectedUsers.length }) }}</span>
          </div>
          <div class="flex space-x-2">
            <button
              @click="showUserSelectorForItem = false; currentEditingItem = null; selectedUsers = []"
              class="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors inline-flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
              {{ t('kanban.taskDetail.footer.cancel') }}
            </button>
            <button
              @click="saveSelectedUsers"
              class="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors inline-flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              {{ t('kanban.taskDetail.assignModal.save') }}
            </button>
          </div>
        </div>

    </div>
    </div>
    </Teleport>

    <!-- Modal do Calendário para Data Prevista -->
    <Teleport to="body">
    <div v-if="showDatePicker" class="fixed inset-0 z-[12050] flex items-center justify-center bg-black bg-opacity-50" @click.stop @keydown.escape.window="showDatePicker = false">
      <div class="modal-surface bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh]" @click.stop>
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ t('kanban.taskDetail.assignModal.dateModalTitle') }}</h3>
          <button @click="showDatePicker = false" class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Calendário -->
        <div class="p-6">
          <div class="space-y-6">
            <!-- Calendário bonito -->
            <div class="relative">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                {{ t('kanban.taskDetail.assignModal.expectedDate') }}
              </label>
              <div class="relative">
              <VueDatePicker
                v-model="selectedDate"
                :enable-time-picker="false"
                :format="'dd/MM/yyyy'"
                :locale="datePickerLocale"
                :placeholder="t('kanban.taskDetail.assignModal.selectDate')"
                :clearable="true"
                :auto-apply="true"
                :preview-format="'dd/MM/yyyy'"
                class="custom-datepicker w-full"
              />
              </div>
            </div>
            
            <!-- Opções rápidas -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                {{ t('kanban.taskDetail.assignModal.quickOptions') }}
              </label>
              <div class="grid grid-cols-2 gap-3">
                <button
                  @click="selectedDate = new Date()"
                  class="quick-option-btn inline-flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M12 3v2m0 14v2m9-9h-2M5 12H3m15.364 6.364l-1.414-1.414M7.05 7.05 5.636 5.636m12.728 0-1.414 1.414M7.05 16.95l-1.414 1.414M12 8a4 4 0 100 8 4 4 0 000-8z"/>
                  </svg>
                  <span>{{ t('kanban.taskDetail.assignModal.today') }}</span>
                </button>
                <button
                  @click="selectedDate = new Date(Date.now() + 24 * 60 * 60 * 1000)"
                  class="quick-option-btn inline-flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                  </svg>
                  <span>{{ t('kanban.taskDetail.assignModal.tomorrow') }}</span>
                </button>
                <button
                  @click="selectedDate = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)"
                  class="quick-option-btn inline-flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M12 8v4l3 3M12 22a10 10 0 110-20 10 10 0 010 20z"/>
                  </svg>
                  <span>{{ t('kanban.taskDetail.assignModal.in2Days') }}</span>
                </button>
                <button
                  @click="selectedDate = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)"
                  class="quick-option-btn inline-flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M12 8v5l2.5 1.5M12 22a10 10 0 110-20 10 10 0 010 20z"/>
                  </svg>
                  <span>{{ t('kanban.taskDetail.assignModal.in3Days') }}</span>
                </button>
                <button
                  @click="selectedDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)"
                  class="quick-option-btn inline-flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M8 7V3m8 4V3M5 11h14M5 19h14M5 7h14M7 11h.01M7 19h.01"/>
                  </svg>
                  <span>{{ t('kanban.taskDetail.assignModal.in1Week') }}</span>
                </button>
                <button
                  @click="selectedDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)"
                  class="quick-option-btn inline-flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M8 7V3m8 4V3M5 11h14M5 19h14M5 7h14M12 11h.01M12 19h.01"/>
                  </svg>
                  <span>{{ t('kanban.taskDetail.assignModal.in2Weeks') }}</span>
                </button>
                <button
                  @click="selectedDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)"
                  class="quick-option-btn inline-flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M8 7V3m8 4V3M5 11h14M5 19h14M5 7h14"/>
                  </svg>
                  <span>{{ t('kanban.taskDetail.assignModal.in1Month') }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex justify-between items-center p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            @click="removeDate(currentDateItem.categoryId, currentDateItem.itemId); showDatePicker = false"
            class="px-4 py-2 text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 transition-colors inline-flex items-center gap-2 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 rounded-lg"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m2 0H7m2 0V5a2 2 0 012-2h2a2 2 0 012 2v2"></path>
            </svg>
            {{ t('kanban.taskDetail.assignModal.removeDate') }}
          </button>
          <div class="flex space-x-2">
            <button
              @click="showDatePicker = false"
              class="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
              {{ t('kanban.taskDetail.footer.cancel') }}
            </button>
            <button
              @click="saveDate"
              class="px-4 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors inline-flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              {{ t('kanban.taskDetail.assignModal.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- Modal de Seleção de Usuários para Categoria do Checklist -->
    <Teleport to="body">
    <div v-if="showUserSelectorForCategory" class="fixed inset-0 z-[12050] flex items-center justify-center bg-black bg-opacity-50" @click.stop @keydown.escape.window="showUserSelectorForCategory = false">
      <div class="modal-surface bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-96 mx-4 max-h-[80vh] overflow-hidden" @click.stop>
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ t('kanban.taskDetail.assignModal.categoryTitle') }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.assignModal.subtitle') }}</p>
          </div>
          <button @click="showUserSelectorForCategory = false" class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Lista de Usuários -->
        <div class="p-4 max-h-96 overflow-y-auto">
          <div v-if="workspaceUsers.length === 0" class="text-center py-8">
            <div class="text-gray-500 dark:text-gray-400">
              <svg class="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
              </svg>
              <p class="text-sm font-medium mb-1">{{ t('kanban.taskDetail.assignModal.noUsers') }}</p>
              <p class="text-xs">{{ t('kanban.taskDetail.assignModal.onlyYou') }}</p>
            </div>
          </div>
          
          <div v-else class="space-y-2">
            <!-- Usuários do workspace -->
            <div 
              v-for="user in workspaceUsers" 
              :key="user.id"
              @click="toggleUserSelection(user.id)"
              class="modal-item-surface flex items-center space-x-4 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors"
              :class="{ 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700': selectedUsers.includes(user.id) }"
            >
              <!-- Avatar do usuário -->
              <div class="relative">
                <img 
                  v-if="user.fotoPerfil && user.fotoPerfil.trim()" 
                  :src="user.fotoPerfil" 
                  :alt="user.nomeCompleto"
                  class="w-12 h-12 rounded-full object-cover border-2 border-white dark:border-gray-600 transition-all duration-300"
                />
                <!-- Spinner temporário enquanto carrega dados -->
                <div 
                  v-else-if="fotosCarregando" 
                  class="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-600 shadow-sm transition-all duration-300"
                >
                  <!-- Spinner animado maior -->
                  <div class="loading-spinner-large"></div>
                </div>
                <!-- Iniciais definitivas quando não há foto -->
                <div 
                  v-else 
                  class="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-lg font-medium border-2 border-white dark:border-gray-600 shadow-sm transition-all duration-300"
                >
                  {{ getUserInitials(user) }}
                </div>
                
                <!-- Indicador de seleção -->
                <div 
                  v-if="selectedUsers.includes(user.id)"
                  class="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center"
                >
                  <svg class="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                  </svg>
                </div>
              </div>
              <div class="flex-1">
                <p class="text-sm font-medium text-gray-900 dark:text-white">{{ user.nomeCompleto }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">{{ user.email }}</p>
                <div v-if="user.roleWorkspace" class="mt-1">
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200">
                    {{ user.roleWorkspace }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer com contador e botões -->
        <div class="flex justify-between items-center p-4 border-t border-gray-200 dark:border-gray-700">
          <div class="text-sm text-gray-600 dark:text-gray-400">
            <span v-if="selectedUsers.length === 0" class="text-gray-500">{{ t('kanban.taskDetail.assignModal.noneSelected') }}</span>
            <span v-else-if="selectedUsers.length === 1" class="text-blue-600 dark:text-blue-400">{{ t('kanban.taskDetail.assignModal.oneSelected') }}</span>
            <span v-else class="text-blue-600 dark:text-blue-400">{{ t('kanban.taskDetail.assignModal.manySelected', { count: selectedUsers.length }) }}</span>
          </div>
          <div class="flex space-x-2">
            <button
              @click="showUserSelectorForCategory = false; currentEditingCategory = null; selectedUsers = []"
              class="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              {{ t('kanban.taskDetail.footer.cancel') }}
            </button>
            <button
              @click="saveSelectedUsersForCategory"
              class="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              {{ t('kanban.taskDetail.assignModal.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- Modal do Calendário para Data Prevista da Categoria -->
    <Teleport to="body">
    <div v-if="showDatePickerForCategory" class="fixed inset-0 z-[12050] flex items-center justify-center bg-black bg-opacity-50" @click.stop @keydown.escape.window="showDatePickerForCategory = false">
      <div class="modal-surface bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh]" @click.stop>
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ t('kanban.taskDetail.assignModal.dateModalCategoryTitle') }}</h3>
          <button @click="showDatePickerForCategory = false" class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Calendário -->
        <div class="p-6">
          <div class="space-y-6">
            <!-- Calendário bonito -->
            <div class="relative">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                {{ t('kanban.taskDetail.assignModal.expectedDate') }}
              </label>
              <div class="relative">
              <VueDatePicker
                v-model="selectedDate"
                :enable-time-picker="false"
                :format="'dd/MM/yyyy'"
                :locale="datePickerLocale"
                :placeholder="t('kanban.taskDetail.assignModal.selectDate')"
                :clearable="true"
                :auto-apply="true"
                :preview-format="'dd/MM/yyyy'"
                class="custom-datepicker w-full"
              />
              </div>
            </div>
            
            <!-- Opções rápidas -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                {{ t('kanban.taskDetail.assignModal.quickOptions') }}
              </label>
              <div class="grid grid-cols-2 gap-3">
                <button
                  @click="selectedDate = new Date()"
                  class="quick-option-btn inline-flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M12 3v2m0 14v2m9-9h-2M5 12H3m15.364 6.364l-1.414-1.414M7.05 7.05 5.636 5.636m12.728 0-1.414 1.414M7.05 16.95l-1.414 1.414M12 8a4 4 0 100 8 4 4 0 000-8z"/>
                  </svg>
                  <span>{{ t('kanban.taskDetail.assignModal.today') }}</span>
                </button>
                <button
                  @click="selectedDate = new Date(Date.now() + 24 * 60 * 60 * 1000)"
                  class="quick-option-btn inline-flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                  </svg>
                  <span>{{ t('kanban.taskDetail.assignModal.tomorrow') }}</span>
                </button>
                <button
                  @click="selectedDate = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)"
                  class="quick-option-btn inline-flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M12 8v4l3 3M12 22a10 10 0 110-20 10 10 0 010 20z"/>
                  </svg>
                  <span>{{ t('kanban.taskDetail.assignModal.in2Days') }}</span>
                </button>
                <button
                  @click="selectedDate = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)"
                  class="quick-option-btn inline-flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M12 8v5l2.5 1.5M12 22a10 10 0 110-20 10 10 0 010 20z"/>
                  </svg>
                  <span>{{ t('kanban.taskDetail.assignModal.in3Days') }}</span>
                </button>
                <button
                  @click="selectedDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)"
                  class="quick-option-btn inline-flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M8 7V3m8 4V3M5 11h14M5 19h14M5 7h14M7 11h.01M7 19h.01"/>
                  </svg>
                  <span>{{ t('kanban.taskDetail.assignModal.in1Week') }}</span>
                </button>
                <button
                  @click="selectedDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)"
                  class="quick-option-btn inline-flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M8 7V3m8 4V3M5 11h14M5 19h14M5 7h14M12 11h.01M12 19h.01"/>
                  </svg>
                  <span>{{ t('kanban.taskDetail.assignModal.in2Weeks') }}</span>
                </button>
                <button
                  @click="selectedDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)"
                  class="quick-option-btn inline-flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M8 7V3m8 4V3M5 11h14M5 19h14M5 7h14"/>
                  </svg>
                  <span>{{ t('kanban.taskDetail.assignModal.in1Month') }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex justify-between items-center p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            @click="removeDateForCategory(currentDateCategory.categoryId); showDatePickerForCategory = false"
            class="px-4 py-2 text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 transition-colors inline-flex items-center gap-2 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 rounded-lg"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m2 0H7m2 0V5a2 2 0 012-2h2a2 2 0 012 2v2"></path>
            </svg>
            {{ t('kanban.taskDetail.assignModal.removeDate') }}
          </button>
          <div class="flex space-x-2">
            <button
              @click="showDatePickerForCategory = false"
              class="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
              {{ t('kanban.taskDetail.footer.cancel') }}
            </button>
            <button
              @click="saveDate"
              class="px-4 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors inline-flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              {{ t('kanban.taskDetail.assignModal.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    </Teleport>
  </div>

  <!-- Modal de Seleção de Usuários Principal -->
  <UserSelectorModal 
    :isOpen="showUserSelector"
    :selectedUsers="selectedUserIds"
    :availableUsers="workspaceUsers"
    @close="showUserSelector = false"
    @confirm="confirmUserSelection"
  />

  <!-- Schedule Modal -->
  <ScheduleModal 
    :is-open="showScheduleModal" 
    :task="props.task"
    :motivos-agendamento="workspaceMotivosAgendamento"
    @close="fecharModalAgendamento" 
    @scheduled="onTaskScheduled" 
  />

  <!-- Menu de Contexto para Itens do Checklist -->
  <div 
    v-if="showContextMenu" 
    class="fixed inset-0 z-[10060]" 
    @click="closeContextMenu"
  >
    <div 
      class="modal-surface absolute bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 min-w-96 max-w-[28rem] w-96 overflow-hidden"
      :style="contextMenuStyle"
      @click.stop
    >
      <!-- Header do Menu -->
      <div class="p-3 border-b border-gray-200 dark:border-gray-700 bg-[var(--bg-secondary)] rounded-t-xl">
        <div class="flex items-center space-x-2">
          <div class="w-2 h-2 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500"></div>
          <h3 class="text-sm font-semibold text-gray-900 dark:text-white">{{ contextMenuItem?.text }}</h3>
          <!-- <div class="ml-auto px-2 py-0.5 bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wide">
            Premium
          </div> -->
        </div>
        <!-- <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ contextMenuItem?.text }}</p> -->
      </div>

      <!-- Seção de Cores -->
      <div class="p-3 border-b border-gray-200 dark:border-gray-700">
        <h4 class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{{ t('kanban.taskDetail.contextMenu.backgroundColor') }}</h4>
        <div class="grid grid-cols-10 gap-1.5">
          <button
            v-for="color in availableColors"
            :key="color.value || 'default'"
            @click="setItemBackgroundColor(color.value)"
            :class="[
              color.class,
              'w-6 h-6 rounded-lg border-2 transition-all duration-200 hover:scale-125',
              contextMenuItem?.backgroundColor === color.value ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-300 dark:border-gray-600'
            ]"
            :title="color.name"
          >
            <div v-if="!color.value" class="w-full h-full rounded-lg flex items-center justify-center">
              <svg class="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4"></path>
              </svg>
            </div>
          </button>
        </div>
      </div>

      <!-- Seção de Tags -->
      <div v-if="workspaceTags.length > 0" class="p-3">
        <h4 class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{{ t('kanban.taskDetail.contextMenu.tags') }}</h4>
        <div class="space-y-2 max-h-40 overflow-y-auto">
          <!-- Agrupar tags por categoria -->
          <div v-for="categoria in groupedTags" :key="categoria.nome" class="space-y-1">
            <h5 class="text-[10px] font-medium text-gray-500 dark:text-gray-400 px-1 uppercase tracking-wide">
              {{ categoria.nome }}
            </h5>
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="tag in categoria.tags"
                :key="tag.id"
                @click="toggleItemTag(tag)"
                :class="[
                  'px-2 py-1 rounded text-xs font-medium transition-all duration-200 hover:scale-110 flex items-center space-x-1.5 border relative',
                  isTagSelected(tag) 
                    ? 'shadow-md border-transparent' 
                    : 'bg-white dark:bg-gray-600 hover:bg-gray-100 dark:hover:bg-gray-500 border-gray-200 dark:border-gray-500'
                ]"
                :style="isTagSelected(tag) ? { 
                  backgroundColor: tag.cor,
                  color: getContrastColor(tag.cor)
                } : {}"
              >
                <!-- Emoji da tag -->
                <span class="text-sm">{{ getTagIcon(tag.icone) }}</span>
                <!-- Nome da tag -->
                <span>{{ tag.nome }}</span>
                <!-- Checkmark quando selecionada -->
                <div v-if="isTagSelected(tag)" class="ml-1">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Mensagem se não há tags -->
      <div v-else class="p-3">
        <p class="text-xs text-gray-500 dark:text-gray-400 text-center">
          {{ t('kanban.taskDetail.sections.tags.noTagsAvailable') }}
        </p>
      </div>

    </div>
  </div>

  <!-- Modal Discord Daily -->
  <Teleport to="body">
    <div v-if="showDiscordDaily" class="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60" @click="showDiscordDaily = false">
      <div class="bg-[#36393f] rounded-xl shadow-2xl w-[600px] max-w-[95vw] max-h-[85vh] overflow-hidden" @click.stop>
        <div class="flex items-center justify-between px-4 py-3 border-b border-[#2f3136]">
          <div class="flex items-center space-x-2">
            <img src="/discord-icon.svg" alt="Discord" class="w-5 h-5" />
            <span class="text-white font-semibold text-sm">{{ t('kanban.taskDetail.discord.dailyTitle') }}</span>
          </div>
          <div class="flex items-center space-x-2">
            <button 
              @click="copyDiscordMessage" 
              class="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200"
              :class="discordCopiado 
                ? 'bg-green-600 text-white' 
                : 'bg-[#5865F2] hover:bg-[#4752C4] text-white'"
            >
              <svg v-if="!discordCopiado" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path>
              </svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              <span>{{ discordCopiado ? 'Copiado!' : 'Copiar' }}</span>
            </button>
            <button @click="showDiscordDaily = false" class="p-1.5 text-gray-400 hover:text-white hover:bg-[#2f3136] rounded-lg transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
        <div class="p-4 overflow-y-auto max-h-[calc(85vh-56px)]">
          <pre class="text-[#dcddde] text-sm font-mono whitespace-pre-wrap break-words leading-relaxed">{{ discordDailyMessage }}</pre>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* RESET TOTAL DO DATEPICKER - versão mínima */
:deep(.dp__menu){ z-index:99999; }
:deep(.dp__menu_inner){ background: var(--bg-modal); color: var(--text-primary); border:1px solid var(--border-primary); border-radius:8px; }
:deep(.dp__calendar), :deep(.dp__calendar_header){ background: transparent; border: none; }
:deep(.dp__calendar_header){
  display: grid !important;
  grid-template-columns: repeat(7, 1fr) !important;
  gap: 4px !important;
  padding: 6px 12px !important; /* diminui top/bottom */
}
:deep(.dp__calendar_header_item){
  font-size:12px; font-weight:600; color: var(--text-secondary);
  display:flex !important; align-items:center !important; justify-content:center !important;
  padding:0 !important; margin:0 !important;
}
:deep(.dp__cell_inner){ color: var(--text-primary); }
body.tema_clara :deep(.dp__btn), body.tema_clara :deep(.dp__month_year_select){ color:#111827 !important; }
body.tema_escura :deep(.dp__btn), body.tema_escura :deep(.dp__month_year_select){ color:#e5e7eb !important; }

/* ===== Overrides finais – deixar mês/ano discretos ===== */
:deep(.dp__month_year_select),
:deep(.dp__btn.dp__month_year_select){
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  text-shadow: none !important;
  padding: 2px 6px !important;
  border-radius: 6px !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  line-height: 1.2 !important;
  color: var(--text-primary) !important;
}

/* ===== Versão moderna/profissional ===== */
/* Container com sombra e cantos suaves */
:deep(.dp__menu_inner){
  border-radius:12px !important;
  box-shadow: 0 18px 40px rgba(0,0,0,.18), 0 2px 8px rgba(0,0,0,.08) !important;
  overflow:hidden !important;
}

/* Cabeçalho agora é grid, separador mantido pelo border-bottom acima */

/* Linha mês/ano: botões discretos */
:deep(.dp__btn), :deep(.dp__month_year_select){
  background: transparent !important;
  border: none !important;
  padding:4px 8px !important;
  border-radius:6px !important;
  font-weight:500 !important;
  font-size: 14px !important;
}
body.tema_clara :deep(.dp__btn:hover),
body.tema_clara :deep(.dp__month_year_select:hover){
  background: rgba(0,0,0,.04) !important;
}
body.tema_escura :deep(.dp__btn:hover),
body.tema_escura :deep(.dp__month_year_select:hover){
  background: rgba(255,255,255,.06) !important;
}

/* Grade: células de tamanho igual e bem alinhadas */
:deep(.dp__calendar_wrap){ padding:12px !important; }
:deep(.dp__calendar_row){
  display: grid !important;
  grid-template-columns: repeat(7, 1fr) !important;
  gap: 4px !important;
}
:deep(.dp__cell){ padding:4px !important; width: 100% !important; }
:deep(.dp__cell_inner){
  height:40px !important; width:40px !important;
  border-radius:10px !important;
  display:flex !important; align-items:center !important; justify-content:center !important;
  transition: background .18s ease, transform .18s ease, box-shadow .18s ease !important;
}
body.tema_clara :deep(.dp__cell_inner:hover){
  background: rgba(0,0,0,.06) !important;
}
body.tema_escura :deep(.dp__cell_inner:hover){
  background: rgba(255,255,255,.08) !important;
}

/* Estado "hoje" com anel sutil */
:deep(.dp__cell_inner.dp__today){
  background: transparent !important;
  box-shadow: inset 0 0 0 2px var(--info) !important;
  color: var(--text-primary) !important;
}

/* Selecionado com alto contraste */
:deep(.dp__cell_inner.dp__active_date){
  background: var(--info) !important;
  color:#ffffff !important;
  box-shadow: 0 6px 16px rgba(88,101,242,.28) !important;
  transform: translateY(-1px) !important;
}

/* Rodapé de ações mais limpo */
:deep(.dp__action_buttons){
  padding:12px 16px !important;
  border-top:1px solid var(--border-primary) !important;
}
:deep(.dp__action_button){
  background: var(--info) !important;
  color:#fff !important;
  border:none !important;
  border-radius:8px !important;
  padding:8px 14px !important;
}

body.tema_escura :deep(.dp__calendar) {
  background-color: var(--bg-modal) !important;
  border-color: var(--border-primary) !important;
  box-shadow: 
    0 20px 25px -5px rgba(0, 0, 0, 0.3),
    0 10px 10px -5px rgba(0, 0, 0, 0.1);
}

/* CORRIGIR PARTES BRANCAS NO TEMA ESCURO */
body.tema_escura :deep(.dp__calendar_wrap) {
  background-color: var(--bg-modal) !important;
}

body.tema_escura :deep(.dp__calendar_header) {
  background-color: var(--bg-tertiary) !important;
  border-bottom-color: var(--border-primary) !important;
}

body.tema_escura :deep(.dp__calendar_header_item) {
  color: var(--text-primary) !important;
  background-color: transparent !important;
}

/* TEMA ESCURO - DESIGN ELEGANTE */
body.tema_escura :deep(.dp__calendar) {
  background-color: #1f2937 !important;
  border-color: #374151 !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3) !important;
}

/* TEMA ESCURO - CALENDÁRIO MODERNO */
body.tema_escura :deep(.dp__calendar) {
  background: linear-gradient(145deg, #0f172a 0%, #1e293b 100%) !important;
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.3),
    0 10px 20px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}

body.tema_escura :deep(.dp__calendar_header) {
  background: transparent !important;
  border-bottom: none !important;
  padding: 0 !important;
  margin: 0 !important;
}

body.tema_escura :deep(.dp__calendar_header_item) {
  color: #94a3b8 !important;
  font-weight: 400 !important;
  font-size: 11px !important;
  opacity: 0.8 !important;
}

/* TEMA ESCURO - SETAS E INSTANCE */
body.tema_escura :deep(.dp__arrow_top) {
  border-bottom-color: #f1f5f9 !important;
}

body.tema_escura :deep(.dp__arrow_bottom) {
  border-top-color: #f1f5f9 !important;
}

body.tema_escura :deep(.dp__arrow_left) {
  border-right-color: #f1f5f9 !important;
}

body.tema_escura :deep(.dp__arrow_right) {
  border-left-color: #f1f5f9 !important;
}

body.tema_escura :deep(.dp__instance_calendar) {
  background-color: #1e293b !important;
  border-color: #374151 !important;
}

/* BOTÕES DE SELEÇÃO DE MÊS E ANO - TEMA ESCURO */
body.tema_escura :deep(.dp__month_year_select) {
  background-color: #1e293b !important;
  color: #ffffff !important;
  border: 1px solid #374151 !important;
  border-radius: 6px !important;
  padding: 8px 12px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
}

/* FORÇAR REMOÇÃO DO COLOR VAR */
:deep(.dp__month_year_select) {
  color: #ffffff !important;
}

:deep(.dp__year_select) {
  color: #ffffff !important;
}

/* TEMA CLARO - FORÇA MÁXIMA */
/* remover estilos exagerados tema claro */

/* remover ápices antigos agressivos */

/* TEMA ESCURO - CORES CORRETAS */
body.tema_escura :deep(.dp__month_year_select) {
  color: #f1f5f9 !important;
  background-color: #334155 !important;
  border: 1px solid #475569 !important;
  font-size: 14px !important;
  font-weight: 600 !important;
}

body.tema_escura :deep(.dp__year_select) {
  color: #f1f5f9 !important;
  background-color: #334155 !important;
  border: 1px solid #475569 !important;
  font-size: 14px !important;
  font-weight: 600 !important;
}

body.tema_escura :deep(.dp__month_year_select:hover) {
  background-color: #334155 !important;
  border-color: #475569 !important;
}

body.tema_escura :deep(.dp__year_select) {
  background-color: #1e293b !important;
  border: 1px solid #374151 !important;
  border-radius: 6px !important;
  padding: 8px 12px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
}

body.tema_escura :deep(.dp__year_select:hover) {
  background-color: #334155 !important;
  border-color: #475569 !important;
}

body.tema_escura :deep(.dp__cell_inner) {
  color: #cbd5e1 !important;
  background: linear-gradient(145deg, transparent 0%, rgba(255, 255, 255, 0.02) 100%) !important;
}

body.tema_escura :deep(.dp__cell_inner:hover) {
  background: linear-gradient(145deg, rgba(59, 130, 246, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%) !important;
  color: #ffffff !important;
  border-color: rgba(59, 130, 246, 0.3) !important;
}

body.tema_escura :deep(.dp__cell_inner.dp__active_date) {
  background: linear-gradient(145deg, #3b82f6 0%, #1d4ed8 100%) !important;
  color: white !important;
  box-shadow: 
    0 15px 35px rgba(59, 130, 246, 0.5),
    0 8px 20px rgba(59, 130, 246, 0.3) !important;
}

body.tema_escura :deep(.dp__cell_inner.dp__today) {
  background: linear-gradient(145deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  box-shadow: 
    0 15px 35px rgba(16, 185, 129, 0.5),
    0 8px 20px rgba(16, 185, 129, 0.3) !important;
}

/* Estilo padronizado para motivos (igual às configurações) */
.reason-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-secondary);
  border-radius: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.reason-card:hover {
  border-color: var(--info);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
}

.reason-icon {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  flex-shrink: 0;
}

.reason-content {
  flex: 1;
}

.reason-content h3 {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.2;
}

body.tema_escura :deep(.dp__action_buttons) {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%) !important;
}

body.tema_escura :deep(.dp__action_button) {
  background: linear-gradient(145deg, #3b82f6 0%, #1d4ed8 100%) !important;
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4) !important;
}

body.tema_escura :deep(.dp__action_button:hover) {
  background: linear-gradient(145deg, #1d4ed8 0%, #1e40af 100%) !important;
  box-shadow: 0 10px 30px rgba(59, 130, 246, 0.6) !important;
}

body.tema_escura :deep(.dp__cell) {
  width: calc(100% / 7) !important;
  height: 36px !important;
  display: table-cell !important;
  vertical-align: middle !important;
  text-align: center !important;
  padding: 0 !important;
  margin: 0 !important;
}

body.tema_escura :deep(.dp__cell_inner) {
  color: #f9fafb !important;
  width: 28px !important;
  height: 28px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin: 0 auto !important;
  padding: 0 !important;
  border-radius: 50% !important;
  transition: all 0.2s ease !important;
}

body.tema_escura :deep(.dp__cell_inner:hover) {
  background-color: #374151 !important;
  color: #f9fafb !important;
  transform: scale(1.1) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
}

body.tema_escura :deep(.dp__cell_inner.dp__active_date) {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
  color: white !important;
  transform: scale(1.15) !important;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4) !important;
}

body.tema_escura :deep(.dp__cell_inner.dp__today) {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  transform: scale(1.1) !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4) !important;
}

body.tema_escura :deep(.dp__cell_inner:hover) {
  background-color: var(--bg-hover) !important;
  color: var(--text-primary) !important;
}

body.tema_escura :deep(.dp__cell_inner.dp__active_date) {
  background-color: var(--info) !important;
  color: white !important;
}

body.tema_escura :deep(.dp__cell_inner.dp__today) {
  background-color: var(--success) !important;
  color: white !important;
  border-color: var(--success) !important;
}

body.tema_escura :deep(.dp__action_buttons) {
  background-color: var(--bg-tertiary) !important;
  border-top-color: var(--border-primary) !important;
}

body.tema_escura :deep(.dp__action_button) {
  background-color: var(--info) !important;
  color: white !important;
}

body.tema_escura :deep(.dp__action_button:hover) {
  background-color: #4C63D2 !important;
  color: white !important;
}

body.tema_escura :deep(.dp__calendar_header) {
  background-color: var(--bg-tertiary);
  border-bottom-color: var(--border-primary);
}

body.tema_escura :deep(.dp__calendar_header_item) {
  color: var(--text-primary);
}

body.tema_escura :deep(.dp__cell_inner) {
  color: var(--text-primary);
}

body.tema_escura :deep(.dp__cell_inner:hover) {
  background-color: var(--bg-hover);
}

body.tema_escura :deep(.dp__cell_inner.dp__active_date) {
  background-color: var(--info);
  color: white;
}

body.tema_escura :deep(.dp__cell_inner.dp__today) {
  background-color: var(--success);
  color: white;
  border-color: var(--success);
}

body.tema_escura :deep(.dp__action_buttons) {
  background-color: var(--bg-tertiary);
  border-top-color: var(--border-primary);
}

body.tema_escura :deep(.dp__action_button) {
  background-color: var(--info);
  color: white;
}

body.tema_escura :deep(.dp__action_button:hover) {
  background-color: #4C63D2;
}

/* TEMA ESCURO - ÍCONES */
body.tema_escura :deep(.dp__input_icon) {
  color: var(--icon-secondary);
}

body.tema_escura :deep(.dp__input_icon:hover) {
  color: var(--icon-primary);
}

body.tema_escura :deep(.dp__clear_icon) {
  color: var(--error);
}

body.tema_escura :deep(.dp__clear_icon:hover) {
  color: #DC2626;
}

/* ========================================
   TEMA CLARO - SOBRESCRITA ESPECÍFICA
   ======================================== */

/* TEMA CLARO - INPUT */
body.tema_clara :deep(.dp__input) {
  background-color: var(--bg-secondary);
  border-color: var(--border-primary);
  color: var(--text-primary);
}

body.tema_clara :deep(.dp__input:hover) {
  border-color: var(--border-hover);
  background-color: var(--bg-hover);
}

body.tema_clara :deep(.dp__input:focus) {
  border-color: var(--info);
  box-shadow: 0 0 0 3px rgba(88, 101, 242, 0.1);
}

/* TEMA CLARO - CALENDÁRIO */
body.tema_clara :deep(.dp__calendar) {
  background-color: var(--bg-modal) !important;
  border-color: var(--border-primary) !important;
  box-shadow: 
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* TEMA CLARO - DESIGN ELEGANTE */
body.tema_clara :deep(.dp__calendar) {
  background-color: #ffffff !important;
  border-color: #d1d5db !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
}

body.tema_clara :deep(.dp__calendar_header) {
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%) !important;
  border-bottom-color: #d1d5db !important;
}

body.tema_clara :deep(.dp__calendar_header_item) {
  color: #111827 !important;
}

body.tema_clara :deep(.dp__cell) {
  width: calc(100% / 7) !important;
  height: 36px !important;
  display: table-cell !important;
  vertical-align: middle !important;
  text-align: center !important;
  padding: 0 !important;
  margin: 0 !important;
}

body.tema_clara :deep(.dp__cell_inner) {
  color: #111827 !important;
  width: 28px !important;
  height: 28px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin: 0 auto !important;
  padding: 0 !important;
  border-radius: 50% !important;
  transition: all 0.2s ease !important;
}

body.tema_clara :deep(.dp__cell_inner:hover) {
  background-color: #f3f4f6 !important;
  color: #111827 !important;
  transform: scale(1.1) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

body.tema_clara :deep(.dp__cell_inner.dp__active_date) {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
  color: white !important;
  transform: scale(1.15) !important;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4) !important;
}

body.tema_clara :deep(.dp__cell_inner.dp__today) {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  transform: scale(1.1) !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4) !important;
}

body.tema_clara :deep(.dp__calendar_header) {
  background-color: var(--bg-tertiary);
  border-bottom-color: var(--border-primary);
}

body.tema_clara :deep(.dp__calendar_header_item) {
  color: var(--text-primary);
}

body.tema_clara :deep(.dp__cell_inner) {
  color: var(--text-primary);
}

body.tema_clara :deep(.dp__cell_inner:hover) {
  background-color: var(--bg-hover);
}

body.tema_clara :deep(.dp__cell_inner.dp__active_date) {
  background-color: var(--info);
  color: white;
}

body.tema_clara :deep(.dp__cell_inner.dp__today) {
  background-color: var(--success);
  color: white;
  border-color: var(--success);
}

body.tema_clara :deep(.dp__action_buttons) {
  background-color: var(--bg-tertiary);
  border-top-color: var(--border-primary);
}

body.tema_clara :deep(.dp__action_button) {
  background-color: var(--info);
  color: white;
}

body.tema_clara :deep(.dp__action_button:hover) {
  background-color: #4C63D2;
}

/* TEMA CLARO - ÍCONES */
body.tema_clara :deep(.dp__input_icon) {
  color: var(--icon-secondary);
}

body.tema_clara :deep(.dp__input_icon:hover) {
  color: var(--icon-primary);
}

body.tema_clara :deep(.dp__clear_icon) {
  color: var(--error);
}

body.tema_clara :deep(.dp__clear_icon:hover) {
  color: #DC2626;
}

/* Efeitos de glassmorphism */
:deep(.dp__calendar) {
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

/* Hover effects premium */
:deep(.dp__cell_inner) {
  position: relative;
  overflow: hidden;
}

:deep(.dp__cell_inner::after) {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: radial-gradient(circle, rgba(34, 197, 94, 0.3) 0%, transparent 70%);
  transition: all 0.3s ease;
  transform: translate(-50%, -50%);
  border-radius: 50%;
}

:deep(.dp__cell_inner:hover::after) {
  width: 100%;
  height: 100%;
}
</style>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useWorkspaceStore } from '@/stores/workspace'
import { useAuthStore } from '@/stores/auth'
import { useLocaleStore } from '@/stores/locale'
import { datePickerLocaleFor } from '@/utils/datePickerLocale'
import api from '@/services/api'
import Swal from 'sweetalert2'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import UserSelectorModal from './UserSelectorModal.vue'
import AnexosSection from './AnexosSection.vue'
import TaskChatPanel from './TaskChatPanel.vue'
import ScheduleModal from './ScheduleModal.vue'
import ColorSelector from './ColorSelector.vue'
import Draggable from 'vuedraggable'
import TaskDetailPlanSection from './TaskDetailPlanSection.vue'
import MoneyInputPrefix from './MoneyInputPrefix.vue'
import { formatMoneyAmount } from '@/utils/format'
import { usePlanSectionGate } from '@/composables/usePlanSectionGate'
import { TASK_SECTION_PLAN_KEYS } from '@/constants/modalPlanSections'

const { planBlocked, planCan, loadPlanForWorkspace } = usePlanSectionGate(TASK_SECTION_PLAN_KEYS)

const PLAN_SECTION_I18N = {
  informacoesTask: 'upgrade.features.taskInfo',
  checklist: 'upgrade.features.taskChecklist',
  anexos: 'upgrade.features.taskAttachments',
  cor: 'upgrade.features.taskColor',
  financeiro: 'upgrade.features.taskFinance',
  progressoTarefa: 'upgrade.features.taskProcess',
  tags: 'upgrade.features.taskTags',
  usuariosAtribuidos: 'upgrade.features.taskAssignedUsers',
  controlesAtividade: 'upgrade.features.taskActivity',
  agendamento: 'upgrade.features.taskScheduling',
  historicoChat: 'upgrade.features.taskChatHistory'
}

const planGateTitle = (sectionKey) => {
  const key = PLAN_SECTION_I18N[sectionKey]
  const label = key ? t(key) : t('planGate.blockedTitle')
  return `${label} — ${t('kanban.taskDetail.planGateSuffix')}`
}

const props = defineProps({
  isOpen: Boolean,
  task: Object
})

const emit = defineEmits(['close', 'save', 'taskUpdated'])

// Stores
const { t, locale } = useI18n()
const localeStore = useLocaleStore()
const datePickerLocale = computed(() => datePickerLocaleFor(localeStore.locale))
const dateLocale = computed(() => {
  if (locale.value === 'en') return 'en-US'
  if (locale.value === 'es') return 'es'
  return 'pt-BR'
})

const workspaceStore = useWorkspaceStore()
const authStore = useAuthStore()

// Registrar o componente VueDatePicker
const components = {
  VueDatePicker,
  ScheduleModal,
  ColorSelector
}

// Flag para indicar que estamos removendo agendamento (evita race condition com watchers/WebSocket)
const removendoAgendamento = ref(false)

// Form data
const formData = ref({
  titulo: '',
  descricao: '',
  checklist: [],
  usuariosAtribuidos: [],
  agendamento: null,
  anexos: [],
  cor: '#3B82F6',
  recebiveis: [],
  progressoTarefaPercentual: 0,
  prazoTarefa: ''
})

const anexosReloadSignal = ref(0)

// Checklist management
const newCategoryName = ref('')
const newItemText = ref({})
const itemInputRefs = ref({})
const minimizedCategories = ref({})
const editingItems = ref({})
const editingText = ref({})
const showUserSelectorForItem = ref(false)
const currentEditingItem = ref(null)
const selectedUsers = ref([])
const workspaceUsers = ref([])
const showDatePicker = ref(false)
const currentDateItem = ref(null)
const selectedDate = ref('')
const getChecklistVisibilityKey = () => 'task-checklist-show-all'
const loadChecklistVisibility = () => {
  try {
    const saved = localStorage.getItem(getChecklistVisibilityKey())
    if (saved === null) return true
    return saved === 'true'
  } catch (e) {
    return true
  }
}
const showAllChecklistItems = ref(loadChecklistVisibility())

// Variáveis para categorias
const showUserSelectorForCategory = ref(false)
const currentEditingCategory = ref(null)
const showDatePickerForCategory = ref(false)
const currentDateCategory = ref(null)

// Variáveis para menu de contexto dos itens
const showContextMenu = ref(false)
const contextMenuPosition = ref({ x: 0, y: 0 })
const contextMenuItem = ref(null)
const contextMenuCategory = ref(null)
const workspaceTags = ref([])
const showRecebivelModal = ref(false)
const recebivelEmEdicaoId = ref(null)
const recebivelForm = ref({
  valor: null,
  dataVencimento: ''
})
const recebivelValorDisplay = ref('')

const clampPercent = (value) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return 0
  return Math.min(100, Math.max(0, parsed))
}

const getProgressPreviewStyle = (value) => {
  const pct = clampPercent(value)
  // Mantém exatamente o mesmo padrão do TaskCard:
  // < 50 = vermelho/laranja | 50-79 = amarelo | >= 80 = verde
  let background = 'linear-gradient(90deg, #ef4444, #f97316)'
  if (pct >= 80) {
    background = 'linear-gradient(90deg, #22c55e, #10b981)'
  } else if (pct >= 50) {
    background = 'linear-gradient(90deg, #f59e0b, #eab308)'
  }

  return {
    width: `${pct}%`,
    background
  }
}

const parseDateOnlyValue = (dateInput) => {
  if (!dateInput) return new Date('invalid')
  if (Array.isArray(dateInput) && dateInput.length >= 3) {
    const [ano, mes, dia] = dateInput
    return new Date(Number(ano), Number(mes) - 1, Number(dia))
  }
  if (typeof dateInput === 'string') {
    const normalized = String(dateInput).trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
      return new Date(`${normalized}T00:00:00`)
    }
    if (normalized.includes('T')) {
      return new Date(`${normalized.split('T')[0]}T00:00:00`)
    }
    const brMatch = normalized.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if (brMatch) {
      const [, dia, mes, ano] = brMatch
      return new Date(Number(ano), Number(mes) - 1, Number(dia))
    }
  }
  return new Date(dateInput)
}

const formatDateOnly = (dateInput) => {
  if (!dateInput) return '--'
  try {
    const parsed = parseDateOnlyValue(dateInput)
    if (Number.isNaN(parsed.getTime())) return '--'
    return parsed.toLocaleDateString(dateLocale.value)
  } catch {
    return '--'
  }
}

/** Sincroniza prazo da tarefa (string yyyy-MM-dd no formData) com Date no VueDatePicker */
const prazoTarefaDate = computed({
  get() {
    const raw = formData.value.prazoTarefa
    if (raw == null || raw === '') return null
    const d = parseDateOnlyValue(raw)
    if (!d || Number.isNaN(d.getTime())) return null
    return d
  },
  set(val) {
    if (!val) {
      formData.value.prazoTarefa = ''
      return
    }
    const d = val instanceof Date ? val : parseDateOnlyValue(val)
    if (!d || Number.isNaN(d.getTime())) {
      formData.value.prazoTarefa = ''
      return
    }
    formData.value.prazoTarefa = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
})

/** Data de vencimento do recebível no modal (string yyyy-MM-dd ↔ Date), mesmo UX do prazo da task */
const recebivelDataVencimentoDate = computed({
  get() {
    const raw = recebivelForm.value.dataVencimento
    if (raw == null || raw === '') return null
    const d = parseDateOnlyValue(raw)
    if (!d || Number.isNaN(d.getTime())) return null
    return d
  },
  set(val) {
    if (!val) {
      recebivelForm.value.dataVencimento = ''
      return
    }
    const d = val instanceof Date ? val : parseDateOnlyValue(val)
    if (!d || Number.isNaN(d.getTime())) {
      recebivelForm.value.dataVencimento = ''
      return
    }
    recebivelForm.value.dataVencimento = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
})

const recebiveisOrdenados = computed(() => {
  const lista = Array.isArray(formData.value.recebiveis) ? [...formData.value.recebiveis] : []
  return lista.sort((a, b) => {
    const da = parseDateOnlyValue(a?.dataVencimento)
    const db = parseDateOnlyValue(b?.dataVencimento)
    const ta = Number.isNaN(da.getTime()) ? Number.MAX_SAFE_INTEGER : da.getTime()
    const tb = Number.isNaN(db.getTime()) ? Number.MAX_SAFE_INTEGER : db.getTime()
    return ta - tb
  })
})

const recebivelStatusClass = (item) => {
  if (item?.pago) return 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-700'
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  const venc = parseDateOnlyValue(item?.dataVencimento)
  if (Number.isNaN(venc.getTime())) return 'border-gray-300 bg-gray-50 dark:bg-gray-700/40 dark:border-gray-600'
  const diffDias = Math.ceil((venc.getTime() - hoje.getTime()) / 86400000)
  if (diffDias < 0) return 'border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700'
  if (diffDias <= 5) return 'border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-700'
  return 'border-gray-300 bg-gray-50 dark:bg-gray-700/40 dark:border-gray-600'
}

const recebivelStatusLabel = (item) => {
  if (item?.pago) return t('kanban.taskDetail.sections.finance.statusPaid')
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  const venc = parseDateOnlyValue(item?.dataVencimento)
  if (Number.isNaN(venc.getTime())) return t('kanban.taskDetail.sections.finance.statusOpen')
  const diffDias = Math.ceil((venc.getTime() - hoje.getTime()) / 86400000)
  if (diffDias < 0) return t('kanban.taskDetail.sections.finance.statusOverdue')
  if (diffDias <= 5) return t('kanban.taskDetail.sections.finance.statusDueIn', { days: diffDias })
  return t('kanban.taskDetail.sections.finance.statusOnTime')
}

const recebivelBadgeClass = (item) => {
  if (item?.pago) return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700'
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  const venc = parseDateOnlyValue(item?.dataVencimento)
  if (Number.isNaN(venc.getTime())) return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-900/30 dark:text-slate-300 dark:border-slate-700'
  const diffDias = Math.ceil((venc.getTime() - hoje.getTime()) / 86400000)
  if (diffDias < 0) return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
  if (diffDias <= 5) return 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700'
  return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700'
}

const formatCurrencyToMask = (value) => {
  const numberValue = Number(value || 0)
  if (!Number.isFinite(numberValue) || numberValue <= 0) return ''
  return formatMoneyAmount(numberValue, locale.value)
}

const parseCurrencyMask = (rawValue) => {
  const digits = String(rawValue || '').replace(/\D/g, '')
  if (!digits) return 0
  return Number(digits) / 100
}

// Funções para motivos de agendamento
const getMotivoIcon = (motivo) => {
  console.log('🔍 TaskDetailModal getMotivoIcon - motivo recebido:', motivo)
  console.log('🔍 TaskDetailModal getMotivoIcon - tipo:', typeof motivo)
  
  if (!motivo) return '📋'
  
  // Primeiro, tentar encontrar o motivo nas configurações do workspace
  const motivoConfig = workspaceMotivosAgendamento.value.find(m => 
    m.motivo === motivo || m.nome === motivo
  )
  
  if (motivoConfig && motivoConfig.icone) {
    console.log('🔍 TaskDetailModal getMotivoIcon - motivo encontrado nas configurações:', motivoConfig)
    const icon = getTagIcon(motivoConfig.icone)
    console.log('🔍 TaskDetailModal getMotivoIcon - icon das configurações:', icon)
    return icon || '📋'
  }
  
  // Se não encontrar nas configurações, tentar usar o iconMapper diretamente
  const icon = getTagIcon(motivo)
  console.log('🔍 TaskDetailModal getMotivoIcon - icon retornado pelo mapper:', icon)
  
  return icon || motivo || '📋'
}

const getMotivoCor = (motivo) => {
  if (!motivo) return '#3B82F6'
  
  // Buscar o motivo nas configurações do workspace
  const motivoConfig = workspaceMotivosAgendamento.value.find(m => m.motivo === motivo)
  if (motivoConfig && motivoConfig.cor) {
    return motivoConfig.cor
  }
  
  // Cores padrão baseadas no tipo de motivo
  const colorMap = {
    'phone': '#3B82F6',
    'presentation': '#10B981',
    'handshake': '#F59E0B',
    'done': '#10B981',
    'important': '#EF4444',
    'low': '#6B7280',
    'high': '#EF4444',
    'project': '#8B5CF6',
    'migration': '#06B6D4',
    'approval': '#10B981',
    'web': '#3B82F6',
    'linkedin': '#0077B5',
    'google': '#4285F4',
    'calendar': '#3B82F6',
    'clock': '#6B7280',
    'check': '#10B981',
    'star': '#F59E0B',
    'heart': '#EF4444',
    'fire': '#F97316',
    'rocket': '#8B5CF6'
  }
  
  return colorMap[motivo.toLowerCase()] || '#3B82F6'
}

const toggleChecklistVisibility = () => {
  showAllChecklistItems.value = !showAllChecklistItems.value
  try {
    localStorage.setItem(getChecklistVisibilityKey(), showAllChecklistItems.value.toString())
  } catch (e) {
    console.error('Erro ao salvar preferência do checklist:', e)
  }
}

const getVisibleChecklistItems = (category) => {
  const items = category?.items || []
  if (showAllChecklistItems.value) {
    return items
  }
  return items.filter(item => !item.completed)
}

// Cores disponíveis para os itens do checklist
const availableColors = [
  { name: 'Padrão', class: 'bg-gray-100 dark:bg-gray-700', value: null },
  { name: 'Azul', class: 'bg-blue-100 dark:bg-blue-900', value: 'blue' },
  { name: 'Verde', class: 'bg-green-100 dark:bg-green-900', value: 'green' },
  { name: 'Amarelo', class: 'bg-yellow-100 dark:bg-yellow-900', value: 'yellow' },
  { name: 'Vermelho', class: 'bg-red-100 dark:bg-red-900', value: 'red' },
  { name: 'Roxo', class: 'bg-purple-100 dark:bg-purple-900', value: 'purple' },
  { name: 'Rosa', class: 'bg-pink-100 dark:bg-pink-900', value: 'pink' },
  { name: 'Laranja', class: 'bg-orange-100 dark:bg-orange-900', value: 'orange' },
  { name: 'Ciano', class: 'bg-cyan-100 dark:bg-cyan-900', value: 'cyan' },
  { name: 'Índigo', class: 'bg-indigo-100 dark:bg-indigo-900', value: 'indigo' }
]

// Computed properties
const contextMenuStyle = computed(() => {
  if (!showContextMenu.value) return {}
  
  return {
    left: Math.min(contextMenuPosition.value.x, window.innerWidth - 448) + 'px', // 448px = 28rem
    top: contextMenuPosition.value.y + 'px'
  }
})

const groupedTags = computed(() => {
  const groups = {}
  
  // Extrair todas as tags das categorias
  workspaceTags.value.forEach(categoria => {
    if (categoria.tags) {
      categoria.tags.forEach(tag => {
        const categoriaNome = categoria.nome || 'Sem categoria'
        
        if (!groups[categoriaNome]) {
          groups[categoriaNome] = {
            nome: categoriaNome,
            tags: []
          }
        }
        
        groups[categoriaNome].tags.push(tag)
      })
    }
  })
  
  return Object.values(groups)
})

// Activity controls
const isActive = ref(false)
const loadingAtividade = ref(false)
const atividadesLog = ref([])
const atividadesAtivas = ref([])
const processandoAtividade = ref(false) // Flag para prevenir cliques duplos

// Schedule modal variables
const showScheduleModal = ref(false)

// Section visibility controls - carregar do localStorage
const loadSectionsVisibleState = () => {
  try {
    const saved = localStorage.getItem('taskModal_sectionsVisible')
    if (saved) {
      const parsed = JSON.parse(saved)
      return {
        cor: parsed.cor !== false,
        tags: parsed.tags !== false,
        usuariosAtribuidos: parsed.usuariosAtribuidos !== false,
        controlesAtividade: parsed.controlesAtividade !== false,
        agendamento: parsed.agendamento !== false,
        financeiro: parsed.financeiro !== false,
        progressoTarefa: parsed.progressoTarefa !== false
      }
    }
  } catch (e) {
    console.error('Erro ao carregar estado das seções do sidebar:', e)
  }
  return {
    cor: true,
    tags: true,
    usuariosAtribuidos: true,
    controlesAtividade: true,
    agendamento: true,
    financeiro: true,
    progressoTarefa: true
  }
}

const sectionsVisible = ref(loadSectionsVisibleState())

// Salvar estado das seções do sidebar no localStorage
const saveSectionsVisibleState = () => {
  try {
    localStorage.setItem('taskModal_sectionsVisible', JSON.stringify(sectionsVisible.value))
  } catch (e) {
    console.error('Erro ao salvar estado das seções do sidebar:', e)
  }
}

// Computed para verificar se usuário atual está ativo
const usuarioAtivo = computed(() => {
  if (!authStore.user?.id) {
    console.log('🔍 usuarioAtivo: false (sem authStore.user.id)')
    return false
  }
  const temAtividade = atividadesAtivas.value.some(atividade => atividade.usuarioId === authStore.user.id)
  console.log('🔍 usuarioAtivo:', temAtividade, 'atividadesAtivas:', atividadesAtivas.value.length, 'userId:', authStore.user.id)
  return temAtividade
})

// Estado de salvamento automático
const salvando = ref(false)
const salvo = ref(false)
const timeoutSalvamento = ref(null)
const timeoutIndicadorSalvo = ref(null)

// Estado de salvamento automático das tags
const salvandoTags = ref(false)
const salvoTags = ref(false)
const timeoutSalvamentoTags = ref(null)
const timeoutIndicadorSalvoTags = ref(null)

// Estado de salvamento automático da cor
const salvandoCor = ref(false)
const salvoCor = ref(false)
const timeoutSalvamentoCor = ref(null)
const timeoutIndicadorSalvoCor = ref(null)

// Estado de salvamento automático do checklist
const salvandoChecklist = ref(false)
const salvoChecklist = ref(false)
const timeoutSalvamentoChecklist = ref(null)
/** Fila para não perder salvamentos quando o utilizador clica várias tags enquanto o PUT anterior ainda corre */
let checklistSaveChain = Promise.resolve()
const checklistInicializado = ref(false)
const timeoutIndicadorSalvoChecklist = ref(null)
const atualizandoDoServidor = ref(false) // Flag para evitar loop infinito
const fotosCarregando = ref(false) // Flag para controlar loading das fotos

onMounted(() => {
  updateMobileViewport()
  window.addEventListener('resize', updateMobileViewport)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateMobileViewport)
  if (timeoutSalvamento.value) clearTimeout(timeoutSalvamento.value)
  if (timeoutIndicadorSalvo.value) clearTimeout(timeoutIndicadorSalvo.value)
  if (timeoutSalvamentoTags.value) clearTimeout(timeoutSalvamentoTags.value)
  if (timeoutIndicadorSalvoTags.value) clearTimeout(timeoutIndicadorSalvoTags.value)
  if (timeoutSalvamentoCor.value) clearTimeout(timeoutSalvamentoCor.value)
  if (timeoutIndicadorSalvoCor.value) clearTimeout(timeoutIndicadorSalvoCor.value)
  if (timeoutSalvamentoChecklist.value) clearTimeout(timeoutSalvamentoChecklist.value)
  if (timeoutIndicadorSalvoChecklist.value) clearTimeout(timeoutIndicadorSalvoChecklist.value)
  if (customFieldsSaveTimeout.value) clearTimeout(customFieldsSaveTimeout.value)
})

// Minimized cards state - carregar do localStorage
const loadMinimizedCardsState = () => {
  const defaults = {
    informacoesTask: false,
    checklist: false,
    anexos: false
  }
  try {
    const saved = localStorage.getItem('taskModal_minimizedCards')
    if (saved) {
      const parsed = JSON.parse(saved)
      return { ...defaults, ...(parsed && typeof parsed === 'object' ? parsed : {}) }
    }
  } catch (e) {
    console.error('Erro ao carregar estado das seções:', e)
  }
  return defaults
}

const minimizedCards = ref(loadMinimizedCardsState())

// Salvar estado no localStorage
const saveMinimizedCardsState = () => {
  try {
    localStorage.setItem('taskModal_minimizedCards', JSON.stringify(minimizedCards.value))
  } catch (e) {
    console.error('Erro ao salvar estado das seções:', e)
  }
}

// Toggle card minimize
const toggleCard = (cardName) => {
  minimizedCards.value[cardName] = !minimizedCards.value[cardName]
  saveMinimizedCardsState() // Salvar automaticamente no localStorage
  if (cardName === 'informacoesTask' && !minimizedCards.value.informacoesTask) {
    scheduleTituloResize()
  }
}

// Toggle category minimize (com persistência no localStorage)
const getCategoryStorageKey = () => {
  const taskId = props.task?.id
  return taskId ? `taskModal_minimizedCategories_${taskId}` : null
}

const loadMinimizedCategoriesState = () => {
  const key = getCategoryStorageKey()
  if (!key) return
  try {
    const saved = localStorage.getItem(key)
    if (saved) {
      const parsed = JSON.parse(saved)
      const currentIds = new Set((formData.value.checklist || []).map(c => String(c.id)))
      const cleaned = {}
      for (const [id, val] of Object.entries(parsed)) {
        if (currentIds.has(String(id))) {
          cleaned[id] = val
        }
      }
      minimizedCategories.value = cleaned
    } else {
      minimizedCategories.value = {}
    }
  } catch {
    minimizedCategories.value = {}
  }
}

const saveMinimizedCategoriesState = () => {
  const key = getCategoryStorageKey()
  if (!key) return
  try {
    localStorage.setItem(key, JSON.stringify(minimizedCategories.value))
  } catch { /* ignore quota errors */ }
}

const toggleCategory = (categoryId) => {
  const key = String(categoryId)
  minimizedCategories.value[key] = !minimizedCategories.value[key]
  saveMinimizedCategoriesState()
}

// Edit item inline
const adjustEditingTextareaHeight = (el) => {
  if (!el) return
  // reset altura para recalcular
  el.style.height = 'auto'
  const computed = window.getComputedStyle(el)
  const lineHeight = parseFloat(computed.lineHeight || '20')
  const maxLines = 8
  const maxHeight = lineHeight * maxLines
  const newHeight = Math.min(el.scrollHeight, maxHeight)
  el.style.height = `${newHeight}px`
  el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
}

const onEditingItemInput = (event) => {
  const el = event?.target
  if (!el) return
  adjustEditingTextareaHeight(el)
}

const autoResizeNewItemTextarea = (event) => {
  const el = event?.target
  if (!el) return
  el.style.height = 'auto'
  el.style.height = Math.min(el.scrollHeight, 120) + 'px'
}

const tituloTextareaRef = ref(null)

const isTituloTextareaVisible = (el) => {
  if (!el?.isConnected) return false
  return el.offsetParent !== null || el.getClientRects().length > 0
}

const scheduleTituloResize = () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      const el = tituloTextareaRef.value
      if (el) autoResizeTitulo(el)
    })
  })
}

const autoResizeTitulo = (el) => {
  if (!el) return
  if (!isTituloTextareaVisible(el)) {
    el.style.height = ''
    el.style.overflowY = ''
    return
  }
  el.style.height = 'auto'
  const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20
  const maxHeight = lineHeight * 5 + 12
  const scrollHeight = el.scrollHeight
  const newHeight = Math.min(Math.max(scrollHeight, lineHeight + 12), maxHeight)
  el.style.height = `${newHeight}px`
  el.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden'
}

watch(
  () => minimizedCards.value.informacoesTask,
  (minimized) => {
    if (!minimized) scheduleTituloResize()
  }
)

// Formatar texto do item: *texto* vira negrito
const formatItemText = (text) => {
  if (!text) return ''
  const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return escaped
    .replace(/\*([^*]+)\*/g, '<b>$1</b>')
    .replace(/\n/g, '<br>')
}

const startEditingItem = async (categoryId, itemId, currentText) => {
  // Salvar qualquer item que esteja sendo editado atualmente
  for (const [existingItemId, isEditing] of Object.entries(editingItems.value)) {
    if (isEditing && existingItemId !== itemId.toString()) {
      // Encontrar a categoria e salvar o item
      for (const category of formData.value.checklist) {
        const item = category.items?.find(i => i.id.toString() === existingItemId)
        if (item && editingText.value[existingItemId]?.trim()) {
          item.text = editingText.value[existingItemId].trim()
        }
      }
      // Limpar o estado de edição
      editingItems.value[existingItemId] = false
      editingText.value[existingItemId] = ''
    }
  }
  
  // Iniciar edição do novo item
  editingItems.value[itemId] = true
  editingText.value[itemId] = currentText
  
  // Focar no textarea após o próximo tick e ajustar altura inicial
  await nextTick()
  const editingInput = document.querySelector(`textarea[data-editing-id="${itemId}"]`)
  if (editingInput) {
    editingInput.focus()
    editingInput.select()
    adjustEditingTextareaHeight(editingInput)
  }
}

const saveItemEdit = (categoryId, itemId) => {
  const category = formData.value.checklist.find(c => c.id === categoryId)
  if (category && category.items) {
    const item = category.items.find(i => i.id === itemId)
    if (item && editingText.value[itemId]?.trim()) {
      item.text = editingText.value[itemId].trim()
    }
  }
  editingItems.value[itemId] = false
  editingText.value[itemId] = ''
  
  salvarChecklistTask(true)
}

const cancelItemEdit = (itemId) => {
  editingItems.value[itemId] = false
  editingText.value[itemId] = ''
}

// User assignment for checklist items
const openUserSelectorForItem = async (categoryId, itemId) => {
  
  currentEditingItem.value = { categoryId, itemId }
  
  // Verificar se temos um workspace ativo
  if (!workspaceStore.currentWorkspace?.id) {
    alert(t('kanban.taskDetail.errors.noWorkspace'))
    return
  }
  
  // Carregar usuários do workspace
  await loadWorkspaceUsers()
  
  // Pre-selecionar usuários já atribuídos
  const category = formData.value.checklist.find(c => c.id === categoryId)
  if (category && category.items) {
    const item = category.items.find(i => i.id === itemId)
    if (item && item.assignedUsers) {
      // Se assignedUsers são objetos, extrair os IDs
      if (typeof item.assignedUsers[0] === 'object' && item.assignedUsers[0].id) {
        selectedUsers.value = item.assignedUsers.map(user => user.id)
      } else {
        // Se assignedUsers já são IDs
        selectedUsers.value = [...item.assignedUsers]
      }
    } else {
      selectedUsers.value = []
    }
  }
  
  showUserSelectorForItem.value = true
}

// User assignment for checklist categories
const openUserSelectorForCategory = async (categoryId) => {
  
  currentEditingCategory.value = { categoryId }
  
  // Verificar se temos um workspace ativo
  if (!workspaceStore.currentWorkspace?.id) {
    alert(t('kanban.taskDetail.errors.noWorkspace'))
    return
  }
  
  // Carregar usuários do workspace
  await loadWorkspaceUsers()
  
  // Pre-selecionar usuários já atribuídos à categoria
  const category = formData.value.checklist.find(c => c.id === categoryId)
  if (category && category.assignedUsers) {
    // Se assignedUsers são objetos, extrair os IDs
    if (typeof category.assignedUsers[0] === 'object' && category.assignedUsers[0].id) {
      selectedUsers.value = category.assignedUsers.map(user => user.id)
    } else {
      // Se assignedUsers já são IDs
      selectedUsers.value = [...category.assignedUsers]
    }
  } else {
    selectedUsers.value = []
  }
  
  showUserSelectorForCategory.value = true
}

// Carregar usuários do workspace
const loadWorkspaceUsers = async () => {
  try {
    // Ativar loading das fotos (mais rápido)
    fotosCarregando.value = true
    
    const token = localStorage.getItem('token')
    if (!token) {
      fotosCarregando.value = false
      return
    }

    const workspaceId = workspaceStore.currentWorkspace?.id
    if (!workspaceId) {
      fotosCarregando.value = false
      return
    }
    
    const response = await api.get(`/workspace-acesso/workspace/${workspaceId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    
    // Mapear dados da API para o formato esperado pelo frontend
    const usuariosApi = response.data || []
    workspaceUsers.value = usuariosApi.map(usuario => ({
      id: usuario.usuarioId,
      nomeCompleto: usuario.usuarioNome,
      email: usuario.usuarioEmail,
      fotoPerfil: usuario.usuarioFotoPerfil,
      roleWorkspace: usuario.roleWorkspace,
      ativo: usuario.ativo
    }))
    
    
    // Aguardar um pouco para mostrar o spinner e depois desativar loading
    setTimeout(() => {
      fotosCarregando.value = false
    }, 800) // 800ms para dar tempo de ver o spinner
    
  } catch (error) {
    
    // Desativar loading mesmo em caso de erro
    fotosCarregando.value = false
    
    // Se for erro de permissão, mostrar mensagem específica
    if (error.response?.status === 403) {
      workspaceUsers.value = []
    } else if (error.response?.status === 401) {
      workspaceUsers.value = []
    } else {
      workspaceUsers.value = []
    }
  }
}

// Toggle user selection
const toggleUserSelection = (userId) => {
  const index = selectedUsers.value.indexOf(userId)
  if (index > -1) {
    selectedUsers.value.splice(index, 1)
  } else {
    selectedUsers.value.push(userId)
  }
}

// Save selected users to item
const saveSelectedUsers = async () => {
  
  // Ativar spinner durante salvamento
  fotosCarregando.value = true
    
  if (currentEditingItem.value) {
    const { categoryId, itemId } = currentEditingItem.value
    const category = formData.value.checklist.find(c => c.id === categoryId)
    if (category && category.items) {
      const item = category.items.find(i => i.id === itemId)
      if (item) {
        // Sempre salvar a seleção, mesmo que seja um array vazio
        // Forçar reatividade criando um novo array
        item.assignedUsers = [...selectedUsers.value]
        
      }
    }
  }
  
  // Aguardar o próximo ciclo de renderização para garantir que o DOM seja atualizado
  await nextTick()
  
  showUserSelectorForItem.value = false
  currentEditingItem.value = null
  selectedUsers.value = []
  
  await salvarChecklistTask(true)
  
  setTimeout(() => {
    fotosCarregando.value = false
  }, 1000) // 1 segundo para dar tempo de ver o spinner
}

// Save selected users to category
const saveSelectedUsersForCategory = async () => {
  // Ativar spinner durante salvamento
  fotosCarregando.value = true
  
  if (currentEditingCategory.value) {
    const { categoryId } = currentEditingCategory.value
    const category = formData.value.checklist.find(c => c.id === categoryId)
    if (category) {
      // Sempre salvar a seleção, mesmo que seja um array vazio
      // Forçar reatividade criando um novo array
      category.assignedUsers = [...selectedUsers.value]
      
    }
  }
  
  // Aguardar o próximo ciclo de renderização para garantir que o DOM seja atualizado
  await nextTick()
  
  showUserSelectorForCategory.value = false
  currentEditingCategory.value = null
  selectedUsers.value = []
  
  await salvarChecklistTask(true)
  
  setTimeout(() => {
    fotosCarregando.value = false
  }, 1000)
}

const removeUserFromItem = async (categoryId, itemId, userId) => {

  // Ativar spinner durante remoção
  fotosCarregando.value = true
  
  const category = formData.value.checklist.find(c => c.id === categoryId)
  if (category && category.items) {
    const item = category.items.find(i => i.id === itemId)
    if (item && item.assignedUsers) {
      // Forçar reatividade criando um novo array
      item.assignedUsers = item.assignedUsers.filter(user => {
        // Verificar se é objeto (com id) ou apenas ID
        const userToRemove = typeof user === 'object' ? user.id : user
        return userToRemove !== userId
      })
    }
  }
  
  await nextTick()
  
  await salvarChecklistTask(true)
  
  setTimeout(() => {
    fotosCarregando.value = false
  }, 800)
}

// Helper functions for user display
const getInitials = (userId) => {
  const user = workspaceUsers.value.find(u => u.id === userId)
  if (user && user.nomeCompleto) {
    const names = user.nomeCompleto.split(' ')
    return names.map(name => name.charAt(0)).join('').substring(0, 2).toUpperCase()
  }
  return userId.toString().substring(0, 2).toUpperCase()
}

const getUserName = (userId) => {
  const user = workspaceUsers.value.find(u => u.id === userId)
  return user ? user.nomeCompleto : `Usuário ${userId}`
}

const getUserPhoto = (userId) => {
  const user = workspaceUsers.value.find(u => u.id === userId)
  return user ? user.fotoPerfil : null
}

// Função para gerar iniciais do usuário
const getUserInitials = (user) => {
  if (!user || !user.nomeCompleto) return '?'
  
  const names = user.nomeCompleto.trim().split(' ')
  // Retornar apenas a primeira letra do primeiro nome
  return names[0].charAt(0).toUpperCase()
}


// Date picker functions
const openDatePicker = (categoryId, itemId) => {
  currentDateItem.value = { categoryId, itemId }
  const category = formData.value.checklist.find(c => c.id === categoryId)
  if (category && category.items) {
    const item = category.items.find(i => i.id === itemId)
    selectedDate.value = item && item.dueDate ? createLocalDate(item.dueDate) : null
  }
  showDatePicker.value = true
}

// Date picker functions for categories
const openDatePickerForCategory = (categoryId) => {
  currentDateCategory.value = { categoryId }
  const category = formData.value.checklist.find(c => c.id === categoryId)
  if (category && category.dataPrevista) {
    selectedDate.value = createLocalDate(category.dataPrevista)
  } else {
    selectedDate.value = null
  }
  showDatePickerForCategory.value = true
}

const saveDate = () => {
  if (currentDateItem.value && selectedDate.value) {
    const { categoryId, itemId } = currentDateItem.value
    const category = formData.value.checklist.find(c => c.id === categoryId)
    if (category && category.items) {
      const item = category.items.find(i => i.id === itemId)
      if (item) {
        item.dueDate = selectedDate.value.toISOString().split('T')[0]
      }
    }
    showDatePicker.value = false
    currentDateItem.value = null
  } else if (currentDateCategory.value && selectedDate.value) {
    const { categoryId } = currentDateCategory.value
    const category = formData.value.checklist.find(c => c.id === categoryId)
    if (category) {
      category.dataPrevista = selectedDate.value.toISOString().split('T')[0]
    }
    showDatePickerForCategory.value = false
    currentDateCategory.value = null
  }
  
  selectedDate.value = null
  
  salvarChecklistTask(true)
}

const removeDate = (categoryId, itemId) => {
  const category = formData.value.checklist.find(c => c.id === categoryId)
  if (category && category.items) {
    const item = category.items.find(i => i.id === itemId)
    if (item) {
      delete item.dueDate
    }
  }
  
  salvarChecklistTask(true)
}

const removeDateForCategory = (categoryId) => {
  const category = formData.value.checklist.find(c => c.id === categoryId)
  if (category) {
    delete category.dataPrevista
  }
  
  salvarChecklistTask(true)
}

// Função auxiliar para criar uma data local corretamente
const createLocalDate = (dateString) => {
  if (!dateString) return null
  
  // Se a data está no formato YYYY-MM-DD (sem timezone), criar a data localmente
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number)
    return new Date(year, month - 1, day) // month é 0-indexed
  }
  
  // Para outros formatos de data, usar o método padrão
  return new Date(dateString)
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  
  const date = createLocalDate(dateString)
  return date.toLocaleDateString(dateLocale.value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

const isOverdue = (dateString) => {
  if (!dateString) return false
  const today = new Date()
  const dueDate = createLocalDate(dateString)
  today.setHours(0, 0, 0, 0)
  dueDate.setHours(0, 0, 0, 0)
  return dueDate < today
}

// Função para determinar o status da data
const getDateStatus = (dateString) => {
  if (!dateString) return 'none'
  
  const today = new Date()
  const dueDate = createLocalDate(dateString)
  today.setHours(0, 0, 0, 0)
  dueDate.setHours(0, 0, 0, 0)
  
  if (dueDate.getTime() === today.getTime()) {
    return 'today' // Hoje
  } else if (dueDate > today) {
    return 'future' // Futuro
  } else {
    return 'overdue' // Vencida
  }
}


// UI state
const showUserSelector = ref(false)
const showScheduler = ref(false)
const selectedUserIds = ref([])

// Função para abrir o modal de seleção de usuários com pré-seleção
const openUserSelector = async () => {
  // Carregar usuários do workspace primeiro
  await loadWorkspaceUsers()
  
  // Pré-selecionar usuários já atribuídos
  selectedUserIds.value = formData.value.usuariosAtribuidos.map(user => {
    const id = typeof user.id === 'string' ? parseInt(user.id) : user.id
    return id
  })

  showUserSelector.value = true
}

// Função para confirmar seleção de usuários
const confirmUserSelection = async (userIds) => {
 
  // Buscar os objetos de usuário completos baseados nos IDs selecionados
  const selectedUsers = workspaceUsers.value.filter(user => userIds.includes(user.id))
 
  // Atualizar a lista de usuários atribuídos
  formData.value.usuariosAtribuidos = selectedUsers

  // Para compatibilidade com o backend atual, também definir o primeiro usuário como usuarioAtribuido
  if (selectedUsers.length > 0) {
    formData.value.usuarioAtribuido = selectedUsers[0]
  } else {
    formData.value.usuarioAtribuido = null
  }
  
  // Salvar automaticamente os usuários atribuídos
  await salvarUsuariosAtribuidos()
  
  showUserSelector.value = false
}

// Função para salvar usuários atribuídos à task principal
const salvarUsuariosAtribuidos = async () => {
  try {
    // Verificar se temos um ID da task
    if (!props.task?.id) {
      return
    }

    const token = localStorage.getItem('token')
    if (!token) {
      console.error('❌ Token não encontrado')
      return
    }

    // Extrair apenas os IDs dos usuários atribuídos
    const userIds = formData.value.usuariosAtribuidos.map(user => {
      return typeof user.id === 'string' ? parseInt(user.id) : user.id
    })

    const response = await api.put(`/tasks/${props.task.id}/usuarios`, {
      userIds: userIds
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    console.log('✅ Usuários atribuídos salvos com sucesso')
    
    // Emitir evento para atualizar a task no componente pai
    emit('taskUpdated', response.data)
    
  } catch (error) {
    console.error('❌ Erro ao salvar usuários atribuídos:', error)
  }
}

// Função para remover usuário da task principal
const removeUser = async (userId) => {
  try {
    // Remover usuário da lista
    formData.value.usuariosAtribuidos = formData.value.usuariosAtribuidos.filter(user => user.id !== userId)
    
    // Atualizar usuarioAtribuido se necessário
    if (formData.value.usuarioAtribuido && formData.value.usuarioAtribuido.id === userId) {
      formData.value.usuarioAtribuido = formData.value.usuariosAtribuidos.length > 0 ? formData.value.usuariosAtribuidos[0] : null
    }
    
    // Salvar automaticamente
    await salvarUsuariosAtribuidos()
    
  } catch (error) {
    console.error('❌ Erro ao remover usuário:', error)
  }
}

// Tags e Motivos de Agendamento
const selectedTags = ref([])
const workspaceMotivosAgendamento = ref([])
const getDefaultSectionEnabled = () => ({
  informacoesTask: true,
  checklist: true,
  anexos: true,
  cor: true,
  financeiro: true,
  progressoTarefa: true,
  tags: true,
  usuariosAtribuidos: true,
  controlesAtividade: true,
  agendamento: true,
  historicoChat: true
})
const sectionEnabled = ref(getDefaultSectionEnabled())
const chatVisibilityResolved = ref(false)
const resolvedHistoricoChatEnabled = computed(() => {
  if (!chatVisibilityResolved.value) return false
  return sectionEnabled.value.historicoChat
})
const layoutCol1El = ref(null)
const layoutCol2El = ref(null)
const showMobileChat = ref(false)
const isMobileViewport = ref(false)
const isTabletViewport = ref(false)
const lastTaskEditFieldsJson = ref('{}')
const taskCustomFormGroups = ref([])
const taskCustomFieldValues = ref({})
const customFieldsSaveTimeout = ref(null)
const taskLayoutColumns = ref({
  col1: ['informacoesTask', 'checklist', 'anexos'],
  col2: ['cor', 'financeiro', 'progressoTarefa', 'tags', 'usuariosAtribuidos', 'controlesAtividade', 'agendamento']
})

const customGroupKey = (groupId) => `customGroup:${groupId}`

const parseCustomFormsConfig = (jsonValue) => {
  if (!jsonValue || typeof jsonValue !== 'string') return []
  try {
    const parsed = JSON.parse(jsonValue)
    const groups = Array.isArray(parsed?.groups)
      ? parsed.groups
      : (Array.isArray(parsed?.forms) ? parsed.forms : [])
    return groups.map((group, index) => ({
      id: group.id || `custom_${index}`,
      nome: group.nome || `Grupo ${index + 1}`,
      campos: Array.isArray(group.campos) ? group.campos : []
    }))
  } catch (error) {
    console.warn('TaskDetailModal: customFormsJson inválido. Ignorando.', error)
    return []
  }
}

const parseCustomFieldValues = (jsonValue) => {
  if (!jsonValue || typeof jsonValue !== 'string') return {}
  try {
    const parsed = JSON.parse(jsonValue)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (error) {
    console.warn('TaskDetailModal: customFieldsJson inválido. Ignorando.', error)
    return {}
  }
}

const parseSelectOptions = (field) => {
  if (Array.isArray(field?.opcoes)) return field.opcoes
  return String(field?.opcoes || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

const getCustomGroupsByColumn = (columnKey) => {
  const columnList = taskLayoutColumns.value[columnKey] || []
  const map = new Map(taskCustomFormGroups.value.map((group) => [customGroupKey(group.id), group]))
  return columnList
    .filter((key) => String(key).startsWith('customGroup:'))
    .map((key) => map.get(key))
    .filter(Boolean)
}

const getCustomFieldValue = (groupId, fieldId, fieldType) => {
  const group = taskCustomFieldValues.value?.[groupId]
  const value = group?.[fieldId]
  if (value !== undefined) return value
  if (fieldType === 'checkbox' || fieldType === 'toggle') return false
  return ''
}

const saveCustomFieldsTask = async () => {
  if (!props.task?.id) return
  try {
    const payload = {
      customFieldsJson: JSON.stringify(taskCustomFieldValues.value || {})
    }
    await api.put(`/tasks/${props.task.id}/custom-fields`, payload)
  } catch (error) {
    console.error('Erro ao salvar campos customizados da task:', error)
  }
}

const scheduleSaveCustomFieldsTask = () => {
  if (customFieldsSaveTimeout.value) {
    clearTimeout(customFieldsSaveTimeout.value)
  }
  customFieldsSaveTimeout.value = setTimeout(() => {
    saveCustomFieldsTask()
  }, 450)
}

const setCustomFieldValue = (groupId, fieldId, value) => {
  if (!taskCustomFieldValues.value[groupId]) {
    taskCustomFieldValues.value[groupId] = {}
  }
  taskCustomFieldValues.value[groupId][fieldId] = value
  scheduleSaveCustomFieldsTask()
}

const applyTaskLayoutDomOrder = () => {
  nextTick(() => {
    const col1 = layoutCol1El.value
    const col2 = layoutCol2El.value
    if (!col1 || !col2) return

    const allKeys = [...taskLayoutColumns.value.col1, ...taskLayoutColumns.value.col2]
    allKeys.forEach((key) => {
      const targetCol = taskLayoutColumns.value.col1.includes(key) ? col1 : col2
      const element = col1.querySelector(`[data-layout-key="${key}"]`) || col2.querySelector(`[data-layout-key="${key}"]`)
      if (!element) return
      targetCol.appendChild(element)
      element.style.display = sectionEnabled.value[key] === false ? 'none' : ''
    })
  })
}

const updateMobileViewport = () => {
  const width = window.innerWidth
  const nextIsMobile = width < 768
  const nextIsTablet = width >= 768 && width < 1024
  const changed = nextIsMobile !== isMobileViewport.value || nextIsTablet !== isTabletViewport.value
  isMobileViewport.value = nextIsMobile
  isTabletViewport.value = nextIsTablet
  if (!nextIsMobile && !nextIsTablet) {
    showMobileChat.value = false
  }
  if (changed && lastTaskEditFieldsJson.value) {
    applyTaskEditLayoutConfig(lastTaskEditFieldsJson.value)
  }
}

const applyTaskEditLayoutConfig = (taskEditFieldsJson) => {
  lastTaskEditFieldsJson.value = typeof taskEditFieldsJson === 'string' ? taskEditFieldsJson : '{}'
  chatVisibilityResolved.value = false
  sectionEnabled.value = getDefaultSectionEnabled()
  taskLayoutColumns.value = {
    col1: ['informacoesTask', 'checklist', 'anexos'],
    col2: ['cor', 'financeiro', 'progressoTarefa', 'tags', 'usuariosAtribuidos', 'controlesAtividade', 'agendamento']
  }
  if (!taskEditFieldsJson || typeof taskEditFieldsJson !== 'string') return
  try {
    const parsed = JSON.parse(taskEditFieldsJson)
    const desktopEnabled = parsed?.layout?.enabled
    const desktopColumns = parsed?.layout?.columns
    const mobileLayout = parsed?.layout?.mobile
    const mobileEnabled = mobileLayout?.enabled
    const mobileColumns = mobileLayout?.columns
    const useMobileLayout = isMobileViewport.value
    const enabled = useMobileLayout ? (mobileEnabled || desktopEnabled) : desktopEnabled
    const columns = useMobileLayout ? (mobileColumns || desktopColumns) : desktopColumns
    if (columns) {
      const validKeys = [
        'informacoesTask',
        'checklist',
        'anexos',
        'cor',
        'financeiro',
        'progressoTarefa',
        'tags',
        'usuariosAtribuidos',
        'controlesAtividade',
        'agendamento',
        ...taskCustomFormGroups.value.map((group) => customGroupKey(group.id))
      ]
      const col1 = Array.isArray(columns.col1) ? columns.col1.filter((key) => validKeys.includes(key)) : []
      const dedupCol1 = [...new Set(col1)]
      if (useMobileLayout) {
        const fallbackDesktop = [
          ...(Array.isArray(desktopColumns?.col1) ? desktopColumns.col1 : []),
          ...(Array.isArray(desktopColumns?.col2) ? desktopColumns.col2 : [])
        ].filter((key) => validKeys.includes(key))
        const baseMobile = dedupCol1.length ? dedupCol1 : [...new Set(fallbackDesktop)]
        const missingMobile = validKeys.filter((key) => !baseMobile.includes(key))
        taskLayoutColumns.value = {
          col1: [...baseMobile, ...missingMobile],
          col2: []
        }
      } else {
        const col2 = Array.isArray(columns.col2) ? columns.col2.filter((key) => validKeys.includes(key)) : []
        const dedupCol2 = [...new Set(col2)].filter((key) => !dedupCol1.includes(key))
        const missing = validKeys.filter((key) => !dedupCol1.includes(key) && !dedupCol2.includes(key))
        taskLayoutColumns.value = {
          col1: dedupCol1.length ? dedupCol1 : ['informacoesTask', 'checklist', 'anexos'],
          col2: [...dedupCol2, ...missing]
        }
      }
    }

    if (useMobileLayout && !columns) {
      const allMobileKeys = [
        'informacoesTask',
        'checklist',
        'anexos',
        'cor',
        'financeiro',
        'progressoTarefa',
        'tags',
        'usuariosAtribuidos',
        'controlesAtividade',
        'agendamento',
        ...taskCustomFormGroups.value.map((group) => customGroupKey(group.id))
      ]
      taskLayoutColumns.value = {
        col1: [...new Set(allMobileKeys)],
        col2: []
      }
    }

    if (enabled && typeof enabled === 'object') {
      Object.keys(sectionEnabled.value).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(enabled, key)) {
          sectionEnabled.value[key] = enabled[key] !== false
        }
      })
    }
    const chatEnabled = useMobileLayout
      ? (mobileLayout?.chatEnabled ?? parsed?.layout?.chatEnabled)
      : parsed?.layout?.chatEnabled
    sectionEnabled.value.historicoChat = chatEnabled !== false
  } catch (error) {
    console.warn('TaskDetailModal: taskEditFieldsJson inválido. Mantendo padrão.', error)
  }
  chatVisibilityResolved.value = true
  applyTaskLayoutDomOrder()
}

// Load workspace configurations
const loadWorkspaceConfigurations = async () => {
  try {
    console.log('🔍 Carregando configurações para workspace:', workspaceStore.currentWorkspace?.id)
    
    const token = localStorage.getItem('token')
    if (!token) {
      return
    }

    const response = await api.get(`/workspace-config/${workspaceStore.currentWorkspace?.id}/configuracoes`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    const uiConfigResponse = await api.get(`/workspace-config/${workspaceStore.currentWorkspace?.id}/ui-config`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    taskCustomFormGroups.value = parseCustomFormsConfig(uiConfigResponse?.data?.customFormsJson || '{}')
    taskCustomFieldValues.value = parseCustomFieldValues(props.task?.customFieldsJson || '{}')
    
    const config = response.data
    applyTaskEditLayoutConfig(uiConfigResponse?.data?.taskEditFieldsJson || '{}')
     
    // Organizar tags por categoria (mantendo estrutura original)
    workspaceTags.value = config.categorias || []
    
    // Organizar motivos de agendamento
    workspaceMotivosAgendamento.value = config.motivosAgendamento || []
    
    console.log('✅ Configurações carregadas:', {
      categorias: config.categorias?.length || 0,
      motivos: config.motivosAgendamento?.length || 0
    })
    
    console.log('🔍 TaskDetailModal - workspaceMotivosAgendamento:', workspaceMotivosAgendamento.value)
    console.log('🔍 TaskDetailModal - motivos disponíveis:', workspaceMotivosAgendamento.value.map(m => ({ id: m.id, nome: m.nome || m.motivo, icone: m.icone })))

  } catch (error) {
    console.error('❌ Erro ao carregar configurações do workspace:', error)
    
    // Fallback para configurações padrão
    workspaceTags.value = [
      {
        id: 1,
        nome: 'Geral',
        tags: [
          { id: 1, nome: 'Atenção', cor: '#F59E0B' },
          { id: 2, nome: 'Interessado em Preço', cor: '#8B5CF6' },
          { id: 3, nome: 'Pronto para Fechar', cor: '#10B981' },
          { id: 4, nome: 'Follow-up', cor: '#3B82F6' }
        ]
      }
    ]
    
    workspaceMotivosAgendamento.value = []
    sectionEnabled.value = getDefaultSectionEnabled()
    taskCustomFormGroups.value = []
    taskCustomFieldValues.value = {}
  }
}

const availableTags = ref([
  {
    nome: 'Nível',
    tags: [
      { id: 1, nome: 'Urgente', cor: '#EF4444' },
      { id: 2, nome: 'Pedente', cor: '#F59E0B' },
      { id: 3, nome: 'Médio', cor: '#F59E0B' },
      { id: 4, nome: 'Leve', cor: '#10B981' },
      { id: 5, nome: 'Pesado', cor: '#7C2D12' }
    ]
  },
  {
    nome: 'Desenvolvimento',
    tags: [
      { id: 6, nome: 'BUG', cor: '#EF4444' },
      { id: 7, nome: 'Alteração', cor: '#F59E0B' },
      { id: 8, nome: 'Fácil', cor: '#10B981' },
      { id: 9, nome: 'Estrutura Base', cor: '#3B82F6' },
      { id: 10, nome: 'Multiempresa', cor: '#7C2D12' },
      { id: 11, nome: 'Cadastros', cor: '#6B7280' }
    ]
  }
])

// Função para converter IDs de usuários em objetos completos
const convertUserIdsToObjects = async (checklistData) => {
  try {
    // Carregar usuários do workspace se ainda não foram carregados (mais rápido)
    if (workspaceUsers.value.length === 0) {
      await loadWorkspaceUsers()
    }
    
    // Função recursiva para processar o checklist
    const processChecklist = (data) => {
      if (Array.isArray(data)) {
        return data.map(item => processChecklist(item))
      } else if (data && typeof data === 'object') {
        const processed = { ...data }
        
        // Converter assignedUsers da categoria de IDs para objetos completos
        if (processed.assignedUsers && Array.isArray(processed.assignedUsers)) {
          processed.assignedUsers = processed.assignedUsers.map(userId => {
            if (typeof userId === 'number' || typeof userId === 'string') {
              // Buscar usuário completo pelo ID
              const user = workspaceUsers.value.find(u => u.id == userId)
              if (user) {
                return user
              } else {
                console.warn('⚠️ Usuário não encontrado para ID da categoria:', userId)
                return { id: userId, nomeCompleto: 'Usuário não encontrado', fotoPerfil: null }
              }
            }
            return userId // Já é um objeto
          })
          
        }
        
        // Se tem items, processar recursivamente
        if (processed.items && Array.isArray(processed.items)) {
          processed.items = processed.items.map(item => {
            const processedItem = { ...item }
            
            // Converter assignedUsers de IDs para objetos completos
            if (processedItem.assignedUsers && Array.isArray(processedItem.assignedUsers)) {
              processedItem.assignedUsers = processedItem.assignedUsers.map(userId => {
                if (typeof userId === 'number' || typeof userId === 'string') {
                  // Buscar usuário completo pelo ID
                  const user = workspaceUsers.value.find(u => u.id == userId)
                  if (user) {
                    return user
                  } else {
                    return { id: userId, nomeCompleto: 'Usuário não encontrado', fotoPerfil: null }
                  }
                }
                return userId // Já é um objeto
              })
              
            }
            
            return processedItem
          })
        }
        
        return processed
      }
      return data
    }
    
    return processChecklist(checklistData)
  } catch (error) {
    console.error('❌ Erro ao converter IDs de usuários:', error)
    return checklistData // Retornar dados originais em caso de erro
  }
}

// Watch for task changes
watch(() => props.task, async (newTask) => {
  if (newTask) {
    // Nova task carregada: bloquear auto-save da checklist até finalizar inicialização
    checklistInicializado.value = false
        
    // Carregar usuários atribuídos da task
    let usuariosAtribuidos = []
    
    // Primeiro, tentar carregar do campo assignedUsers (novo formato)
    if (newTask.assignedUsers) {
      try {
        let assignedUsersData = []
        if (Array.isArray(newTask.assignedUsers)) {
          assignedUsersData = newTask.assignedUsers
        } else if (typeof newTask.assignedUsers === 'string') {
          assignedUsersData = JSON.parse(newTask.assignedUsers)
        }
        
        // Converter IDs para objetos de usuário completos
        if (assignedUsersData && assignedUsersData.length > 0) {
          // Carregar usuários do workspace primeiro
          await loadWorkspaceUsers()
          
          usuariosAtribuidos = assignedUsersData.map(userId => {
            const user = workspaceUsers.value.find(u => u.id == userId)
            if (user) {
              return user
            } else {
              return { id: userId, nomeCompleto: 'Usuário não encontrado', fotoPerfil: null }
            }
          })
        }
      } catch (error) {
        console.warn('❌ Erro ao carregar assignedUsers:', error)
      }
    }
    
    // Fallback para o formato antigo se não houver assignedUsers
    if (usuariosAtribuidos.length === 0) {
      if (newTask.usuariosAtribuidos && Array.isArray(newTask.usuariosAtribuidos)) {
        usuariosAtribuidos = newTask.usuariosAtribuidos
      } else if (newTask.usuarioAtribuido) {
        usuariosAtribuidos = [newTask.usuarioAtribuido]
      }
    }
    
    // 🔧 CORREÇÃO: Salvar agendamento atual antes de sobrescrever
    // Se estamos removendo o agendamento, NÃO restaurar o valor antigo (evita race condition)
    const agendamentoAtual = removendoAgendamento.value ? null : formData.value?.agendamento
    
    // Cancelar qualquer save de checklist pendente para evitar que dados antigos sobrescrevam a atualização do servidor
    if (timeoutSalvamentoChecklist.value) {
      clearTimeout(timeoutSalvamentoChecklist.value)
      timeoutSalvamentoChecklist.value = null
    }
    
    // Bloquear o deep watcher do checklist para evitar loop com WebSocket
    atualizandoDoServidor.value = true
    
    formData.value = {
      titulo: newTask.titulo || '',
      descricao: newTask.descricao || '',
      checklist: newTask.checklist || [],
      usuariosAtribuidos: usuariosAtribuidos,
      agendamento: removendoAgendamento.value ? null : (agendamentoAtual || newTask.agendamento || null),
      cor: newTask.cor || '#3B82F6',
      anexos: newTask.anexos || [],
      recebiveis: Array.isArray(newTask.recebiveis) ? newTask.recebiveis : [],
      progressoTarefaPercentual: newTask.progressoTarefaPercentual != null ? newTask.progressoTarefaPercentual : 0,
      prazoTarefa: newTask.prazoTarefa || ''
    }
    
    // Resetar a flag após o Vue processar a reatividade
    setTimeout(() => {
      atualizandoDoServidor.value = false
      checklistInicializado.value = true
    }, 200)
    
    // Carregar tags - pode vir como array ou string JSON
    
    if (newTask.tags) {
      if (Array.isArray(newTask.tags)) {
        selectedTags.value = newTask.tags
      } else if (typeof newTask.tags === 'string') {
        try {
          selectedTags.value = JSON.parse(newTask.tags)
        } catch (e) {
          console.warn('❌ Erro ao fazer parse das tags:', e)
          selectedTags.value = []
        }
      }
    } else {
      selectedTags.value = []
    }
    
    // Carregar checklist de forma super otimizada
    if (newTask.checklist) {
      let checklistData = null
      if (Array.isArray(newTask.checklist)) {
        checklistData = newTask.checklist
      } else if (typeof newTask.checklist === 'string') {
        try {
          checklistData = JSON.parse(newTask.checklist)
        } catch (e) {
          console.warn('❌ Erro ao fazer parse do checklist:', e)
          checklistData = []
        }
      }
      
      // Carregar checklist diretamente - conversão de usuários será feita sob demanda
      formData.value.checklist = checklistData || []
      
      // Carregar estado de categorias minimizadas do localStorage (após checklist estar parseado)
      loadMinimizedCategoriesState()
      
      // Converter IDs de usuários apenas quando necessário (lazy loading)
      if (checklistData && checklistData.length > 0) {
        // Usar setTimeout com delay mínimo para não bloquear
        setTimeout(async () => {
          try {
            formData.value.checklist = await convertUserIdsToObjects(checklistData)
          } catch (error) {
            console.warn('⚠️ Erro ao converter usuários:', error)
            // Manter dados originais se houver erro
          }
        }, 50) // Delay mínimo de 50ms
      }
    } else {
      formData.value.checklist = []
      loadMinimizedCategoriesState()
    }
    
    if (!minimizedCards.value.informacoesTask) {
      scheduleTituloResize()
    }
  }
}, { immediate: true })

// Methods
const closeModal = () => {
  emit('close')
}

const saveTask = async () => {
  try {
    const taskData = {
      ...formData.value,
      tags: selectedTags.value
    }
    emit('save', taskData)
    closeModal()
  } catch (error) {
    console.error('Erro ao salvar task:', error)
  }
}

// Função para salvar automaticamente as informações da task (com debounce)
const salvarInformacoesTask = () => {
  // Limpar timeout anterior se existir
  if (timeoutSalvamento.value) {
    clearTimeout(timeoutSalvamento.value)
  }

  // Aguardar 1 segundo antes de salvar (debounce)
  timeoutSalvamento.value = setTimeout(async () => {
    await executarSalvamento()
  }, 1000)
}

// Função que executa o salvamento
const executarSalvamento = async () => {
  // Evitar múltiplas chamadas simultâneas
  if (salvando.value) {
    return
  }

  try {
    // Verificar se temos um ID da task
    if (!props.task?.id) {
      return
    }

    // Verificar se os dados mudaram
    const tituloAtual = props.task.titulo || ''
    const descricaoAtual = props.task.descricao || ''
    
    if (formData.value.titulo === tituloAtual && formData.value.descricao === descricaoAtual) {
      
      return
    }

  
    salvando.value = true
    
    const token = localStorage.getItem('token')
    if (!token) {
      console.error('❌ Token não encontrado')
      return
    }

    const response = await api.put(`/tasks/${props.task.id}/info`, {
      titulo: formData.value.titulo,
      descricao: formData.value.descricao
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    
    // Mostrar indicador "salvo" por 2 segundos
    salvo.value = true
    if (timeoutIndicadorSalvo.value) {
      clearTimeout(timeoutIndicadorSalvo.value)
    }
    timeoutIndicadorSalvo.value = setTimeout(() => {
      salvo.value = false
    }, 2000)
    
    // Emitir evento para atualizar a task no componente pai
    emit('taskUpdated', response.data)
    
  } catch (error) {
    console.error('❌ Erro ao salvar informações automaticamente:', error)
    // Não mostrar erro para o usuário, apenas log
  } finally {
    salvando.value = false
  }
}

// Funções para gerenciar atividades
const carregarAtividades = async () => {
  if (!props.task?.id) return
  
  console.log('🔄 carregarAtividades() - Iniciando carregamento para task:', props.task.id)
  
  try {
    const token = localStorage.getItem('token')
    
    // Carregar todas as atividades
    const responseLog = await api.get(`/atividades-task/task/${props.task.id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    
    // Ordenar do mais recente para o mais antigo (garantia extra)
    atividadesLog.value = (responseLog.data || []).sort((a, b) => {
      const dateA = new Date(a.dataInicio)
      const dateB = new Date(b.dataInicio)
      return dateB - dateA // Mais recente primeiro
    })
    
    console.log('📊 Atividades LOG carregadas:', atividadesLog.value.length)
    
    // Carregar atividades ativas
    const responseAtivas = await api.get(`/atividades-task/task/${props.task.id}/ativas`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    atividadesAtivas.value = responseAtivas.data || []
    
    console.log('✅ Atividades ATIVAS carregadas:', atividadesAtivas.value.length)
    console.log('📋 Detalhes das atividades ativas:', JSON.stringify(atividadesAtivas.value))
    console.log('🖼️ A seção "Usuários trabalhando" deveria estar', atividadesAtivas.value.length > 0 ? 'VISÍVEL' : 'OCULTA')
    
    // Verificar se o usuário atual tem atividade ativa
    if (authStore.user?.id) {
      const minhaAtividade = atividadesAtivas.value.find(a => a.usuarioId === authStore.user.id)
      if (minhaAtividade) {
        console.log('✅ Usuário atual TEM atividade ativa:', minhaAtividade.id)
      } else {
        console.log('⚠️ Usuário atual NÃO tem atividade ativa')
      }
    }
    
  } catch (error) {
    console.error('❌ Erro ao carregar atividades:', error)
  }
}

// Watch para carregar configurações quando o modal abrir
watch(() => props.isOpen, async (isOpen) => {
 
  if (isOpen) {
    chatVisibilityResolved.value = false
    updateMobileViewport()
    showMobileChat.value = false
    const wsId = workspaceStore.currentWorkspace?.id
    if (wsId) await loadPlanForWorkspace(wsId)
    loadWorkspaceConfigurations()
    if (props.task?.id) {
      await carregarAtividades()
      // 🔧 CORREÇÃO: Sempre carregar agendamentos após inicialização
      await carregarAgendamentosTask()
    }
  } else {
    // Resetar flags quando modal fecha
    checklistInicializado.value = false
    removendoAgendamento.value = false
    showMobileChat.value = false
    chatVisibilityResolved.value = false
   
  }
}, { immediate: true })

watch(
  () => props.task?.customFieldsJson,
  (newValue) => {
    taskCustomFieldValues.value = parseCustomFieldValues(newValue || '{}')
  },
  { immediate: true }
)

// 🔧 CORREÇÃO: Watch adicional para garantir que agendamentos sejam carregados quando task mudar
watch(() => [props.task?.id, props.isOpen], async ([taskId, isOpen]) => {
  if (isOpen && taskId) {
    // Pequeno delay para garantir que formData foi inicializado
    setTimeout(async () => {
      await carregarAgendamentosTask()
    }, 100)
  }
}, { immediate: true })

// ✅ Watch para sincronizar atividades quando task for atualizada via WebSocket
watch(() => props.task, async (newTask, oldTask) => {
  console.log('🔍 TaskDetailModal watch(task) disparado - isOpen:', props.isOpen, 'taskId:', newTask?.id)
  
  // Recarregar atividades se activeUsersIds mudou e o modal está aberto
  if (props.isOpen && newTask?.id) {
    const newIds = JSON.stringify(newTask.activeUsersIds || [])
    const oldIds = JSON.stringify(oldTask?.activeUsersIds || [])
    
    console.log('🔍 Comparando activeUsersIds - Antigo:', oldIds, 'Novo:', newIds)
    
    if (newIds !== oldIds) {
      console.log('🔄 TaskDetailModal - activeUsersIds mudou - sincronizando atividades...')
      console.log('📊 IDs anteriores:', oldIds)
      console.log('📊 IDs novos:', newIds)
      console.log('📊 Task ID:', newTask.id)
      await carregarAtividades()
    } else {
      console.log('⚠️ activeUsersIds não mudou (são iguais)')
    }
  } else {
    console.log('⚠️ Watch não executou: isOpen =', props.isOpen, 'taskId =', newTask?.id)
  }
}, { deep: true, immediate: false })

const createCategory = async () => {
  if (newCategoryName.value.trim()) {
    const category = {
      id: Date.now(),
      nome: newCategoryName.value.trim(),
      items: [],
      completed: 0
    }
    formData.value.checklist.push(category)
    newCategoryName.value = ''
    newItemText.value[category.id] = ''
    
    // Aguardar o próximo tick do Vue para garantir que o DOM foi atualizado
    await nextTick()
    
    // Focar no primeiro input de item da nova categoria usando refs
    const itemInput = itemInputRefs.value[category.id]
    if (itemInput) {
      itemInput.focus()
    }
    
    salvarChecklistTask(true)
  }
}

const addItem = (categoryId) => {
  const category = formData.value.checklist.find(c => c.id === categoryId)
  const text = newItemText.value[categoryId]
  
  if (text && text.trim()) {
    const item = {
      id: Date.now(),
      text: text.trim(),
      completed: false
    }
    if (!category.items) category.items = []
    category.items.push(item)
    newItemText.value[categoryId] = ''
    
    nextTick(() => {
      const el = itemInputRefs.value[categoryId]
      if (el) {
        el.style.height = 'auto'
        el.focus()
      }
    })
    
    salvarChecklistTask(true)
  }
}

const isDarkMode = () => document.documentElement.classList.contains('dark')

const getSwalTheme = () => {
  const dark = isDarkMode()
  return {
    background: dark ? '#1f2937' : '#ffffff',
    color: dark ? '#f3f4f6' : '#1f2937',
    confirmButtonColor: '#EF4444',
    cancelButtonColor: dark ? '#4B5563' : '#6B7280',
  }
}

const deleteCategory = async (categoryId) => {
  const category = formData.value.checklist.find(c => c.id === categoryId)
  const categoryName = category?.name || t('kanban.taskDetail.swal.defaultCategory')
  const theme = getSwalTheme()
  
  const result = await Swal.fire({
    title: t('kanban.taskDetail.swal.deleteCategoryTitle'),
    html: t('kanban.taskDetail.swal.deleteCategoryHtml', { name: categoryName }),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: theme.confirmButtonColor,
    cancelButtonColor: theme.cancelButtonColor,
    confirmButtonText: t('kanban.taskDetail.swal.confirmDelete'),
    cancelButtonText: t('kanban.taskDetail.footer.cancel'),
    background: theme.background,
    color: theme.color,
    reverseButtons: true,
  })
  
  if (!result.isConfirmed) return
  
  formData.value.checklist = formData.value.checklist.filter(c => c.id !== categoryId)
  delete newItemText.value[categoryId]
  
  await executarSalvamentoChecklist(true)
}

const toggleCategoryCompletion = async (categoryId) => {
  const category = formData.value.checklist.find((c) => c.id === categoryId)
  if (!category || !Array.isArray(category.items) || category.items.length === 0) return

  const allCompleted = category.items.every((item) => item.completed)
  category.items = category.items.map((item) => ({ ...item, completed: !allCompleted }))
  category.completed = category.items.filter((item) => item.completed).length

  await executarSalvamentoChecklist(true)
}

const deleteItem = async (categoryId, itemId) => {
  const category = formData.value.checklist.find(c => c.id === categoryId)
  if (!category) return
  
  const item = category.items?.find(i => i.id === itemId)
  const itemName = item?.text || t('kanban.taskDetail.swal.defaultItem')
  const theme = getSwalTheme()
  
  const result = await Swal.fire({
    title: t('kanban.taskDetail.swal.deleteItemTitle'),
    html: t('kanban.taskDetail.swal.deleteItemHtml', { name: itemName }),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: theme.confirmButtonColor,
    cancelButtonColor: theme.cancelButtonColor,
    confirmButtonText: t('kanban.taskDetail.swal.confirmDelete'),
    cancelButtonText: t('kanban.taskDetail.footer.cancel'),
    background: theme.background,
    color: theme.color,
    reverseButtons: true,
  })
  
  if (!result.isConfirmed) return
  
  category.items = category.items.filter(i => i.id !== itemId)
  category.completed = (category.items || []).filter(i => i.completed).length
  
  await executarSalvamentoChecklist(true)
}

const onCategoryReorder = () => {
  salvarChecklistTask(true)
}

const toggleActivity = () => {
  isActive.value = !isActive.value
}

const iniciarAtividade = async () => {
  if (!props.task?.id || !authStore.user?.id) return
  
  // ✅ Prevenir cliques duplos
  if (processandoAtividade.value || loadingAtividade.value) {
    console.log('⏸️ Já está processando uma atividade, aguarde...')
    return
  }
  
  // ✅ Verificar se já tem atividade ativa (verificação local)
  const jaTemAtividadeAtiva = atividadesAtivas.value.some(
    atividade => atividade.usuarioId === authStore.user.id
  )
  if (jaTemAtividadeAtiva) {
    console.log('⚠️ Você já tem uma atividade ativa nesta task')
    alert(t('kanban.taskDetail.errors.alreadyWorking'))
    return
  }
  
  processandoAtividade.value = true
  loadingAtividade.value = true
  try {
    const token = localStorage.getItem('token')
    console.log('🚀 Iniciando atividade...', { taskId: props.task.id, usuarioId: authStore.user.id })
    
    await api.post('/atividades-task/iniciar', {
      taskId: props.task.id,
      usuarioId: authStore.user.id,
      observacoes: null
    }, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    
    // Recarregar atividades
    await carregarAtividades()
    
    // Recarregar a task atualizada do backend (com activeUsersIds)
    const responseTask = await api.get(`/tasks/${props.task.id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    
    // Emitir evento para atualizar o card no Kanban
    console.log('✅ Atividade iniciada, emitindo taskUpdated')
    emit('taskUpdated', responseTask.data)
    
  } catch (error) {
    console.error('❌ Erro ao iniciar atividade:', error)
    console.error('📋 Detalhes do erro:', {
      status: error.response?.status,
      data: error.response?.data,
      headers: error.response?.headers
    })
    
    // Extrair mensagem de erro do backend
    let errorMsg = 'Erro ao iniciar atividade'
    if (error.response?.data?.error) {
      errorMsg = error.response.data.error
    } else if (typeof error.response?.data === 'string') {
      // Tentar extrair mensagem do JSON string
      try {
        const parsed = JSON.parse(error.response.data)
        errorMsg = parsed.error || errorMsg
      } catch {
        errorMsg = error.response.data
      }
    } else if (error.message) {
      errorMsg = error.message
    }
    
    alert(`Erro ao iniciar trabalho:\n${errorMsg}`)
    // Recarregar atividades para sincronizar estado
    await carregarAtividades()
  } finally {
    loadingAtividade.value = false
    // Liberar flag após 1 segundo (debounce)
    setTimeout(() => {
      processandoAtividade.value = false
    }, 1000)
  }
}

const pararAtividade = async () => {
  if (!props.task?.id || !authStore.user?.id) return
  
  // ✅ Prevenir cliques duplos
  if (processandoAtividade.value || loadingAtividade.value) {
    console.log('⏸️ Já está processando uma atividade, aguarde...')
    return
  }
  
  processandoAtividade.value = true
  loadingAtividade.value = true
  try {
    const token = localStorage.getItem('token')
    
    console.log('🔍 Estado atual das atividades ativas:', JSON.stringify(atividadesAtivas.value))
    
    // Encontrar atividade ativa do usuário atual
    const atividadeAtiva = atividadesAtivas.value.find(
      atividade => atividade.usuarioId === authStore.user.id
    )
    
    if (!atividadeAtiva) {
      console.log('⚠️ Nenhuma atividade ativa encontrada para parar no estado local')
      console.log('🔄 Recarregando atividades do backend...')
      await carregarAtividades()
      
      const atividadeAtivaRecarregada = atividadesAtivas.value.find(
        atividade => atividade.usuarioId === authStore.user.id
      )
      
      if (!atividadeAtivaRecarregada) {
        console.log('⚠️ Nenhuma atividade ativa encontrada após recarregar')
        alert(t('kanban.taskDetail.errors.notWorking'))
        return
      }
      
      console.log('✅ Atividade ativa encontrada após recarregar:', atividadeAtivaRecarregada)
    }
    
    const atividadeParaParar = atividadeAtiva || atividadesAtivas.value.find(
      atividade => atividade.usuarioId === authStore.user.id
    )
    
    console.log('🛑 Parando atividade...', { 
      atividadeId: atividadeParaParar.id,
      usuarioId: atividadeParaParar.usuarioId,
      dataInicio: atividadeParaParar.dataInicio
    })
    
    await api.post('/atividades-task/parar', {
      atividadeId: atividadeParaParar.id,
      observacoes: null
    }, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    
    // Recarregar atividades
    await carregarAtividades()
    
    // Recarregar a task atualizada do backend (com activeUsersIds)
    const responseTask = await api.get(`/tasks/${props.task.id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    
    // Emitir evento para atualizar o card no Kanban
    console.log('✅ Atividade parada, emitindo taskUpdated')
    emit('taskUpdated', responseTask.data)
    
  } catch (error) {
    console.error('❌ Erro ao parar atividade:', error)
    console.error('📋 Detalhes do erro:', {
      status: error.response?.status,
      data: error.response?.data,
      headers: error.response?.headers
    })
    
    // Extrair mensagem de erro do backend
    let errorMsg = 'Erro ao parar atividade'
    if (error.response?.data?.error) {
      errorMsg = error.response.data.error
    } else if (typeof error.response?.data === 'string') {
      // Tentar extrair mensagem do JSON string
      try {
        const parsed = JSON.parse(error.response.data)
        errorMsg = parsed.error || errorMsg
      } catch {
        errorMsg = error.response.data
      }
    } else if (error.message) {
      errorMsg = error.message
    }
    
    alert(`Erro ao parar trabalho:\n${errorMsg}`)
    // Recarregar atividades para sincronizar estado
    await carregarAtividades()
  } finally {
    loadingAtividade.value = false
    // Liberar flag após 1 segundo (debounce)
    setTimeout(() => {
      processandoAtividade.value = false
    }, 1000)
  }
}

const formatarData = (data) => {
  if (!data) return 'N/A'
  
  try {
    // Se data já é um objeto Date, usar diretamente
    let date
    if (data instanceof Date) {
      date = data
    } else if (typeof data === 'string' || typeof data === 'number') {
      date = new Date(data)
    } else if (data && data.length === 7) {
      // Formato [ano, mês, dia, hora, minuto, segundo, nanosegundo]
      date = new Date(data[0], data[1] - 1, data[2], data[3], data[4], data[5] || 0)
    } else {
      return 'N/A'
    }
    
    // Verificar se a data é válida
    if (isNaN(date.getTime())) {
      return 'N/A'
    }
    
    return date.toLocaleString(dateLocale.value, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (error) {
    console.error('Erro ao formatar data:', error, data)
    return 'N/A'
  }
}

const calcularTempoDecorrido = (dataInicio) => {
  if (!dataInicio) return 'N/A'
  
  try {
    let inicio
    if (dataInicio instanceof Date) {
      inicio = dataInicio
    } else if (typeof dataInicio === 'string' || typeof dataInicio === 'number') {
      inicio = new Date(dataInicio)
    } else if (dataInicio && dataInicio.length === 7) {
      // Formato [ano, mês, dia, hora, minuto, segundo, nanosegundo]
      inicio = new Date(dataInicio[0], dataInicio[1] - 1, dataInicio[2], dataInicio[3], dataInicio[4], dataInicio[5] || 0)
    } else {
      return 'N/A'
    }
    
    if (isNaN(inicio.getTime())) {
      return 'N/A'
    }
    
    const agora = new Date()
    const diff = agora - inicio
    
    if (diff < 0) return 'N/A'
    
    const dias = Math.floor(diff / (1000 * 60 * 60 * 24))
    const horas = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutos = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    
    if (dias > 0) {
      return `em andamento há ${dias} dia${dias > 1 ? 's' : ''}, ${horas}h ${minutos}min`
    } else if (horas > 0) {
      return `em andamento há ${horas}h ${minutos}min`
    } else {
      return `em andamento há ${minutos}min`
    }
  } catch (error) {
    console.error('Erro ao calcular tempo decorrido:', error, dataInicio)
    return 'N/A'
  }
}

const calcularDuracao = (dataInicio, dataFim) => {
  if (!dataInicio) return 'N/A'
  
  try {
    let inicio
    if (dataInicio instanceof Date) {
      inicio = dataInicio
    } else if (typeof dataInicio === 'string' || typeof dataInicio === 'number') {
      inicio = new Date(dataInicio)
    } else if (dataInicio && dataInicio.length === 7) {
      inicio = new Date(dataInicio[0], dataInicio[1] - 1, dataInicio[2], dataInicio[3], dataInicio[4], dataInicio[5] || 0)
    } else {
      return 'N/A'
    }
    
    if (isNaN(inicio.getTime())) {
      return 'N/A'
    }
    
    let fim
    if (!dataFim) {
      fim = new Date()
    } else if (dataFim instanceof Date) {
      fim = dataFim
    } else if (typeof dataFim === 'string' || typeof dataFim === 'number') {
      fim = new Date(dataFim)
    } else if (dataFim && dataFim.length === 7) {
      fim = new Date(dataFim[0], dataFim[1] - 1, dataFim[2], dataFim[3], dataFim[4], dataFim[5] || 0)
    } else {
      return 'N/A'
    }
    
    if (isNaN(fim.getTime())) {
      return 'N/A'
    }
    
    const diff = fim - inicio
    
    if (diff < 0) return 'N/A'
    
    const dias = Math.floor(diff / (1000 * 60 * 60 * 24))
    const horas = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutos = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    
    if (dias > 0) {
      return `${dias} dia${dias > 1 ? 's' : ''}, ${horas}h ${minutos}min`
    } else if (horas > 0) {
      return `${horas}h ${minutos}min`
    } else {
      return `${minutos}min`
    }
  } catch (error) {
    console.error('Erro ao calcular duração:', error, dataInicio, dataFim)
    return 'N/A'
  }
}

// Schedule modal functions
const abrirModalAgendamento = () => {
  showScheduleModal.value = true
}

const fecharModalAgendamento = () => {
  showScheduleModal.value = false
}

const onTaskScheduled = async (scheduleData) => {
  console.log('✅ Task agendada:', scheduleData)
  
  // Recarregar os agendamentos da task (o novo já foi criado)
  await carregarAgendamentosTask()
  
  // 🔄 Emitir evento para atualizar o card no Kanban
  if (props.task?.id && formData.value.agendamento) {
    // Usar os dados carregados pelo carregarAgendamentosTask para evitar problemas de timezone
    const dataHora = formData.value.agendamento.dataHora.split(' ')
    
    const taskAtualizada = {
      ...props.task,
      dataAgendamento: dataHora[0], // Usar a data já processada pelo backend
      horario: dataHora[1],
      motivo: formData.value.agendamento.motivo
    }
    
    console.log('🔄 Emitindo taskUpdated com agendamento:', taskAtualizada)
    emit('taskUpdated', taskAtualizada)
  }
  
  fecharModalAgendamento()
}

// Função para carregar agendamentos da task
const carregarAgendamentosTask = async () => {
  if (!props.task?.id) {
    console.log('🔍 TaskDetailModal carregarAgendamentosTask - Sem task ID')
    return
  }
  
  // Se estamos removendo agendamento, não recarregar (evita restaurar o que acabou de ser deletado)
  if (removendoAgendamento.value) {
    console.log('🔍 TaskDetailModal carregarAgendamentosTask - Ignorado (removendo agendamento)')
    return
  }
  
  console.log('🔍 TaskDetailModal carregarAgendamentosTask - Iniciando para task ID:', props.task.id)
  
  try {
    const token = localStorage.getItem('token')
    const response = await api.get(`/agendamentos/task/${props.task.id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    
    console.log('🔍 TaskDetailModal carregarAgendamentosTask - response.data:', response.data)
    
    // Atualizar o formData com os agendamentos
    if (response.data && response.data.length > 0) {
      // Pegar o agendamento mais recente
      const ultimoAgendamento = response.data[0]
      console.log('🔍 TaskDetailModal carregarAgendamentosTask - ultimoAgendamento:', ultimoAgendamento)
      console.log('🔍 TaskDetailModal carregarAgendamentosTask - motivo:', ultimoAgendamento.motivo)
      
      const novoAgendamento = {
        dataHora: `${ultimoAgendamento.dataAgendamento} ${ultimoAgendamento.horario}`,
        motivo: ultimoAgendamento.motivo || null
      }
      
      formData.value.agendamento = novoAgendamento
      console.log('✅ TaskDetailModal carregarAgendamentosTask - Agendamento definido:', novoAgendamento)
      console.log('✅ TaskDetailModal carregarAgendamentosTask - formData.agendamento atual:', formData.value.agendamento)
    } else {
      console.log('🔍 TaskDetailModal carregarAgendamentosTask - Nenhum agendamento encontrado')
      formData.value.agendamento = null
    }
  } catch (error) {
    console.error('❌ TaskDetailModal carregarAgendamentosTask - Erro:', error)
  }
}

// Funções para formatar agendamento
const formatAgendamentoData = (agendamento) => {
  console.log('🔍 formatAgendamentoData recebeu:', agendamento)
  
  if (!agendamento) return ''
  
  let data, hora
  
  // Se é um objeto com dataHora e motivo
  if (typeof agendamento === 'object' && agendamento.dataHora) {
    [data, hora] = agendamento.dataHora.split(' ')
  } else {
    [data, hora] = agendamento.split(' ')
  }
  
  console.log('📅 Data extraída:', data, '⏰ Hora extraída:', hora)
  
  if (!data) return ''
  
  try {
    // Formato esperado: YYYY-MM-DD
    const [ano, mes, dia] = data.split('-')
    console.log('📊 Partes da data - Ano:', ano, 'Mês:', mes, 'Dia:', dia)
    
    if (!ano || !mes || !dia) {
      console.warn('⚠️ Formato inválido, retornando data original:', data)
      return data
    }
    
    // Formato brasileiro: DD/MM/YYYY
    const diaFormatado = dia.padStart(2, '0')
    const mesFormatado = mes.padStart(2, '0')
    const anoFormatado = ano
    
    const resultado = `${diaFormatado}/${mesFormatado}/${anoFormatado}`
    console.log('✅ Data formatada:', resultado)
    return resultado
  } catch (error) {
    console.error('❌ Erro ao formatar data:', error)
    return data
  }
}

const formatAgendamentoHora = (agendamento) => {
  if (!agendamento) return ''
  
  let data, hora
  
  // Se é um objeto com dataHora e motivo
  if (typeof agendamento === 'object' && agendamento.dataHora) {
    [data, hora] = agendamento.dataHora.split(' ')
  } else {
    [data, hora] = agendamento.split(' ')
  }
  
  if (!hora) return ''
  
  try {
    const [horas, minutos] = hora.split(':')
    const horaFormatada = `${horas}:${minutos}`
    return horaFormatada
  } catch (error) {
    return hora
  }
}

// Nova função simples para formato brasileiro
const formatDataBrasileira = (agendamento) => {
  if (!agendamento) return ''
  
  let data, hora
  
  // Se é um objeto com dataHora e motivo
  if (typeof agendamento === 'object' && agendamento.dataHora) {
    [data, hora] = agendamento.dataHora.split(' ')
  } else {
    [data, hora] = agendamento.split(' ')
  }
  if (!data) return ''
  
  // Converter vírgulas para hífens primeiro
  const dataLimpa = data.replace(/,/g, '-')
  
  // Forçar conversão para formato brasileiro
  const partes = dataLimpa.split('-')
  
  if (partes.length === 3) {
    const [ano, mes, dia] = partes
    return `${dia}/${mes}/${ano}`
  }
  
  return data
}

// Toggle section visibility
const toggleSection = (sectionName) => {
  sectionsVisible.value[sectionName] = !sectionsVisible.value[sectionName]
  saveSectionsVisibleState() // Salvar automaticamente no localStorage
}

// Função para remover agendamento
const removerAgendamento = async () => {
  if (!props.task?.id) {
    console.warn('⚠️ removerAgendamento: task.id não disponível')
    return
  }
  
  console.log('🗑️ removerAgendamento: Iniciando remoção para task ID:', props.task.id)
  
  // Ativar flag ANTES de qualquer operação (protege contra race condition com watchers/WebSocket)
  removendoAgendamento.value = true
  
  // Limpar a interface imediatamente para feedback visual
  formData.value.agendamento = null
  
  try {
    const token = localStorage.getItem('token')
    
    // Buscar todos os agendamentos da task
    const response = await api.get(`/agendamentos/task/${props.task.id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    
    console.log('🔍 removerAgendamento: Agendamentos encontrados:', response.data?.length || 0, response.data)
    
    if (response.data && response.data.length > 0) {
      // Deletar TODOS os agendamentos encontrados (mesma lógica do Lead que funciona)
      for (const agendamento of response.data) {
        console.log('🗑️ removerAgendamento: Deletando agendamento ID:', agendamento.id)
        try {
          await api.delete(`/agendamentos/${agendamento.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
          console.log('✅ removerAgendamento: Agendamento', agendamento.id, 'deletado com sucesso')
        } catch (deleteError) {
          console.error('❌ removerAgendamento: Erro ao deletar agendamento', agendamento.id, ':', deleteError.response?.status, deleteError.response?.data || deleteError.message)
        }
      }
    } else {
      console.log('ℹ️ removerAgendamento: Nenhum agendamento encontrado no backend')
    }
    
    // Garantir que a interface está limpa
    formData.value.agendamento = null
    
    // 🔄 Emitir evento para atualizar o card no Kanban (remover agendamento)
    if (props.task?.id) {
      const taskAtualizada = {
        ...props.task,
        dataAgendamento: null,
        horario: null,
        motivo: null,
        agendamento: null,
        agendamentos: '[]'
      }
      
      console.log('🔄 removerAgendamento: Emitindo taskUpdated - agendamento removido')
      emit('taskUpdated', taskAtualizada)
    }
    
    console.log('✅ removerAgendamento: Processo concluído com sucesso')
    
  } catch (error) {
    console.error('❌ removerAgendamento: Erro geral:', error.response?.status, error.response?.data || error.message)
  } finally {
    // Desativar flag após um pequeno delay para garantir que todos os watchers já processaram
    setTimeout(() => {
      removendoAgendamento.value = false
      console.log('🔓 removerAgendamento: Flag removendoAgendamento desativada')
    }, 500)
  }
}

// Toggle tag selection
const toggleTag = (tagId) => {
  const index = selectedTags.value.indexOf(tagId)
  if (index > -1) {
    selectedTags.value.splice(index, 1)
  } else {
    selectedTags.value.push(tagId)
  }
  
  // Salvar automaticamente as tags
  salvarTagsTask()
}

// Função para salvar automaticamente as tags (com debounce)
const salvarTagsTask = () => {
  // Limpar timeout anterior se existir
  if (timeoutSalvamentoTags.value) {
    clearTimeout(timeoutSalvamentoTags.value)
  }

  // Aguardar 500ms antes de salvar (debounce mais rápido para tags)
  timeoutSalvamentoTags.value = setTimeout(async () => {
    await executarSalvamentoTags()
  }, 500)
}

// Auto-save function for color
const saveColor = async () => {
  try {
    console.log('💾 Salvando cor da task...', {
      id: props.task?.id,
      cor: formData.value.cor
    })
    
    salvandoCor.value = true
    salvoCor.value = false
    
    const colorData = {
      cor: formData.value.cor
    }
    
    const response = await api.put(`/tasks/${props.task.id}/color`, colorData)
    console.log('✅ Cor da task salva com sucesso')
    
    // Atualizar a task local com os dados retornados
    if (response.data) {
      Object.assign(props.task, response.data)
      console.log('🔄 Task atualizada localmente:', response.data)
    }
    
    // Emitir evento para notificar o componente pai
    emit('taskUpdated', response.data)
    
    salvandoCor.value = false
    salvoCor.value = true
    
    // Reset do status após 2 segundos
    setTimeout(() => {
      salvoCor.value = false
    }, 2000)
    
  } catch (error) {
    console.error('❌ Erro ao salvar cor da task:', error)
    salvandoCor.value = false
    salvoCor.value = false
  }
}

const openRecebivelModal = (item = null) => {
  if (item) {
    recebivelEmEdicaoId.value = item.id
    recebivelForm.value = {
      valor: Number(item.valor || 0),
      dataVencimento: item.dataVencimento || ''
    }
    recebivelValorDisplay.value = formatCurrencyToMask(item.valor || 0)
  } else {
    recebivelEmEdicaoId.value = null
    recebivelForm.value = { valor: null, dataVencimento: '' }
    recebivelValorDisplay.value = ''
  }
  showRecebivelModal.value = true
}

const closeRecebivelModal = () => {
  showRecebivelModal.value = false
  recebivelEmEdicaoId.value = null
  recebivelValorDisplay.value = ''
}

const handleRecebivelValorInput = (event) => {
  const raw = event?.target?.value ?? ''
  const parsed = parseCurrencyMask(raw)
  recebivelForm.value.valor = parsed > 0 ? parsed : null
  recebivelValorDisplay.value = parsed > 0 ? formatCurrencyToMask(parsed) : ''
}

const saveRecebivelItem = async () => {
  const valor = Number(recebivelForm.value.valor || 0)
  const dataVencimento = recebivelForm.value.dataVencimento
  if (valor <= 0 || !dataVencimento) return

  const lista = Array.isArray(formData.value.recebiveis) ? [...formData.value.recebiveis] : []
  if (recebivelEmEdicaoId.value) {
    const index = lista.findIndex((item) => item.id === recebivelEmEdicaoId.value)
    if (index >= 0) {
      lista[index] = {
        ...lista[index],
        valor,
        dataVencimento
      }
    }
  } else {
    lista.push({
      id: `r_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      valor,
      dataVencimento,
      pago: false,
      dataPagamento: null
    })
  }
  formData.value.recebiveis = lista
  closeRecebivelModal()
  await saveFinanceiro()
}

const toggleRecebivelPago = async (item) => {
  const lista = Array.isArray(formData.value.recebiveis) ? [...formData.value.recebiveis] : []
  const index = lista.findIndex((r) => r.id === item.id)
  if (index < 0) return
  const agora = new Date()
  const hoje = `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}-${String(agora.getDate()).padStart(2, '0')}`
  const nextPago = !Boolean(lista[index].pago)
  lista[index] = {
    ...lista[index],
    pago: nextPago,
    dataPagamento: nextPago ? hoje : null
  }
  formData.value.recebiveis = lista
  await saveFinanceiro()
}

// Salvar campos financeiros (recebíveis + progresso + prazo)
const saveFinanceiro = async () => {
  try {
    if (!props.task?.id) return
    
    const recebiveis = Array.isArray(formData.value.recebiveis) ? formData.value.recebiveis : []
    const pctConc = formData.value.progressoTarefaPercentual ?? 0
    const prazoTarefa = formData.value.prazoTarefa || null
    
    console.log('💰 Salvando dados financeiros da task...', {
      id: props.task.id,
      recebiveis,
      progressoTarefaPercentual: pctConc,
      prazoTarefa
    })
    
    // Atualizar imediatamente no card (feedback instantâneo)
    const taskAtualizada = {
      ...props.task,
      recebiveis,
      progressoTarefaPercentual: pctConc,
      prazoTarefa
    }
    emit('taskUpdated', taskAtualizada)
    
    // Salvar no backend
    const token = localStorage.getItem('token')
    const response = await api.put(`/tasks/${props.task.id}`, {
      titulo: formData.value.titulo || props.task.titulo,
      descricao: formData.value.descricao !== undefined ? formData.value.descricao : props.task.descricao,
      workspaceId: props.task.workspaceId,
      recebiveis,
      progressoTarefaPercentual: pctConc,
      prazoTarefa,
      atualizarPrazoTarefa: true
    }, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    
    console.log('✅ Dados financeiros salvos com sucesso')
    
    if (response.data) {
      Object.assign(props.task, response.data)
    }
  } catch (error) {
    console.error('❌ Erro ao salvar dados financeiros:', error)
  }
}

// Função que executa o salvamento das tags
const executarSalvamentoTags = async () => {
  
// Evitar múltiplas chamadas simultâneas
  if (salvandoTags.value) {
    return
  }

  try {
    // Verificar se temos um ID da task
    if (!props.task?.id) {
      return
    }

    // Verificar se as tags mudaram
    const tagsAtuais = props.task.tags || []
    
    // Comparar arrays (ordem não importa)
    const tagsIguais = selectedTags.value.length === tagsAtuais.length &&
                      selectedTags.value.every(tagId => tagsAtuais.includes(tagId))
    
    if (tagsIguais) {
      
      return
    }

    salvandoTags.value = true
    
    const token = localStorage.getItem('token')
    if (!token) {
      
      return
    }

    const response = await api.put(`/tasks/${props.task.id}/tags`, {
      tags: selectedTags.value
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })


    
    // Mostrar indicador "salvo" por 2 segundos
    salvoTags.value = true
    if (timeoutIndicadorSalvoTags.value) {
      clearTimeout(timeoutIndicadorSalvoTags.value)
    }
    timeoutIndicadorSalvoTags.value = setTimeout(() => {
      salvoTags.value = false
    }, 2000)
    
    // Emitir evento para atualizar a task no componente pai
    emit('taskUpdated', response.data)
    
  } catch (error) {
    console.error('❌ Erro ao salvar tags automaticamente:', error)
    // Não mostrar erro para o usuário, apenas log
  } finally {
    salvandoTags.value = false
  }
}

// Função para salvar automaticamente o checklist (com debounce curto)
const salvarChecklistTask = (imediato = false) => {
  if (timeoutSalvamentoChecklist.value) {
    clearTimeout(timeoutSalvamentoChecklist.value)
  }

  const delay = imediato ? 50 : 150
  timeoutSalvamentoChecklist.value = setTimeout(() => {
    const forceSave = !!imediato
    checklistSaveChain = checklistSaveChain.then(async () => {
      try {
        await executarSalvamentoChecklist(forceSave)
      } catch (e) {
        console.error('Erro ao salvar checklist (fila):', e)
      }
    })
  }, delay)
}

// Função para converter objetos de usuário para IDs antes de enviar para o backend
const converterChecklistParaBackend = (checklist) => {
  if (!checklist || !Array.isArray(checklist)) return checklist
  
  return checklist.map(category => {
    const categoriaConvertida = { ...category }
    
    // Converter assignedUsers da categoria de objetos para IDs
    if (categoriaConvertida.assignedUsers && Array.isArray(categoriaConvertida.assignedUsers)) {
      categoriaConvertida.assignedUsers = categoriaConvertida.assignedUsers.map(user => {
        // Se é um objeto de usuário, extrair o ID
        if (typeof user === 'object' && user.id) {
          
          return user.id
        }
        // Se já é um ID, manter como está
        return user
      })
    }
    
    if (categoriaConvertida.items && Array.isArray(categoriaConvertida.items)) {
      categoriaConvertida.items = categoriaConvertida.items.map(item => {
        const itemConvertido = { ...item }
        
        // Converter assignedUsers de objetos para IDs
        if (itemConvertido.assignedUsers && Array.isArray(itemConvertido.assignedUsers)) {
          itemConvertido.assignedUsers = itemConvertido.assignedUsers.map(user => {
            // Se é um objeto de usuário, extrair o ID
            if (typeof user === 'object' && user.id) {
              
              return user.id
            }
            // Se já é um ID, manter como está
            return user
          })
        }
        
        // Converter tags para formato TagInfo do backend
        if (itemConvertido.tags && Array.isArray(itemConvertido.tags)) {
          itemConvertido.tags = itemConvertido.tags.map(tag => {
            // Se é um objeto de tag, converter para formato TagInfo
            if (typeof tag === 'object' && tag.id) {
             
              return {
                id: tag.id,
                nome: tag.nome,
                cor: tag.cor,
                icone: tag.icone,
                categoriaId: tag.categoriaId,
                categoriaNome: tag.categoriaNome,
                ativo: tag.ativo
              }
            }
            // Se já é um ID, criar TagInfo básico
            if (typeof tag === 'number' || typeof tag === 'string') {
        
              return {
                id: tag,
                nome: 'Tag não encontrada',
                cor: '#666666',
                icone: '🏷️',
                categoriaId: null,
                categoriaNome: null,
                ativo: true
              }
            }
            // Se já é um objeto TagInfo, manter como está
            return tag
          })
        }
        
        return itemConvertido
      })
    }
    
    return categoriaConvertida
  })
}

// Função para converter IDs de volta para objetos de usuário após resposta do backend
const converterChecklistDoBackend = (checklist) => {
  if (!checklist) return []
  
  // Se é uma string JSON, fazer parse
  let checklistArray = checklist
  if (typeof checklist === 'string') {
    try {
      checklistArray = JSON.parse(checklist)
      
    } catch (error) {
      console.error('❌ Erro ao fazer parse do checklist:', error)
      return []
    }
  }
  
  if (!Array.isArray(checklistArray)) {
    console.warn('⚠️ Checklist não é um array válido:', checklistArray)
    return []
  }
  
  return checklistArray.map(category => {
    const categoriaConvertida = { ...category }
    
    // Converter assignedUsers da categoria de IDs para objetos completos
    if (categoriaConvertida.assignedUsers && Array.isArray(categoriaConvertida.assignedUsers)) {
      categoriaConvertida.assignedUsers = categoriaConvertida.assignedUsers.map(userId => {
        if (typeof userId === 'number' || typeof userId === 'string') {
          // Buscar usuário completo pelo ID
          const user = workspaceUsers.value.find(u => u.id == userId)
          if (user) {
            return user
          } else {
            console.warn('⚠️ Usuário não encontrado para ID da categoria:', userId)
            return { id: userId, nomeCompleto: 'Usuário não encontrado', fotoPerfil: null }
          }
        }
        return userId // Já é um objeto
      })
    }
    
    if (categoriaConvertida.items && Array.isArray(categoriaConvertida.items)) {
      categoriaConvertida.items = categoriaConvertida.items.map(item => {
        const itemConvertido = { ...item }
        
        // Converter assignedUsers de IDs para objetos completos
        if (itemConvertido.assignedUsers && Array.isArray(itemConvertido.assignedUsers)) {
          itemConvertido.assignedUsers = itemConvertido.assignedUsers.map(userId => {
            if (typeof userId === 'number' || typeof userId === 'string') {
              // Buscar usuário completo pelo ID
              const user = workspaceUsers.value.find(u => u.id == userId)
              if (user) {
                return user
              } else {
                console.warn('⚠️ Usuário não encontrado para ID:', userId)
                return { id: userId, nomeCompleto: 'Usuário não encontrado', fotoPerfil: null }
              }
            }
            return userId // Já é um objeto
          })
        }
        
        // Converter tags do backend para objetos completos
        if (itemConvertido.tags && Array.isArray(itemConvertido.tags)) {
          itemConvertido.tags = itemConvertido.tags.map(tag => {
            // Se é um objeto TagInfo do backend, manter como está (já tem todas as informações)
            if (typeof tag === 'object' && tag.id && tag.nome) {
             
              return tag
            }
            
            // Se é um ID, buscar tag completa pelo ID em todas as categorias de tags
            if (typeof tag === 'number' || typeof tag === 'string') {
              let tagEncontrada = null
              for (const categoria of workspaceTags.value) {
                if (categoria.tags) {
                  tagEncontrada = categoria.tags.find(t => t.id == tag)
                  if (tagEncontrada) break
                }
              }
              
              if (tagEncontrada) {
                
                return tagEncontrada
              } else {
                console.warn('⚠️ Tag não encontrada para ID:', tag)
                return { id: tag, nome: 'Tag não encontrada', cor: '#666666' }
              }
            }
            
            return tag // Já é um objeto válido
          })
        }
        
        return itemConvertido
      })
    }
    
    return categoriaConvertida
  })
}

// Função que executa o salvamento do checklist
const executarSalvamentoChecklist = async (forceSave = false) => {
  // Não salvar se estamos recebendo dados do servidor (evita sobrescrever exclusões remotas)
  if (atualizandoDoServidor.value && !forceSave) {
    return
  }
  
  // Evitar múltiplas chamadas simultâneas
  if (salvandoChecklist.value && !forceSave) {
    return
  }

  try {
    // Verificar se temos um ID da task
    if (!props.task?.id) {
      
      return
    }

    // Verificar se o checklist mudou comparando com a versão original da task
    const checklistAtual = props.task.checklist || '[]'
    
    // Converter o checklist atual para o mesmo formato que formData para comparação
    const checklistAtualConvertido = converterChecklistDoBackend(checklistAtual)
    const checklistFormDataConvertido = converterChecklistParaBackend(formData.value.checklist)
    
    // Comparar JSONs (ordem não importa) - mas ignorar se for chamado diretamente
    const checklistIguais = JSON.stringify(checklistFormDataConvertido) === JSON.stringify(checklistAtualConvertido)
    
    // Não salvar enquanto a checklist ainda está inicializando
    if (!checklistInicializado.value && !forceSave) {
      return
    }
    
    if (checklistIguais && !forceSave) { 
      return
    }

 
    
    // Converter checklist para formato do backend (objetos -> IDs)
    const checklistParaBackend = converterChecklistParaBackend(formData.value.checklist)

    
    
    salvandoChecklist.value = true
    
    const token = localStorage.getItem('token')
    if (!token) {
      console.error('❌ Token não encontrado')
      return
    }

    const response = await api.put(`/tasks/${props.task.id}/checklist`, {
      checklist: checklistParaBackend
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })


    
    // Atualizar dados locais com a resposta do servidor
    if (response.data && response.data.checklist) {
     
      // Marcar que estamos atualizando do servidor para evitar loop
      atualizandoDoServidor.value = true
      
      // Converter IDs de volta para objetos de usuário
      const checklistAtualizado = converterChecklistDoBackend(response.data.checklist)
      formData.value.checklist = checklistAtualizado
      resyncContextMenuItemFromChecklist()

      
      setTimeout(() => {
        atualizandoDoServidor.value = false
      }, 200)
    }
    
    // Mostrar indicador "salvo" por 2 segundos
    salvoChecklist.value = true
    if (timeoutIndicadorSalvoChecklist.value) {
      clearTimeout(timeoutIndicadorSalvoChecklist.value)
    }
    timeoutIndicadorSalvoChecklist.value = setTimeout(() => {
      salvoChecklist.value = false
    }, 2000)
    
    // Emitir evento para atualizar a task no componente pai
    emit('taskUpdated', response.data)
    
  } catch (error) {
    console.error('❌ Erro ao salvar checklist automaticamente:', error)
    // Não mostrar erro para o usuário, apenas log
  } finally {
    salvandoChecklist.value = false
  }
}

// Importar utilitário centralizado de mapeamento de ícones
import { getTagIcon } from '@/utils/iconMapper'

// Função para calcular cor de contraste
const getContrastColor = (hexColor) => {
  // Remove o # se presente
  const hex = hexColor.replace('#', '')
  
  // Converte para RGB
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)
  
  // Calcula o brilho usando a fórmula de luminância
  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  
  // Retorna branco para fundos escuros e preto para fundos claros
  return brightness > 128 ? '#000000' : '#FFFFFF'
}

// ==================== Discord Daily ====================
/** Altere para `true` quando for liberar o botão no header do modal */
const showDiscordDailyButton = false
const showDiscordDaily = ref(false)
const discordCopiado = ref(false)

const resolveTagName = (tagId) => {
  for (const cat of workspaceTags.value) {
    if (cat.tags) {
      const found = cat.tags.find(t => t.id === tagId)
      if (found) return found.nome
    }
  }
  return null
}

const discordDailyMessage = computed(() => {
  const lines = []
  const hoje = new Date().toLocaleDateString(dateLocale.value, { day: '2-digit', month: '2-digit', year: 'numeric' })
  
  lines.push(`## :clipboard: Daily Report \u2014 ${hoje}`)
  
  lines.push(`### :dart: Task: ${formData.value.titulo || 'Sem t\u00EDtulo'}`)
  if (formData.value.descricao) {
    const descLines = formData.value.descricao.split('\n').filter(l => l.trim())
    for (const dl of descLines) {
      lines.push(`> - ${dl.trim()}`)
    }
  }
  
  const pct = formData.value.progressoTarefaPercentual ?? 0
  const filled = Math.round(pct / 10)
  const empty = 10 - filled
  const bar = '\u2588'.repeat(filled) + '\u2591'.repeat(empty)
  lines.push(`### :bar_chart: Progresso Geral: **${pct}%**`)
  lines.push(`\`${bar}\` ${pct}%`)
  
  if (formData.value.checklist && formData.value.checklist.length > 0) {
    lines.push(`### :white_check_mark: Checklist`)
    lines.push('```ini')
    
    for (let i = 0; i < formData.value.checklist.length; i++) {
      const category = formData.value.checklist[i]
      const totalItems = category.items?.length || 0
      const completedItems = category.items?.filter(it => it.completed)?.length || 0
      const catPct = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0
      const allDone = totalItems > 0 && completedItems === totalItems
      
      lines.push(`[${category.nome} (${completedItems}/${totalItems} \u2014 ${catPct}%)]`)
      
      if (category.dataPrevista) {
        const status = getDateStatus(category.dataPrevista)
        let statusLabel
        if (allDone) {
          statusLabel = 'CONCLU\u00CDDO'
        } else if (status === 'overdue') {
          statusLabel = 'VENCIDA'
        } else if (status === 'today') {
          statusLabel = 'HOJE'
        } else {
          statusLabel = 'No prazo'
        }
        lines.push(`# Prazo ${formatDate(category.dataPrevista)} - ${statusLabel}`)
      }
      
      if (category.assignedUsers && category.assignedUsers.length > 0) {
        const nomes = category.assignedUsers.map(u => u.nomeCompleto || 'Usu\u00E1rio').join(', ')
        lines.push(`# Resp: ${nomes}`)
      }
      
      if (category.items && category.items.length > 0) {
        for (const item of category.items) {
          const label = item.completed ? '[CONCLUIDO]' : '[PENDENTE]'
          lines.push(`${label} ${item.text}`)
        }
      }
      
      if (i < formData.value.checklist.length - 1) {
        lines.push('')
      }
    }
    
    lines.push('```')
  }
  
  return lines.join('\n')
})

const openDiscordDailyModal = () => {
  discordCopiado.value = false
  showDiscordDaily.value = true
}

const copyDiscordMessage = async () => {
  try {
    await navigator.clipboard.writeText(discordDailyMessage.value)
    discordCopiado.value = true
    setTimeout(() => { discordCopiado.value = false }, 2500)
  } catch {
    const textarea = document.createElement('textarea')
    textarea.value = discordDailyMessage.value
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    discordCopiado.value = true
    setTimeout(() => { discordCopiado.value = false }, 2500)
  }
}

// Watch for item completion changes
watch(() => formData.value.checklist, (newChecklist) => {
  // Verificar se newChecklist é um array válido
  if (!newChecklist || !Array.isArray(newChecklist)) {
    // console.warn('⚠️ Watch checklist: newChecklist não é um array válido:', newChecklist)
    return
  }
  
  // Não salvar se estamos atualizando do servidor (evita loop infinito)
  if (atualizandoDoServidor.value) {
    return
  }
  
  newChecklist.forEach(category => {
    category.completed = (category.items || []).filter(item => item.completed).length
  })
  
  // Salvar automaticamente o checklist quando houver mudanças
  salvarChecklistTask()
}, { deep: true })

// Localiza o item/categoria atuais no formData (sempre que o checklist for trocado por referência:
// resposta do PUT, WebSocket task.updated → watch(props.task), convertUserIdsToObjects, etc.)
const findLiveChecklistContext = (refItem, refCategory) => {
  if (!refItem) return null
  const itemId = refItem.id
  const checklist = formData.value.checklist || []

  const findItemInCategory = (cat) => {
    const items = cat?.items || []
    if (itemId != null && itemId !== undefined) {
      return items.find((it) => it.id == itemId) || null
    }
    return (
      items.find(
        (it) => it.text === refItem.text && it.completed === refItem.completed
      ) || null
    )
  }

  if (refCategory?.id != null) {
    const cat = checklist.find((c) => c.id == refCategory.id)
    if (cat) {
      const found = findItemInCategory(cat)
      if (found) return { category: cat, item: found }
    }
  }
  for (const cat of checklist) {
    const found = findItemInCategory(cat)
    if (found) return { category: cat, item: found }
  }
  return null
}

// Após substituir checklist, reamarra o menu ao objeto vivo (evita órfãos).
const resyncContextMenuItemFromChecklist = () => {
  if (!showContextMenu.value || !contextMenuItem.value) return
  const live = findLiveChecklistContext(
    contextMenuItem.value,
    contextMenuCategory.value
  )
  if (live) {
    contextMenuCategory.value = live.category
    contextMenuItem.value = live.item
  }
}

// task.updated (WS) recria formData.checklist sem passar pelo PUT — precisamos do mesmo rebind.
watch(
  () => formData.value.checklist,
  () => {
    if (showContextMenu.value && contextMenuItem.value) {
      nextTick(() => resyncContextMenuItemFromChecklist())
    }
  }
)

// Funções para menu de contexto dos itens
const openContextMenu = (event, item, category) => {
 
  event.preventDefault()
  contextMenuPosition.value = { x: event.clientX, y: event.clientY }
  contextMenuItem.value = item
  contextMenuCategory.value = category
  showContextMenu.value = true
  
}

const closeContextMenu = () => {
  showContextMenu.value = false
  contextMenuItem.value = null
  contextMenuCategory.value = null
}

const getItemBackgroundClass = (item) => {
  if (!item.backgroundColor) {
    return 'modal-item-surface bg-gray-100 dark:bg-gray-700'
  }
  
  const colorConfig = availableColors.find(c => c.value === item.backgroundColor)
  return colorConfig ? colorConfig.class : 'modal-item-surface bg-gray-100 dark:bg-gray-700'
}

const setItemBackgroundColor = (colorValue) => {
  if (!contextMenuItem.value) return
  const live = findLiveChecklistContext(
    contextMenuItem.value,
    contextMenuCategory.value
  )
  const item = live?.item || contextMenuItem.value
  if (live) {
    contextMenuCategory.value = live.category
    contextMenuItem.value = live.item
  }
  item.backgroundColor = colorValue
  closeContextMenu()
  executarSalvamentoChecklist(true)
}

const toggleItemTag = (tag) => {
  if (!contextMenuItem.value) {
    console.warn('⚠️ toggleItemTag: contextMenuItem não encontrado')
    return
  }

  const live = findLiveChecklistContext(
    contextMenuItem.value,
    contextMenuCategory.value
  )
  const item = live?.item || contextMenuItem.value
  if (live) {
    contextMenuCategory.value = live.category
    contextMenuItem.value = live.item
  }

  if (!item.tags) {
    item.tags = []
  }

  const existingTagIndex = item.tags.findIndex((t) => t.id == tag.id)

  if (existingTagIndex >= 0) {
    item.tags.splice(existingTagIndex, 1)
  } else {
    item.tags.push(tag)
  }

  salvarChecklistTask(true)
}

const isTagSelected = (tag) => {
  if (!contextMenuItem.value) return false
  const live = findLiveChecklistContext(
    contextMenuItem.value,
    contextMenuCategory.value
  )
  const item = live?.item || contextMenuItem.value
  if (!item?.tags) return false
  return item.tags.some((t) => t.id == tag.id)
}

const getTagStyle = (tag) => {
  if (tag.cor) {
    return `background-color: ${tag.cor}20; color: ${tag.cor}; border: 1px solid ${tag.cor}40;`
  }
  return 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
}

const loadWorkspaceTags = async () => {
  // Os dados já são carregados em loadWorkspaceConfigurations
  
}

// Função para lidar com atualizações de anexos
const handleAnexosUpdated = () => {
  console.log('✅ Anexos atualizados na task')
}

const handleChatAnexosUpdated = () => {
  anexosReloadSignal.value += 1
  console.log('🔄 Recarregando AnexosSection após upload no chat')
}
</script>

<style scoped>
/* Modal: manter mesmo fundo do sidebar no tema escuro */
:global(body.tema_escura) .modal-panel {
  background-color: var(--bg-secondary) !important;
}

:global(body.tema_escura) .modal-panel .modal-header,
:global(body.tema_escura) .modal-panel .modal-panel-column {
  background-color: var(--bg-secondary) !important;
}

:global(body.tema_escura) .modal-panel .modal-surface,
:global(body.tema_escura) .modal-panel .modal-surface-gradient {
  background: var(--bg-secondary) !important;
  border-color: var(--border-primary) !important;
}

:global(body.tema_escura) .modal-panel .modal-item-surface {
  background-color: var(--bg-tertiary) !important;
  border-color: var(--border-primary) !important;
}

:global(body.tema_escura) .modal-panel .modal-item-surface:hover {
  background-color: var(--bg-hover) !important;
}

:global(body.tema_escura) .modal-panel input,
:global(body.tema_escura) .modal-panel textarea,
:global(body.tema_escura) .modal-panel select {
  background-color: var(--bg-secondary) !important;
  border-color: var(--border-primary) !important;
  color: var(--text-primary) !important;
}

:global(body.tema_escura) .modal-panel .dark\:bg-gray-600,
:global(body.tema_escura) .modal-panel .dark\:bg-gray-700,
:global(body.tema_escura) .modal-panel .dark\:bg-gray-800 {
  background-color: var(--bg-secondary) !important;
}

/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Padrão visual da barra de progresso (igual preview do card) */
.financeiro-progress {
  width: 100%;
  height: 5px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

:global(body.tema_escura) .financeiro-progress {
  background-color: #4b5563;
}

.financeiro-progress-bar {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease, background 0.3s ease;
}

/* ===== DatePicker - Estilo MÍNIMO e consistente (reset do zero) ===== */

/* Garante que o menu fique acima do modal */
:deep(.dp__menu) { z-index: 99999; }

/* Container principal do calendário – moderno */
:deep(.dp__menu_inner) {
  background-color: var(--bg-modal);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  box-shadow: 0 18px 40px rgba(0,0,0,.18), 0 2px 8px rgba(0,0,0,.08);
  overflow: hidden;
}

/* Remove fundos extras e bordas internas */
:deep(.dp__calendar),
:deep(.dp__calendar_header) {
  background: transparent;
  border: none;
}

/* Cabeçalho dos dias – elegante */
:deep(.dp__calendar_header) {
  display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; padding: 6px 12px;
}
:deep(.dp__calendar_header_item) {
  font-weight: 600; font-size: 12px; letter-spacing: .3px;
  color: var(--text-secondary); opacity: .9;
  display:flex; align-items:center; justify-content:center;
}

/* Mês e Ano (botões) – discretos com hover sutil */
:deep(.dp__month_year_select),
:deep(.dp__btn) {
  color: var(--text-primary);
  background: transparent;
  border: none;
  padding: 6px 10px;
  border-radius: 6px;
}
body.tema_clara :deep(.dp__month_year_select:hover),
body.tema_clara :deep(.dp__btn:hover) { background: rgba(0,0,0,.04); }
body.tema_escura :deep(.dp__month_year_select:hover),
body.tema_escura :deep(.dp__btn:hover) { background: rgba(255,255,255,.06); }

/* Células dos dias – moderno, sem alterar tamanho */
:deep(.dp__cell_inner) {
  color: var(--text-primary);
  background: transparent;
  border-radius: 10px;
  transition: background .18s ease, box-shadow .18s ease, transform .18s ease;
}
body.tema_clara :deep(.dp__cell_inner:hover) { background: rgba(0,0,0,.05); }
body.tema_escura :deep(.dp__cell_inner:hover) { background: rgba(255,255,255,.08); }
:deep(.dp__cell_inner.dp__today) { box-shadow: inset 0 0 0 2px var(--info); }
:deep(.dp__cell_inner.dp__active_date) { background: var(--info); color:#fff; box-shadow: 0 6px 16px rgba(88,101,242,.25); }

/* Ajustes de contraste por tema */
body.tema_escura :deep(.dp__month_year_select),
body.tema_escura :deep(.dp__btn) { color: #f1f5f9; }

body.tema_clara :deep(.dp__month_year_select),
body.tema_clara :deep(.dp__btn) { color: #111827; }

/* Botões modernos das Opções Rápidas */
.quick-option-btn {
  @apply px-4 py-3 text-sm rounded-lg font-medium transition-all;
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  box-shadow: 0 1px 2px rgba(0,0,0,.04);
}
.quick-option-btn:hover {
  background: var(--bg-hover);
  border-color: var(--border-hover);
  transform: translateY(-1px);
  box-shadow: 0 6px 12px rgba(0,0,0,.08);
}
.quick-option-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(0,0,0,.06);
}

/* Animações para o indicador de status */
.status-fade-enter-active,
.status-fade-leave-active {
  transition: all 0.3s ease;
}

.status-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

.status-fade-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.95);
}

.status-fade-enter-to,
.status-fade-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

/* Estilos para botões do checklist com informações */
.checklist-info-button {
  position: relative;
  overflow: hidden;
}

.checklist-info-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.checklist-info-button:hover::before {
  left: 100%;
}

/* Animação para botões com dados */
.checklist-info-button.has-data {
  animation: pulse-glow 2s infinite;
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.1);
  }
}

/* Estilo para datas vencidas */
.checklist-info-button.overdue {
  animation: pulse-red 2s infinite;
}

@keyframes pulse-red {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
  }
}

/* Estilo para data de hoje */
.checklist-info-button.today {
  animation: pulse-yellow 2s infinite;
}

@keyframes pulse-yellow {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(234, 179, 8, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(234, 179, 8, 0.1);
  }
}

/* Estilo para datas futuras */
.checklist-info-button.future {
  animation: pulse-green 2s infinite;
}

@keyframes pulse-green {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.1);
  }
}

/* Loading Spinners */
.loading-spinner {
  width: 8px;
  height: 8px;
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  border-top: 1.5px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-spinner-large {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Range slider financeiro */
.financeiro-range {
  background: #e5e7eb;
}
.financeiro-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #10b981;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  margin-top: -4px; /* centralizar na track de 8px */
}
.financeiro-range::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #10b981;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}
.financeiro-range::-webkit-slider-runnable-track {
  height: 8px;
  border-radius: 4px;
  background: #e5e7eb;
}
.financeiro-range::-moz-range-track {
  height: 8px;
  border-radius: 4px;
  background: #e5e7eb;
}

/* Dark mode */
:deep(.dark) .financeiro-range,
.dark .financeiro-range {
  background: #4b5563;
}
:deep(.dark) .financeiro-range::-webkit-slider-runnable-track,
.dark .financeiro-range::-webkit-slider-runnable-track {
  background: #4b5563;
}
:deep(.dark) .financeiro-range::-moz-range-track,
.dark .financeiro-range::-moz-range-track {
  background: #4b5563;
}

/* Card progresso da task + datepicker prazo */
.financeiro-range--task::-webkit-slider-runnable-track {
  height: 10px;
  border-radius: 9999px;
}
.financeiro-range--task::-moz-range-track {
  height: 10px;
  border-radius: 9999px;
}
.financeiro-range--task::-webkit-slider-thumb {
  width: 18px;
  height: 18px;
  margin-top: -4px;
}
.financeiro-range--task::-moz-range-thumb {
  width: 18px;
  height: 18px;
}
.financeiro-progress--task {
  background-color: rgba(15, 23, 42, 0.07);
}
body.tema_escura .financeiro-progress--task {
  background-color: rgba(148, 163, 184, 0.22);
}
.task-prazo-datepicker :deep(.dp__input_wrap) {
  width: 100%;
  border-radius: 0.75rem;
}
.task-prazo-datepicker :deep(.dp__input) {
  width: 100%;
  border-radius: 0.75rem !important;
  border: 1px solid #e5e7eb !important;
  /* Ícone do calendário à esquerda: reservar espaço (evita placeholder/texto sobre o ícone) */
  padding-top: 0.625rem !important;
  padding-bottom: 0.625rem !important;
  padding-left: 2.75rem !important;
  padding-right: 2.5rem !important;
  font-size: 0.875rem;
  font-weight: 500;
  background: #ffffff !important;
  color: #111827 !important;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.task-prazo-datepicker :deep(.dp__input_icon) {
  left: 0.65rem;
  width: 1.25rem;
  height: 1.25rem;
}
.task-prazo-datepicker :deep(.dp__clear_icon) {
  right: 0.65rem;
}
.task-prazo-datepicker :deep(.dp__input::placeholder) {
  color: #9ca3af;
}
.task-prazo-datepicker :deep(.dp__input:hover) {
  border-color: #c7d2fe !important;
}
.task-prazo-datepicker :deep(.dp__input:focus) {
  border-color: #6366f1 !important;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.22);
  outline: none;
}
body.tema_escura .task-prazo-datepicker :deep(.dp__input) {
  border-color: #4b5563 !important;
  background: #111827 !important;
  color: #f9fafb !important;
  box-shadow: none;
}
body.tema_escura .task-prazo-datepicker :deep(.dp__input::placeholder) {
  color: #9ca3af;
}
body.tema_escura .task-prazo-datepicker :deep(.dp__input:hover) {
  border-color: #6366f1 !important;
}
body.tema_escura .task-prazo-datepicker :deep(.dp__input:focus) {
  border-color: #818cf8 !important;
  box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.28);
}

.category-ghost {
  opacity: 0.4;
  border: 2px dashed #6366f1;
  border-radius: 0.75rem;
}

.category-drag {
  box-shadow: 0 10px 25px -5px rgba(0,0,0,0.15), 0 8px 10px -6px rgba(0,0,0,0.1);
  border-radius: 0.75rem;
  transform: rotate(1deg);
}

.category-drag-handle {
  opacity: 0.35;
  transition: opacity 0.15s ease;
}

.category-drag-handle:hover {
  opacity: 0.8;
}

.titulo-textarea {
  overflow: hidden;
  line-height: 1.5;
}
</style>
